// Cross-cutting UI strings shared by more than one domain or feature area.
//
// This is the home for strings that belong to no single domain -- generic
// actions ('Reset'), empty/error states ('No data', the report load failure),
// and the chrome of shared components (filter panels, grid and chart
// containers). Keys that are meaningful only inside one domain still belong in
// that domain's file.
//
// `common` is registered FIRST in the `domains` array in `src/i18n/index.js`,
// so it acts as a base layer: a domain file that defines the same key
// deliberately overrides the shared default rather than silently colliding
// with it.
export default {
  en: {
    // Migrated from <i18n> SFC blocks (#6500)
    'buOverview': 'Operator View',
    'quick-sight-insights': 'Business Insights',
    'offer': 'BU Offer Details',
    'buWatchlistDetails': 'BU Menu Item Details',
    'buWatchlistDetailsWithSummary': 'BU Menu Item Details w/ Summary',
    'buWatchlistDetailsHeader': 'Business Unit Menu Item Details',
    'buWatchlistDetailsSubtitle': 'Select up to five watchlists per BU to display to O/Os in the TA section on the QSRSoft Home Dashboard. There will be no summary or total across the watchlists.',
    'buWatchlistDetailsWithSummaryHeader': 'Agency Menu Items with Summary across Watchlists',
    'buWatchlistDetailsWithSummarySubtitle': 'Select up to five watchlists per BU to display to O/Os in the TA section on the QSRSoft Home Dashboard. The widget will show each watchlist plus a summary across the watchlists. All items in each watchlist should be part of the same platform, e.g., Summer Drinks or Meal Deals.',
    'nationalAAIncentiveBuilder': 'National AA Incentive Builder',
    'nationalMenuItemDetails': 'Details',
    'nationalMenuItemDetailsWithSummary': 'Details w/ Summary',
    'nationalMenuItemDetailsHeader': 'National Menu Item Details',
    'nationalMenuItemDetailsSubtitle': 'Select up to five watchlists per category to display to O/Os in the AA section on the QSRSoft Home Dashboard. There will be no summary or total across the watchlists.',
    'nationalMenuItemDetailsWithSummaryHeader': 'National Menu Items with Summary across Watchlists',
    'nationalMenuItemDetailsWithSummarySubtitle': 'Select up to five watchlists per category to display to O/Os in the AA section on the QSRSoft Home Dashboard. The widget will show each watchlist plus a summary across the watchlists. All items in each watchlist should be part of the same platform, e.g., Summer Drinks or Meal Deals.',
    'nationalMenuItemDetailsBevs': 'Details (Bevs)',
    'nationalMenuItemDetailsBevsHeader': 'National Menu Item Details (Beverages)',
    'nationalMenuItemDetailsBevsSubtitle': 'Select up to five watchlists per category to display to O/Os in the AA section on the QSRSoft Home Dashboard. There will be no summary or total across the watchlists.',
    'nationalMenuItemDetailsBevsWithSummary': 'Details (Bevs) w/ Summary',
    'nationalMenuItemDetailsBevsWithSummaryHeader': 'National Menu Items (Beverages) with Summary across Watchlists',
    'nationalMenuItemDetailsBevsWithSummarySubtitle': 'Select up to five watchlists per category to display to O/Os in the AA section on the QSRSoft Home Dashboard. The widget will show each watchlist plus a summary across the watchlists. All items in each watchlist should be part of the same platform.',
    // Migrated from <i18n> SFC blocks (#6500)
    'Sunday': 'Sunday',
    'Monday': 'Monday',
    'Tuesday': 'Tuesday',
    'Wednesday': 'Wednesday',
    'Thursday': 'Thursday',
    'Friday': 'Friday',
    'Saturday': 'Saturday',
    'day-of-week-days': 'days',
    'day-of-week-all': 'All',
    'day-of-week-v2-days': 'days',
    'day-of-week-v2-all': 'All',
    'loading-range': 'Loading {range}',
    'suspicious-activity-insight-data-failed-to-load': 'The data failed to load. Please try again or contact support if the issue persists.',
    'suspicious-activity-insight-more-info': 'More Info',
    'suspicious-activity-insight-please-subscribe': 'Please Subscribe',
    'suspicious-activity-insight-learn-more': 'Learn More',
    'security-drill-down-security-drill-down': 'Security Drill Down',
    'hello-world': 'Food over base calculation based on PMIX usage and may be slightly different from Cash and Inventory calculation',
    'drivers-add-up': 'PMIX, Pricing and Raw Cost add up to the change in Base Food % versus the comparison period.',
    'drivers': 'What moved it',
    'fob-change': 'Base Food vs Pr Mth',
    'vs-pr-mth': 'vs Pr Mth',
    'adds-up-to': 'adds up to {value}',
    'colour-key': 'Green lowers food cost, red raises it.',
    'raw-cost': 'Raw Cost',
    'pricing': 'Pricing',
    'pmix': 'PMIX',
    'rbd-events': 'RBDs',
    'rbd-tooltip': 'RBD\'s can skew Product Mix information and the resulting food cost data presented.',
    'rbd-store-line': 'Store {store}: {count} RBD day(s)',
    'base-food-decomp-widget-base-food-decomposition': 'Base Food Analysis',
    'base-food-decomp-widget-fob': 'Base Food',
    'base-food-decomp-widget-disclaimer': 'Analyzing what is driving Base food shift is an important part of understanding event performance, but it\'s not the entire story. Promo and Discount shifts as well as 2-year Sales and GC growth are also needed for a complete analytic picture.',
    'base-food-decomp-widget-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'day-of-week-v3-days': 'days',
    'day-of-week-v3-all': 'All',
    'role-defaults': 'Role Defaults',
    'enterprise-settings': 'Enterprise Settings*',
    'enterprise-help': 'Uncheck anything this enterprise doesn\'t use. Unchecked items are hidden everywhere and can\'t be enabled by role defaults.',
    'select-role': 'Select Role',
    'select-role-prompt': 'Select a role above to configure its dashboard defaults.',
    'allow-access': 'Allow Access',
    'edit-thresholds': 'Edit thresholds',
    'edit-card-color': 'Edit card color',
    'edit-pmix-products': 'Edit PMIX products',
    'pmix-role-products': 'PMIX Products (Role Default)',
    'rule': 'Rule',
    'higher-is-better': 'Higher is better',
    'lower-is-better': 'Lower is better',
    'restore-default': 'Restore Default',
    'switch-role-title': 'Switch role?',
    'switch-role-body': 'Switching roles will discard the current configuration changes. Are you sure you want to continue?',
    'unsaved-changes': 'Unsaved changes',
    'unsaved-changes-body': 'You have applied changes that haven\'t been saved. Discard them?',
    'keep-editing': 'Keep editing',
    'save-success': 'Enterprise settings saved',
    'save-failed': 'Failed to save enterprise settings',
    'copy-to-roles': 'Copy to other roles',
    'copy-to-roles-body': 'Copy the settings currently shown for {role} (including unsaved changes) to the roles below. Their existing dashboard defaults will be replaced.',
    'copy': 'Copy',
    'copy-success': 'Settings copied to {count} role(s)',
    'copy-failed': 'Failed to copy settings to: {roles}',
    'dashboard-settings-select-all': 'Select all',
    'dashboard-settings-done': 'Done',
    'dashboard-settings-preview': 'Preview',
    // Migrated from <i18n> SFC blocks (#6500)
    'You currently have': 'You currently have',
    'items out in the last': 'items out in the last',
    'for': 'for',
    'product-outage-insight-item': 'item',
    'oepe-w/o-park': 'OEPE W/O Park',
    'dt-pulled-fwrd-pct': 'DT Pulled Fwrd %',
    'snapshot-service-kvs-time-per-gc': 'KVS Time / GC',
    'snapshot-service-kvs-items-per-gc': 'KVS Items / GC',
    'snapshot-service-dt-pulled-forward-pct': 'DT Pulled Forward %',
    // Migrated from <i18n> SFC blocks (#6500)
    'custom-options-end-time': 'End Time',
    'custom-options-open': 'Open',
    '12to4': '12 to 4',
    '4to12': '4 to 12',
    'timeslice-dropdown-overnight': 'Overnight',
    'breakfast-desc': 'Cash Sheet Breakfast Sales',
    'total-desc': 'All Sales',
    'drive-thru-desc': 'Cash Sheet DT sales',
    'in-store-desc': 'Includes Front Counter, Delivery, and Kiosk - everything that is not Drive Thru',
    'kiosk-desc': 'Sales originating at Kiosk, regardless of where paid',
    'mobile-order-and-pay-desc': 'Sales originating on Digital App regardless of where paid',
    'delivery-desc': 'Total of 3PO Delivery and GMA Delivery',
    'sales-group-selector-drive-thru': 'Drive Thru',
    'sales-group-selector-all': 'All',
    'sales-group-selector-in-store': 'In-Store',
    'sales-group-selector-kiosk': 'Kiosk',
    'sales-group-selector-v2-drive-thru': 'Drive Thru',
    'sales-group-selector-v2-all': 'All',
    'sales-group-selector-v2-in-store': 'In-Store',
    'sales-group-selector-v2-kiosk': 'Kiosk',
    'custom-options-v2-end-time': 'End Time',
    'custom-options-v2-open': 'Open',
    // Migrated from <i18n> SFC blocks (#6500)
    'header': 'Each restaurant has a different subset of EVMs that qualify for McDonald\'s partnership dollars.',
    'part-1': 'The EVM must have received a price reduction recommended by Deloitte in late August (in the respective channel).',
    'part-2': 'In-store units for the $5/$8 menu items from September, November, and January are not included in partnership amount during the promotion window.',
    'part-3': 'EVMs sold at a price above the Deloitte recommendation might not be eligible for partnership',
    'part-4': 'Big Mac / SME is between Sept 8th - Oct 5th',
    'part-5': '10 Pc Nug / SEC McGrd is between Nov 4th - 30th',
    'part-6': '2 Wrps / Sau Egg McMuff is between Jan 6 - Feb 2',
    'part-7': 'SME / QPC is between Nov 2 - Nov 29, 2026',
    'operator-view': 'BU Comparison',
    'overview-container-buSummaryContainersDescription': 'The BU Comp section allows operators to compare their organization and each restaurant against the BU average. These containers allow you to see BU-level data in the same format.',
    // Migrated from <i18n> SFC blocks (#6500)
    'sectioned-dashboard-grid-no-data': 'No Data',
    'sectioned-dashboard-grid-labor': 'Labor',
    'rank-of-the-store-compared-to-the-whole-bu': 'Rank of the store compared to the whole BU',
    'units-1k-rod-gc': 'Units/1K ROD GC',
    'number-of-menu-items-that-are-sold-related': 'Number of menu items that are sold related to McCrispy Strips during Rest of Day (ROD) per thousand units.',
    'mc-crispy-strips-widget-rank': 'Rank',
    'new-beverage-units': 'New Beverage Units',
    'new-beverage-watch-list': 'Beverage 2026 Watch List',
    'new-beverage-widget-new-beverage': 'Specialty Drinks',
    'new-beverage-widget-units-1k-gc': 'Units/1K GC',
    'new-beverage-widget-number-of-menu-items-that-are-sold-related': 'Number of menu items that are sold related to AST Beverage per thousand units.',
    'new-beverage-widget-rank': 'Rank',
    'rmhc-gc-percentage': 'RMHC GC %',
    'rmhc-gc-total-gc': 'RMHC GC / Total GC',
    'add-reward-message-link-text': 'Reward employees',
    'add-reward-message-end': 'hitting Round Up targets',
    'rmhc-incentive-widget-premium-upgrade-message-start': 'Upgrade to Premium:',
    'rmhc-incentive-widget-premium-upgrade-message-end': 'Improve Round Up with Rewards',
    'rmhc-incentive-widget-rank': 'Rank',
    'created-date-header': 'Created Date',
    'incident-date-header': 'Incident Date',
    'case-number-header': 'Case Number',
    'type-header': 'Type',
    'customer-name-header': 'Customer Name',
    'recovery-required-header': 'Recovery Required',
    'status-header': 'Status',
    'resolution-header': 'Resolution',
    'customer-recovery-grid-close': 'close',
    'customer-recovery-grid-yes': 'Yes',
    'customer-recovery-grid-no': 'No',
    'total-number-header': 'Total/#',
    'issue-header': 'Issue',
    'equipment-header': 'Equipment',
    'reported-header': 'Reported',
    'priority-header': 'Priority',
    'assignee-header': 'Assignee',
    'status-reported': 'Reported',
    'status-accepted': 'Accepted',
    'status-in-progress': 'In Progress',
    'status-pending': 'Pending',
    'status-resolved': 'Resolved',
    'priority-low': 'Low',
    'priority-medium': 'Medium',
    'priority-high': 'High',
    'tickets-grid-close': 'close',
    'trailing-365': 'Trailing 365 Days',
    'trailing-7': 'Trailing 7 Days',
    'last-7': 'Last 7 Days',
    'last-30': 'Last 30 Days',
    'trailing-30': 'Trailing 30 Days',
    't365': 'T365',
    'snapshot-container-last-week': 'Last Week',
    'snapshot-container-point-of-order': 'Point Of Order',
    'snapshot-container-last-month': 'Last Month',
    'snapshot-container-wtd': 'WTD',
    'snapshot-container-mtd': 'MTD',
    'snapshot-container-ytd': 'YTD',
    'mop-w-offers': 'MOP W/ Offers',
    'mop-wo-offers': 'MOP W/O Offers',
    'digital-app-digital-app': 'Digital App',
    'digital-app-average-check': 'Average Check',
    'digital-app-trading': 'Trading',
    'digital-app-loyalty-self-id': 'Loyalty Self ID',
    'pct-sales': '% Sales',
    'kiosk-w-offer': 'Kiosk w/ Offer',
    'kiosk-w-o-offer': 'Kiosk w/o Offer',
    'mcDelivery-w-offer': 'McDelivery w/ Offer',
    'mcDelivery-w-o-offer': 'McDelivery w/o Offer',
    'gma-delivery-w-offer': 'GMA Delivery w/ Offer',
    'gma-delivery-w-o-offer': 'GMA Delivery w/o Offer',
    'digital-order-points-digital-order-pts': 'Digital Order Pts',
    'digital-order-points-average-check': 'Average Check',
    'digital-order-points-trading': 'Trading',
    'digital-order-points-kiosk': 'Kiosk',
    'below-threshold': 'below lower target threshold',
    'between-threshold': 'between target thresholds',
    'above-threshold': 'above upper target thresholds',
    'any-order-containing': 'Any order containing a Digital App and/or Digital Order Point component',
    'snapshot-digital-usage-red': 'red',
    'snapshot-digital-usage-yellow': 'yellow',
    'snapshot-digital-usage-green': 'green',
    'snapshot-digital-usage-no-data': 'No Data',
    'snapshot-digital-usage-digital-usage': 'Digital Usage',
    'snapshot-digital-usage-error-loading-data': 'Error Loading Data',
    'agency': 'Agency',
    'local-offer': 'Local Offer',
    'national-offer': 'National Offer',
    'non-offer': 'Non-Offer',
    'bu-watchlist-no-data': 'No Data - Either',
    'bu-watchlist-select-a-bigger-date-range': 'select a bigger date range where this menu items occurred',
    'bu-watchlist-ask-your-agency': 'ask your agency to select up to two menu items to display here',
    'bu-watchlist-meal-pct-of-gc': '% of Total GC',
    'bu-watchlist-avg-check': 'Avg Check',
    'prior': '8/10/2025 - 9/6/2025',
    'extra-value-meal-comp-type-selector-trading': 'Prior Year (Trading)',
    'five-and-eight-units-served-rd': 'Units Served/R/D',
    'five-and-eight-avg-check': 'Avg Check',
    'five-and-eight-september': 'Big Mac/SME',
    'five-and-eight-november': '10 Pc Nug/SEC McGrd',
    'five-and-eight-january': '2 Wrps/Sau Egg McMuff',
    'five-and-eight-november-2026': 'QPC/SME',
    'five-and-eight-meal-pct-of-gc': '% of GC',
    'five-and-eight-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'for-more-detail': 'For more detail, please visit the',
    'pmix-report': 'Pmix Report',
    'national-watchlist-no-data': 'No Data - Either',
    'national-watchlist-select-a-bigger-date-range': 'select a bigger date range where this menu items occurred',
    'national-watchlist-ask-your-agency': 'ask your agency to select up to two menu items to display here',
    'national-watchlist-meal-pct-of-gc': '% of Total GC',
    'national-watchlist-avg-check': 'Avg Check',
    'national-watchlist-national': 'National',
    'national-watchlist-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'scorecard-ranking': 'Scorecard Ranking',
    'show-goal-diff': 'Show Goal Diff',
    'pivot': 'Pivot',
    'rank-settings': 'Rank Settings',
    'score-card-rank-loc': 'Loc',
    'score-card-rank-patch': 'Patch',
    'card-settings': 'Card Settings',
    'rows': 'Rows',
    'widget-settings-thresholds': 'Thresholds',
    'watchlist-modal-local': 'Local Offer',
    'watchlist-modal-watchlist': 'Watchlist',
    'watchlist-modal-filter': 'Filter',
    // Migrated from <i18n> SFC blocks (#6500)
    'share': 'Share',
    'preparing_report': 'Preparing your report...',
    'share_as': 'Share as',
    'image': 'Image',
    'share-table': 'Table',
    'All Filters': 'All Filters',
    'Clear All': 'Clear All',
    'Case Number': 'Case Number',
    'Search Case Number': 'Search Case Number',
    'Customer Name': 'Customer Name',
    'Search Customer Name': 'Search Customer Name',
    'Status': 'Status',
    'Recovery Required': 'Recovery Required',
    'Global Performance Matrix': 'Global Performance Matrix',
    'Type': 'Type',
    'ALL FILTERS {count}/{total}': 'ALL FILTERS {count}/{total}',
    'voice-analysis': 'Voice Analysis',
    '1-800-analysis': '1-800 Analysis',
    'tab-1-800': '1-800',
    'tab-voice': 'VOICE',
    'setup-1800-instructions': 'To set up 1-800, please follow the instructions',
    'payload-too-large': 'The data request is too large. Please reduce your date range or apply filters to view the data.',
    'deleted': 'Deleted',
    'delete-selected': 'Delete Selected ({count})',
    'close-selected': 'Close Selected ({count})',
    'feedback-type-complaint': 'Complaint',
    'feedback-type-praise': 'Praise',
    'feedback-type-inquiry': 'Inquiry',
    'complaints-per-100k-gc': 'Complaints per 100K GC',
    'praise-per-100k-gc': 'Praise per 100K GC',
    'inquiries-per-100k-gc': 'Inquiries per 100K GC',
    'col-quick-close': 'Quick Close',
    'col-deleted-by': 'Deleted By',
    'unknown': 'Unknown',
    'col-processed-date': 'Processed Date',
    'col-created-date': 'Created Date',
    'col-incident-date': 'Incident Date',
    'col-case-number': 'Case Number',
    'col-type': 'Type',
    'col-customer-name': 'Customer Name',
    'col-recovery-required': 'Recovery Required',
    'col-status': 'Status',
    'col-resolution': 'Resolution',
    'col-actions': 'Actions',
    'col-created-from': 'Created From',
    'col-case-origin': 'Case Origin',
    'col-issue-category': 'Issue Category',
    'col-issue-reason': 'Issue Reason',
    'col-issue-subcode': 'Issue Subcode',
    'col-global-performance-matrix': 'Global Performance Matrix',
    'col-recovery-next-steps': 'Recovery Next Steps',
    'col-recovery-form': 'Recovery Form',
    'col-customer-email': 'Customer Email',
    'col-customer-phone': 'Customer Phone',
    'col-location-address': 'Location Address',
    'col-order-point': 'Order Point',
    'col-pickup-point': 'Pickup Point',
    'col-letter-sent': 'Letter Sent',
    'col-customer-comments': 'Customer Comments',
    'add-shift': 'Add Shift',
    'job-title': 'Job Title',
    'shift-times': 'Shift Times',
    'shift-length': 'Shift Length',
    'breaks': 'Breaks',
    'add-break': 'Add break',
    'break-tooltip': 'Breaks are scheduled to avoid peak meal hours and conflicts with other employees\' breaks.',
    'meal-break': 'Meal Break',
    'rest-break': 'Rest Break',
    'scheduling-conflicts': 'Scheduling Notes',
    'add-to-schedule': 'Add to Schedule',
    'add-shift-dialog-this-week': 'this week',
    'add-shift-dialog-hours-this-week': 'Hours This Week',
    'available-employees': 'Unscheduled Employees',
    'tooltip-description': 'Employees not scheduled today. Numbers indicate hours this week. Click an employee to view their contact info. Highlighted rows indicate minors.',
    'available-employees-error-loading': 'Error loading employee data',
    'available-employees-no-data-available': 'No available employees',
    'swap-shift': 'Swap Shift',
    'current-employee': 'Current Employee',
    'scheduled-shift': 'Scheduled Shift',
    'not-working-today': 'Not Working Today',
    'working-description': 'Select an employee who is already working today. Both employees will swap their actual work times.',
    'not-working-description': 'Select an available employee to take over this shift. The new employee will replace the original on this shift.',
    'select-employee': 'Select Employee',
    'their-shift': 'Their Shift',
    'will-work': 'will work',
    'will-take-over': 'will take over the shift',
    'additional-notes': 'Additional Notes',
    'notes-placeholder': 'Optional notes about this swap...',
    'confirm-swap': 'Confirm Swap',
    'swap-shift-dialog-hours-this-week': 'Hours This Week',
    // Migrated from <i18n> SFC blocks (#6500)
    'dashboard-settings-dashboard-settings': 'Dashboard Settings',
    'customize-dashboard-containers': 'Customize Dashboard Containers',
    'unable-to-save-dashboard-settings': 'Unable to save dashboard settings.',
    'unable-to-reset-dashboard-settings-to-default': 'Unable to reset dashboard settings to default.',
    'shift-dashboard': 'Shift Dashboard',
    'disclaimer': 'FO sales results are displayed after McDonalds quarterly sales release.',
    'roiOnePager': 'Return On Investment',
    'clickToSetup': 'Click to view consulting ROI report',
    'clickToViewMore': 'Click here to view more',
    'qsrsoftConsulting': 'QSRsoft Consulting',
    'yourOrganization': 'Your Organization',
    'heroTitle': 'You Built Something Valuable.',
    'heroSubtitle': 'Now let\'s protect it — and grow what you\'ve built.',
    'heroAmountText': '{amount} is leaving your bank account today',
    'heroShowYou': 'We\'ll show you',
    'exactlyWhere': 'exactly where',
    'andHowTo': 'and how to',
    'takeItBack': 'take it back',
    'theProblem': 'The Problem',
    'theGuide': 'THE GUIDE',
    'guideSubtitle': 'Nearly 100 years inside the system.',
    'excessiveCrewLabor': 'Excessive Crew Labor',
    'laborText': '6-month cashflow opportunity — {amount} per store silently eroding your bottom line.',
    'foodOpportunity': 'Food Opportunity',
    'foodText': 'Benchmarked against a norm of best-in-class operators - {amount} per store opportunity.',
    'gcOpportunity': 'GC Opportunity',
    'guestCountText': 'Comp GC Gap to the BU opportunity',
    'theTransformation': 'The Transformation',
    'transformationTitle': 'The Transformation — A {return} Return. A {roi}%+ ROI.',
    'basedOnCapturing': 'Based on capturing just 25% of the opportunity',
    'thePlan': 'THE PLAN — 3 STEPS, 90 DAYS',
    'diagnose': 'Diagnose',
    'diagnoseText': 'Connect to your QSRSoft data. Build a precise, store-by-store, category-by-week picture of exactly where cash is leaking.',
    'target': 'Target',
    'targetText': 'Rank highest-value opportunities — crew labor, overtime, hours of operation — and build a prioritized plan WITH your GMs.',
    'capture': 'Capture',
    'captureText': 'Coach your team through execution. Track captured value in real time. Close the gap from where you are to where you want to be.',
    'whatOperatorsSay': 'WHAT OPERATORS ARE SAYING',
    'testimonial1': 'Lowered crew labor targets 4% for the trailing twelve months. Grew cashflow by over $1 million.',
    'testimonial2': 'Moved from data collection to meaningful improvement. GMs more aware, admin more efficient.',
    'testimonial3': 'Saved hundreds of thousands in labor — while still growing guests and outperforming our business unit.',
    'franchisee': 'McDonald\'s Franchisee',
    'nextStepSimple': 'Your next step is simple.',
    'letsTalk': 'Let\'s Talk',
    'see-all-peers': 'See All Peers',
    'this-setting-will': 'This setting will allow users to see peers that don\'t directly report to their supervisor. This will',
    'only': 'only',
    'affect the peers widget': 'affect the peers widget',
    'show-peers-across-hierarchy': 'Show Peers Across Hierarchy',
    'hierarchy': 'Hierarchy',
    'hierarchy-container-locations': 'Locations',
    'hierarchy-grid-no-data': 'No Data',
    'peers': 'Peers',
    'avg-sales': 'Avg Sales',
    'avg-net-sales': 'Avg Net Sales',
    'oepe-wo-park': 'OEPE W/O Park',
    'dt-total-time': 'DT Total Time',
    'kvs-items-per-gc': 'KVS Items / GC',
    'healthy-use': 'Healthy Use',
    'healthy-usage': 'Healthy Usage',
    'kvs-time-per-item': 'KVS Time / Item',
    'peers-sales-kvs-time-per-gc': 'KVS Time / GC',
    'peers-sales-dt-sales': 'DT Sales',
    'peers-sales-dt-average-check': 'DT Average Check',
    'incentive-grid-no-data': 'No Data',
    'incentive-grid-location': 'Location',
    'incentive-grid-trend': 'Trend',
    'select-metric': 'Select Metric',
    'array-cannot-be-empty': 'Array cannot be empty',
    'incentive-trend-trend': 'Trend',
    '3d-guest-count-club': '3D Guest Count Club',
    'average-daily-guest-counts': 'Average Daily Guest Counts',
    'gma-kiosk': 'GMA & Kiosk',
    'gmad-3po': 'GMAD & 3PO',
    'widget-drive-thru': 'Drive Thru',
    'rank-out-of': 'Rank out of',
    'restaurants': 'Restaurants',
    'practice-week': 'Practice Wk',
    'overall-rank': 'Overall Rank',
    'created-by': 'Created By',
    'custom-incentive-week': 'Wk',
    'incentive-trend': 'Incentive Trend',
    'bu': 'BU',
    'the-ranking-is-the-same-as': 'The ranking is the same as recent Big Bets Incentive. The data is sum all peaks. Stores need to be open for at least a year and not be closed for more then 1 day in both this year and last year.',
    'the-overall-rank-is-based-on': 'The Overall Rank is based on the McD Point calculation. The rank is based off a cumulative score starting from 7/07/2024 to yesterday',
    'gc-day-plus-minus': 'GC/Day +/-',
    'guest-count-per-store-per-day': 'Guest Count per Store per Day compared to last year',
    'digital-gc-day-plus-minus': 'Digital GC/Day +/-',
    'digital-order-points-gc-r-d': 'Digital Order Pts GC/R/D compared to last year',
    'oepe-w-o-park': 'OEPE W/O Park',
    'order-end-to-present-end': 'Order End to Present End without including parked orders; time from when the customer finishes ordering until the order is presented.',
    'oepe-w-o-park-plus-minus': 'OEPE W/O Park +/-',
    'oepe-w-o-parked-compared-to-last-year': 'OEPE W/O Parked Compared to last year',
    'quality-sat-percent': 'Quality Sat %',
    'overall-quality-satisfaction-score': 'Overall Quality Satisfaction Score',
    'default-incentives-widget-rank-out-of': 'Rank out of',
    'incentives': 'Incentives',
    'warm-up-week-1': 'Warm up Wk 1',
    'warm-up-week-2': 'Warm up Wk 2',
    'week-1': 'Wk 1',
    'week-2': 'Wk 2',
    'week-3': 'Wk 3',
    'week-4': 'Wk 4',
    'week-5': 'Wk 5',
    'week-6': 'Wk 6',
    'fall-incentives': 'GOAL-den Momentum',
    'fifa-club-world-cup': 'FIFA World Cup 26',
    'overall-rank-based-point-calculation': 'The Overall Rank is based on the McD Point calculation. The rank is based off a cumulative score starting from 9/21/2025 to yesterday',
    'vs-last-week': '\'Vs Last Week\' displays how much your overall rank has changed as of business close on the most recent Saturday',
    'mcd-corporation-calc': 'McDonald Corporation will calculate final ranking which will be used for final recognition',
    'fifa-upt-definition': '\'FIFA UPT +/-\' is the number of FIFA menu items sold per 1,000 GC, compared to the prior year',
    'upsized-definition': '\'Upsized %\' is the percent of Big Mac and 10 Pc meals that were upsized to Large out of total ROD meals',
    'rmhc-bonus': 'RMHC is a bonus metric that only adds points for weeks 1-3',
    'oepe-r2p': '* \'OEPE\' metric uses OEPE w/o Parks. Non-DT restaurants use R2P',
    'fall-incentives-widget-rank-out-of': 'Rank out of',
    'round-up': 'Round Up',
    'pos': 'POS',
    'register-type': 'POS Area',
    'round-up-qty-pct': 'Round Up Qty %',
    'round-up-by-pos-area-location': 'Location',
    'round-up-by-pos-area-grid-no-data': 'No Data',
    'rank-up-pos': 'Round Up POS/Employee',
    'warm-up-week-3': 'Warm up Wk 3',
    'warm-up-week-4': 'Warm up Wk 4',
    'summer-splash': 'Summer Splash',
    'summer-incentives-widget-overall-rank-based-point-calculation': 'The Overall Rank is based on the McD Point calculation. The rank is based off a cumulative score starting from 6/29/2025 to yesterday',
    'summer-incentives-widget-oepe-r2p': '* \'OEPE or R2P\' metric uses OEPE W/O Park unless the store does not have a drive thru, then this is R2P',
    'summer-incentives-widget-rod': 'Rest of Day (ROD) transactions excludes breakfast',
    'summer-incentives-widget-rank-out-of': 'Rank out of',
    'business-dates': 'Business Date(s)',
    'number-of-BDC-missing': '# of BDC missing',
    'bdc-grid-more-info': 'MORE INFO',
    'bdc-grid-location': 'Location',
    'missing-at-least': 'missing at least',
    'one-business-day-close': 'one business day close',
    'bdc-insight-store': 'store',
    'item-num': 'Item Num',
    'desc': 'Desc',
    'used-diff': 'Used Diff',
    'dollar-diff': '$ Diff',
    'pct-net-sales': '% of Net Sales',
    'stat-grid-title': 'Inventory Loss Details',
    'stat-grid-store': 'Loc',
    'most-recent-inventory': 'Most Recent Inventory',
    'top-loss-items': 'Top 5 Loss Items',
    'no-loss-items': 'No loss items found',
    'raw-waste-amount': 'Raw Waste $',
    'raw-waste-qty': 'Raw Waste Qty',
    'comp-waste-amount': 'Comp Waste $',
    'comp-waste-qty': 'Comp Waste Qty',
    'missing-type': 'Missing Type',
    'both': 'Both',
    'raw-only': 'Raw Only',
    'comp-only': 'Comp Only',
    'waste-entry-grid-title': 'Waste Entry Details',
    'waste-entry-grid-store': 'Loc',
    'stores-missing-raw-waste': 'Stores Missing Raw Waste',
    'stores-missing-comp-waste': 'Stores Missing Comp Waste',
    'stores-missing-raw-waste-entry': 'stores haven\'t entered raw waste in the last 7 days',
    'stores-missing-comp-waste-entry': 'stores haven\'t entered comp waste in the last 7 days',
    'store-missing-raw-waste-entry': 'store hasn\'t entered raw waste in the last 7 days',
    'store-missing-comp-waste-entry': 'store hasn\'t entered comp waste in the last 7 days',
    'day-threshold-settings-days': 'Days',
    'filter-message-open-late': '** Displaying stores that have opened late more than the Days Missed Limit ({daysThreshold}) days in the last 7 opens.',
    'filter-message-close-early': '** Displaying stores that have closed early more than the Days Missed Limit ({daysThreshold}) days in the last 7 closes.',
    'olce-grid-more-info': 'MORE INFO',
    'olce-grid-minutes': 'minutes',
    'olce-grid-days-missed-limit': 'Days Missed Limit can be adjusted in the Insights settings.',
    'olce-grid-filter-2': 'Filter',
    'full-employee-name-header': 'Name',
    'crew-type-header': 'Crew Type',
    'termed-header': 'Terminated',
    'hire-date-header': 'Hire',
    'termination-date-header': 'Termination',
    'merged-terms-more-info-yes': 'yes',
    'merged-terms-more-info-no': 'no',
    'crew-termed-prefix': 'crew termed in the first',
    'crew-termed-suffix': 'days since hire.',
    'turnover-prefix': 'Turnover is over',
    'turnover-with': 'with',
    'turnover-suffix': 'crew terminated within 90 days.',
    'with-90-day-turnover-above-prefix': 'with 90 day turnover above',
    'people-terms-insight-no-30-day-terms': 'No crew termed in the first 30 days.',
    'people-terms-insight-location-2': 'location | locations',
    'ot-grid-title': 'Potential OT',
    'this-metric-does-not-have-any-goals-set': 'This Metric Does Not Have Any Goals Set',
    'all-goals': 'All Goals',
    'employees-over-threshold': 'employees are on track to work over {threshold} hours this week.',
    'ot-threshold-settings-hours': 'Hours',
    'prod-out-grid-all-outages': 'ALL OUTAGES',
    'prod-out-grid-menu-item-number': 'Menu Item Number',
    'prod-out-grid-active-product-outages': 'Active Product Outages',
    'prod-out-grid-more-info': 'More Info',
    'watch-lists': 'Watch Lists',
    'equipment-tickets': 'Equipment Tickets',
    'open-seven-days': 'open > 7 days',
    'customer-recovery': 'Customer Recovery',
    'open-forty-eight-hours': 'open > 48 hours',
    'training-certificates': 'Training Certificates',
    'to-expire': 'to expire < 30 days',
    'simplethink-info-title': 'You are not currently subscribed to SimpleThink.',
    'simplethink-info-I': 'SimpleThink™ by QSRSoft is an all-in-one digital assistant to help you run your restaurant with more of the',
    'simplethink-info-II': 'right',
    'simplethink-info-III': 'controls. Personalized, playful experiences that encourage, reward, and drive great behaviors.',
    'simplethink-info-IV': 'Streamline your operations and supercharge your crew!',
    'simplethink-info-modal-button': 'Learn More',
    'tickets-open-seven-days': 'Tickets open > 7 days',
    'certificates-to-expire': 'Certificates to expire < 30 days',
    'tickets-open-report': 'Tickets Open > 7 Days',
    'training-certificates-report': 'Training Certificates to expire < 30 days',
    'customer-recovery-report': 'Customer Recovery Open > 48hrs',
    'simple-think-insight-training': 'Training',
    'location-header': 'Location',
    'last-name-header': 'Last Name',
    'course-name-header': 'Course Name',
    'confirmation-id-header': 'Confirmation ID',
    'issue-date-header': 'Issue Date',
    'expiration-date-header': 'Expiration Date',
    'bdc-prefix': 'BDC -',
    'instore-events-insight-old-stores-with-alerts': '{count} store(s) with active alerts',
    'instore-events-insight-old-stores-with-alerts-suffix': 'store(s) with active alerts',
    'instore-events-insight-old-store': 'store',
    'instore-events-insight-old-one': 'one',
    'instore-events-insight-old-more-info': 'More Info',
    'merged-alerts-more-info-business-dates': 'Business Date(s)',
    'merged-alerts-more-info-number-of-BDC-missing': '# of BDC missing',
    'employees-with-more-than': 'Employees With More Than ',
    'employee-view-modified-punches': ' Modified Punches ',
    '%-of-modified-punches-in-last-7-days:': ' Above avg modified punches in last 7 days:',
    'store-trend': 'store above store trend | stores above store trend',
    'org-trend': 'store above org trend | stores above org trend',
    'employee-threshold-part1': 'employee had more than | employees had more than',
    'employee-threshold-part2': ' modified punches in the last pay period',
    'mod-punches': 'Modified Punches',
    'modified-punches-tabs-store-trend': 'Store Trend',
    'modified-punches-tabs-org-trend': 'Org Trend',
    'stores-above-org-trend-in-last-7-days': 'Stores Above Org Trend in Last 7 Days',
    'punches-threshold-settings-punches': 'Punches',
    'stores-above-store-trend-in-last-7-days': 'Stores Above Store Trend in Last 7 Days',
    'rbd-completions': 'RBD Completions',
    'rbd-activations': 'RBD Activations',
    'merged-rbd-more-info-store': 'Loc',
    'merged-rbd-more-info-more-info': 'More Info',
    'rbd-completed-grid-store': 'Loc',
    'rbd-completed-grid-more-info': 'More Info',
    'store-has': 'store has',
    'stores-have': 'stores have',
    'or-more': 'or more',
    'error-try-again': 'ERROR - TRY AGAIN',
    'rbd-completed-insight-rbd-completions': 'RBD completions in the last 30 days',
    'rbd-completions-label': 'RBD Completions',
    'rbd-completions-suffix': 'in the last 30 days.',
    'no-rbd-completions': 'No stores over the RBD completions threshold.',
    'store-has-rbd': 'store has at least one RBD activated in the last 30 days but not completed.',
    'stores-have-rbd': 'stores have at least one RBD activated in the last 30 days but not completed.',
    'store-has-prefix': 'store has at least one',
    'stores-have-prefix': 'stores have at least one',
    'rbd-activated': 'RBD Activated',
    'activated-middle': 'in the last 30 days but',
    'not-completed': 'not completed.',
    'no-rbd-activations': 'No stores with pending RBD activations.',
    'rbd-insight-rbd-completed': 'Completed',
    'rbd-insight-rbd-activation': 'Activation',
    'rbd-insight-you-have': 'you have',
    'today-beta': 'Today (Beta)',
    'snapshot-digital': 'Digital Snapshot',
    'consulting': 'Consulting',
    'bu-comp': 'BU Comp',
    'main-security-drill-down': 'Security Drill Down',
    'main-file-manager': 'Resource Library',
    'main-transaction-analytics': 'Advanced Analytics',
    'over-ring': 'Over Ring',
    'red-before': 'Red Before',
    'red-after': 'Red After',
    'total-refund': 'Total Refund',
    'snapshot-cash-over-short': 'Over Short',
    'actual-labor-pct': 'Actual Labor %',
    'crew-labor-pct': 'Crew Labor %',
    'punched-labor-pct': 'Punched Labor %',
    'avg-rate': 'Avg Rate',
    'total-labor-pct': 'Total Labor %',
    'ot-$': 'OT',
    'ot-hrs': 'OT Hrs',
    'snapshot-link-container-point-of-order': 'Point Of Order',
    'avg-chk': 'Average Check',
    'breakfast-gc': 'Breakfast GC\'s',
    'dt-avg-chk': 'DT Average Check',
    'dt-gcs': 'DT GC\'s',
    'dt-gcs-qty': 'DT GC\'s Qty',
    'gcs': 'GC\'s',
    'prod-sales': 'Prod Sales',
    'gcs-qty': 'GC\'s Qty',
    'breakfast-gcs-qty': 'Breakfast GC\'s Qty',
    'comparison': 'Comparison',
    'showing-comparison-to': 'Showing Comparison to',
    'year-before-last': 'Year Before Last',
    'snapshot-sales-avg-sales': 'Avg Sales',
    'snapshot-sales-breakfast-sales': 'Breakfast Sales',
    'snapshot-sales-last-year': 'Last Year',
    'day-parts': 'Dayparts',
    'pmix-grid-item': 'Item',
    'setup-or-manage-watchlists': 'Setup or Manage Watchlists',
    'Thru': 'Thru',
    'date-options-for-pmix-widget-exclude': 'Date options for the PMIX widget exclude YTD, TTM, T365, and any custom range greater than 31 days.',
    'please-select-at-least-one': 'Please select at least one menu item or watchlist to view PMIX data.',
    'digital-snapshot': 'Digital Snapshot',
    'digital-hero-kpis': 'Digital Hero KPIs',
    'digital-diagnostics': 'Digital Diagnostics',
    'mop-fulfillment': 'MOP Fulfillment',
    'digital-hero-kpis-2024': '2024 Digital Hero KPIs',
    'snapshot-digital-container-digital-usage': 'Digital Usage',
    'snapshot-digital-container-digital-order-pts': 'Digital Order Pts',
    'snapshot-digital-container-digital-app': 'Digital App',
    'linked-payment-cnt': 'Linked Payment Cnt',
    'linked-payment-pct-digital': 'Linked Payment % Digital App GC',
    'linked-payment-pct-total-gc': 'Linked Payment % Total GC',
    'gma-delivery-pct': 'GMA Delivery % Total Delivery GC',
    'snapshot-digital-diagnostics-no-data': 'No Data',
    'snapshot-digital-diagnostics-error-loading-data': 'Error Loading Data',
    'digital-hero-kpi-definition': 'Digital Hero KPI Definition Overview',
    'kpi-name': 'KPI Name',
    'kpi-definition': 'KPI Definition',
    'total-digital-percent-of-sales': 'Total Digital Percent of Sales',
    'proportion-of-total-sales-digital-channel': 'Proportion of total sales that are from a Digital channel (app, kiosk, and/or delivery)',
    'digital-app-percent-of-sales': 'Digital App Percent of Sales',
    'proportion-of-total-sales-digital-app': 'Proportion of total sales that are from the digital app',
    'loyalty-90-day-active-customers': 'Loyalty 90-Day Active Customers',
    'number-of-customers-90-days': 'Number of customers* who placed at least one digital app transaction during the 90-Days ending the last day of the period selected',
    'excludes-customers-without-ccpa': '*This excludes any customers who opted out of CCPA',
    'average-number-digital-app': 'Average number of digital app transactions per restaurant per day',
    'new-digital-app-customers-per-30-days': 'New Digital App Customers per 30-Days',
    'number-of-customers-first-digital-app-transaction': 'Number of customers who had their first ever digital app transaction in the period selected, divided by the number of days in the period selected, multiplied by 30',
    'summarizes-performace-highest-digital-kpis': 'Summarizes performance of the highest priority Digital KPIs in 2024',
    'click-for-detail': 'Click for Detail',
    'data-for-these-metrics': 'Data for these metrics is through',
    'number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Number of customers* who placed at least one digital app transaction during the period selected',
    'new-digital-app-customers': 'New Digital App Customers',
    'mcDelivery-percent-of-sales': 'McDelivery Percent of Sales',
    'proportion-of-system-sales': 'Proportion of system sales that come from McDelivery (both 3PO and GMA)',
    'pct-of-mcDelivery-sales': '% of McDelivery Sales from the App',
    'proportion-of-all-mcDelivery-sales': 'Proportion of all McDelivery sales that are from GMA Delivery transactions',
    'digital-hero2025-number-of-customers-first-digital-app-transaction': 'Number of customers who had their first ever digital app transaction in the period selected',
    'this-excludes-customers-opted-out': '*This excludes any customers who opted out of the California Consumer Privacy Act (and therefore are ineligible to participate in the MyMcDonald\'s Rewards Program)',
    'note-this-metric-only-gets-displayed': 'Note: This metric only gets displayed at an Operator or single store-level. The functionality to see this metric when multiple stores are selected is not currently available',
    'number-of-customers-who-had-first-digital-app-transaction': 'Number of customers who had their first ever digital app transaction in the period selected, divided by the number of days in the period selected, multiplied by 30*',
    'this-metric-normalizes': '*This metric normalizes for the number of days in a period, allowing for more effective comparisons of trends. E.g., March will always have more new digital app customers than February due to having more days in the month. However, by normalizing for number of days, this metric allows for a fair comparison of new customer acquisition between the months',
    'digital-hero-rank-trend': 'Trend',
    'digital-hero-rank-number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Number of customers* who placed at least one digital app transaction during the 90 days ending the last day of the period selected',
    'digital-hero-rank-rankings': 'Rankings',
    'digital-hero-rank2025-trend': 'Trend',
    'digital-hero-rank2025-number-of-customers-who-had-first-digital-app-transaction': 'Number of customers who had their first ever digital app transaction in the period selected',
    'digital-hero-rank2025-rankings': 'Rankings',
    '90-day-active-customer-trend': '90 Day Active Customer Trend',
    'table': 'Table',
    'difference': 'Difference',
    'box-target': 'Target',
    'box-container-no-data': 'No Data',
    'snapshot-month-container-food': 'Food',
    'raw-waste-pct': 'Raw Waste %',
    'condiments-pct': 'Condiments %',
    'base-food-pct': 'Base Food %',
    'comp-waste-pct': 'Comp Waste %',
    'discount-pct': 'Discount %',
    'emp-meals-pct': 'Emp Meals %',
    'stat-variance-pct': 'Stat Var %',
    'unexplained-pct': 'Unexplained %',
    'snapshot-month-food-food': 'Food',
    'sales-and-service': 'Sales & Service',
    'today.retiring-soon-prefix': 'Retiring soon! Click',
    'today.here': 'here',
    'today.retiring-soon-suffix': 'to update to the new and improved Today view.',
    'today.why': 'Why?',
    'today.tooltip.better-labor': 'Better Labor Reporting',
    'today.tooltip.aligns-dar': 'Aligns to DAR report',
    'today.tooltip.additional-metrics': 'Additional metrics available for customization',
    'today-container-labor': 'Labor',
    'actual-vs-needed': 'Actual vs Needed',
    'actual-hrs-vs-needed': 'Actual Hrs vs Needed',
    'actual-vs-scheduled': 'Actual vs Scheduled',
    'actual-hrs-vs-scheduled': 'Actual Hrs vs Scheduled',
    'sales-per-punched-hr': 'Sales / Punched Hr',
    'gcs-per-punched-hr': 'GCs / Punched Hr',
    'today-labor-labor': 'Labor',
    'today-labor-avg-rate': 'Avg Rate',
    'today-labor-punch-labor-pct': 'Punch Labor %',
    'product-sales-comp-pct': 'Product Sales +/- %',
    'average-check-comp-pct': 'Average Check +/- %',
    'gcs-comp-pct': 'GC\'s +/- %',
    'oepe-wo-park-comp': 'OEPE W/O Park +/-',
    'oepe': 'OEPE',
    'oepe-comp': 'OEPE +/-',
    'r2p': 'R2P',
    'r2p-comp': 'R2P +/-',
    'kvs-healthy-usage-comp': 'KVS Healthy Usage +/-',
    'kvs-time-per-gc-comp': 'KVS Time / GC +/-',
    'today-sales-and-service-kvs-time-per-gc': 'KVS Time / GC',
    'today-sales-and-service-all-net-sales': 'All Net Sales',
    'today-sales-and-service-all-net-sales-comp-pct': 'All Net Sales +/- %',
    'today-sales-and-service-prod-sales': 'Prod Sales',
    'today-sales-and-service-average-check': 'Average Check',
    'today-sales-and-service-gcs': 'GC\'s',
    'today-sales-and-service-avg-ctp': 'Avg CTP',
    'today-sales-and-service-avg-ctp-comp': 'Avg CTP +/-',
    'sales-per-punched': 'Sales / Punched Hr',
    'gc-per-punched': 'TPPH',
    'today-widget-kvs-time-per-gc': 'KVS Time / GC',
    'today-widget-labor': 'Labor',
    'today-widget-all-net-sales': 'All Net Sales',
    'today-widget-all-net-sales-comp-pct': 'All Net Sales +/- %',
    'today-widget-prod-sales': 'Prod Sales',
    'today-widget-average-check': 'Average Check',
    'today-widget-gcs': 'GC\'s',
    'today-widget-dt-sales': 'DT Sales',
    'today-widget-avg-dt-ttl': 'Avg DT TTL',
    'today-widget-kvs-items-per-gc': 'KVS Items / GC',
    'today-widget-avg-ctp': 'Avg CTP',
    'today-widget-kvs-time-per-item': 'KVS Time / Item',
    'today-widget-avg-rate': 'Avg Rate',
    'today-widget-punch-labor-pct': 'Punch Labor %',
    'the-menu-items-in-this-container': 'The menu items in this container are selected by your agency. That allows you to align with your agency on the most important menu items happening in your restaurants and market. Please direct questions on the details of these menu items to your agency.',
    'bu-menuitems-no-data': 'No Data - Either',
    'bu-menuitems-select-a-bigger-date-range': 'select a bigger date range where this menu items occurred',
    'bu-menuitems-ask-your-agency': 'ask your agency to select up to two menu items to display here',
    'bu-menuitems-meal-pct-of-gc': '% of Total GC',
    'bu-menuitems-avg-check': 'Avg Check',
    'date-selector-ok': 'OK',
    'launch-freebie-verbiage': 'Advanced Analytics (TA) has been temporarily activated to support the system EVM initiative launch. To continue and have full TA visibility, contact your BU lead to discuss a subscription.',
    'extra-value-meal-container-extra-value-meal': 'Extra Value Meal',
    'extra-value-meal-container-start-date': 'Start Date',
    'extra-value-meal-container-end-date': 'End Date',
    'report-rankings-title': '{report} Rankings - {date}',
    'ta-rank-trend': 'Trend',
    'ta-trend': 'TA Trend',
    'spicy-mcmuffins': 'Spicy McMuffins',
    '5-meal-deal': '$5 Meal Deal',
    'base-food-decomposition': 'Base Food Decomposition',
    'transaction-analytics-container-mcCrispy-strips': 'New Chicken Platform',
    'transaction-analytics-container-two-year-growth': 'Two Year Growth',
    'transaction-analytics-container-addon-subscribed': 'Add-on · Subscribed',
    'transaction-analytics-container-addon-upgrade': 'Add-on · Upgrade',
    'transaction-analytics-container-addon-upgrade-tooltip': 'Talk to your BU lead about adding Advanced Analytics for:\n1. Agency & BU Support via a Business Insights application\n2. O/O Support via MyQ on National & BU offers and events',
    'transaction-analytics-container-transaction-analytics': 'Advanced Analytics',
    'transaction-analytics-container-boao-for-1': 'BOAO for $1',
    'no-options-selected': 'Your agency has not selected any options for this Business Unit yet. Ask your agency to select offers and menu items to display here.',
    'this-and-agency-support': 'This is part of the Advanced Analytics BU Service',
    'talk-to-bu-lead': 'Talk to your BU lead about adding Advanced Analytics for:',
    'agency-and-bu-support': 'Agency & BU Support via a Business Insights application',
    'oo-support-myq': 'O/O Support via MyQ on National & BU offers and events',
    'current-incentives': 'Current National Events',
    'transaction-analytics-service-disclaimer-learn-more': 'Learn More',
    'net-sales-comp': 'Net Sales/R +/-',
    'pr-yr-net-sales-comp': 'Pr Yr Net Sales/R +/-',
    'gc-comp': 'GC/R +/-',
    'pr-yr-gc-comp': 'Pr Yr GC/R +/-',
    'agency-control': 'Agency Control',
    'bu-offers-no-data': 'No Data - Please',
    'bu-offers-meal-pct-of-gc': '% of Total GC',
    'bu-offers-avg-check': 'Avg Check',
    'base-food-decomp-trend': 'Base Food Decomp Trend',
    'trend-chart-placeholder': 'Trend chart will be implemented here',
    'copied': 'Copied to clipboard!',
    'ROD': 'ROD',
    'urd': 'U/R/D',
    'extra-value-meal-report-title': 'Extra Value Meal: 15%',
    'extra-value-meal-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'breakfast-abbr': 'BFAST',
    'five-and-fifteen-five-and-fifteen': '5%/15%',
    'five-and-fifteen-units-served-rd': 'Units Served/R/D',
    'five-and-fifteen-avg-check': 'Avg Check',
    'five-and-fifteen-meal-pct-of-gc': '% of GC',
    'five-and-fifteen-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'menu-item-selector': 'Set of menu items',
    'metric-selector-day': 'Per Day',
    'metric-selector-gc': 'Per 1K Guests',
    'per-day-label': 'Units Sold/R/D',
    'per-gc-label': 'Units Sold/1K GC',
    'week-range': 'Sun-Sat',
    'trend-prior-no-data': 'No Data',
    'trend-prior-report-title': 'Weekly Trend',
    'prior-year': 'Prior Year',
    'trend-trading-no-data': 'No Data',
    'trend-trading-report-title': 'Weekly Trend',
    'trend-trading-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'five-meal-deal-weekly-units': 'Weekly units, F&P investment and margin are averaged by GCs that include any national Meal Deal in the order',
    'mcCrispySandwiches': 'McCrispy Sandwiches',
    'snackWraps': 'Snack Wraps',
    'biscuits': 'Biscuits',
    'hot-honey-meal-pct-of-rod-gc': '% of GC',
    'hot-honey-units-served-1k-rod-gc': 'Units Served/1K GC',
    'mcCrispyStrips': 'McCrispy Strips',
    'this-category': 'This Category',
    'national-category-trend-no-data': 'No Data',
    'national-category-trend-report-title': '5 Week Trend',
    'national-rail-row-margin-pct': 'MARGIN %',
    'as-of': 'As of',
    'what-this-shows': 'For the food and condiment raw items the watched menu items\' recipes use: $ Diff (actual minus theoretical, summed over the selected stores) and % Prod Sales (absolute $ Diff over month-to-date product sales), largest % first. Open the rank view for each store, its last inventory date, and $ Diff from biggest loss to biggest gain.',
    'national-watchlist-stat-loss-no-data': 'No stat loss rows for the watched items in the selected restaurants on this end date',
    'national-watchlist-stat-loss-report-title': 'Stat Loss',
    'national-control': 'National',
    'national-widget-container-if-units-were-only-sold': 'If units were only served for 5 out of 7 days, then the "Day" in Units/R/D divides units by 5 days',
    'national-widget-container-mcCrispy-strips': 'New Chicken Platform',
    'national-widget-container-watchlist': 'Select Watchlist',
    'national-widget-container-boao-for-1': 'BOAO for $1',
    'national-widget-container-agency-menu-items-bevs': 'Agency Menu Items (Bevs)',
    'national-widget-container-agency-menu-items-bevs-summary': 'Agency Menu Items (Bevs) w/ Summary',
    'meal-pct-of-breakfast-gc': '% of Breakfast GC',
    'units-served-1k-breakfast-gc': 'Units Served/1K Breakfast GC',
    'spicy-mc-muffins-units-served-rd': 'Units Served/R/D',
    'spicy-mc-muffins-avg-check': 'Avg Check',
    'spicy-mc-muffins-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'you-can-only-have': 'You can only have up to 10 scorecards. Please delete one before creating a new one.',
    'click-on-the-cells-to-edit-like-excel': 'Click on the cells to edit - like Excel',
    'global-goals': 'Global Goals',
    'the-goals-that-you': 'The goals that you have set will apply to all scorecards. You can edit these goals in the main setting\'s \'GOALS\' tab.',
    'do-you-want-to-continue': 'Do you want to continue?',
    'yes-save-close': 'Yes, Save & Create',
    'create-score-card-create-scorecard': 'Create Scorecard',
    'create-score-card-name-assign': 'Name & Assign',
    'create-score-card-qsr-defaults': 'QSR Defaults',
    'create-score-card-defaults': 'Defaults',
    'no-goals-set': 'No goals set',
    'scorecard-name': 'Scorecard Name',
    'assigning-roles-to': 'Assigning roles to a scorecard will allow only users with those roles to view the scorecard',
    'create-copy': 'Create Copy',
    'score-card-container-settings-scorecard-settings': 'Scorecard Settings',
    'score-card-container-settings-scorecards-2': 'Scorecards',
    'goals-per-location': 'Goals Per Location',
    'all-locs': 'All Locs',
    'goal-grid-locations': 'Locations',
    'row-order': 'Row Order',
    'tab-date-selector-ok': 'OK',
    'customize-metrics': 'Customize Metrics',
    'row': 'Row',
    'thresholds': 'Thresholds',
    'healthy-limit': 'Healthy Limit',
    'under': 'Under',
    'between': 'Between',
    'upper': 'Upper',
    'lower': 'Lower',
    'threshold-settings-over': 'Over',
    'no-shared-links': 'No shared links have been added for your organization.',
    'that-need-attention': 'that need attention',
    'manage-requests': 'Manage Requests',
    'requests': 'requests',
    'employee-requests-insight-request': 'request',
    'employee-requests-insight-you-have': 'You have',
    // BUOverview chart series; only the -title key existed
    'two-year-growth': '2 Year Growth',
    // Migrated from <i18n> SFC blocks (#6500)
    'voice-rankings-voice-rankings': 'Voice Rankings',
    'financial-is-used-for-current-mid-month': 'Financial is used for current mid-month or date ranges that span outside completed months. Financial excludes locations that have not been open for a full 13 months. Default for Financial is Trading Day Comparison.',
    'analytic-is-used-for-completed-months': 'Analytic is used for completed months and date ranges that include completed months. Analytics uses a 5-day rule. If a location is missing sales for 5 or more days (in either Current Year or Prior Year) it is excluded from the calculation. Default for Analytic is Calendar Day Comparison.',
    'confirmDeletion': 'Confirm Deletion',
    'areYouSure': 'Are you sure you want to delete this watchlist?',
    'businessUnitMenuItemDetails': 'National Menu Item Details',
    'watchlist': 'Watchlist',
    'subtitle': 'Select up to five watchlists per category to display to O/Os in the AA section on the QSRSoft Home Dashboard. There will be no summary or total across the watchlists.',
    'widgetLabel': 'Widget Title',
    'national-menu-items': 'National Menu Items',
    'widgetDialogTitle': 'Edit Widget Title',
    'widgetTitleDisclaimer': 'Create a watchlist before setting the title',
    'watchlistName': 'Watchlist Name',
    'non_offer': 'Non-Offer',
    'national': 'National Offer',
    'availableMenuItems': 'Available Menu Items',
    'noMatchingMenuItems': 'No matching menu items found.',
    'showAll': 'Show All',
    'showSelected': 'Show Selected',
    'saveWatchlist': 'Save Watchlist',
    'clear': 'Clear',
    'widgetTitleLabel': 'Widget Title (Applies to all watchlists)',
    'construction-watchlist-modal-local': 'Local Offer',
    'construction-watchlist-modal-watchlist': 'Watchlist',
    'construction-watchlist-modal-filter': 'Filter',
    'bumid-businessUnitMenuItemDetails': 'Business Unit Menu Item Details',
    'menu-item-cell-selectMenuItem': 'Select Menu Item',
    'menu-item-modal-selectMenuItem': 'Select Menu Item',
    'menu-item-modal-availableMenuItems': 'Available Menu Items',
    'businessUnitOffers': 'Business Unit Offers',
    'selectOffersInstructions': 'Select up to two auto-bundles/mobile offers per BU to display to O/Os in the TA section of the MyQSRSoft Home Dashboard.',
    'promotionMembers': 'Promotion Members',
    'selectOffer': 'Select Offer',
    'transactionAnalytics': 'Advanced Analytics',
    'hideWidget': 'Hide',
    'showWidget': 'Show',
    'offers': 'Offers',
    'example-widget-businessUnit': 'BU',
    'availableOffers': 'Available Offers',
    'offerDetails': 'Offer Details',
    'selectOfferToViewDetails': 'Select Offer to view details',
    'promo-selector-back': 'Back',
    'bu-grid-noData': 'No Data',
    'operatorDashboard': 'Operator View Dashboard',
    'operatorDashboardDescription': 'View what operators see on a daily basis. For in-depth analysis and trending, select the',
    'businessIntelligence': 'Business Insights',
    'tool': 'tool.',
    'mcCrispy-strips': 'McCrispy Strips',
    'loyalty-90-day-active-trend': 'Loyalty 90-day Active Trend',
    'agency-menu-items': 'Agency Menu Items',
    'agency-menu-items-with-summary': 'Agency Menu Items with Summary',
    'agency-menu-items-bevs': 'Agency Menu Items (Bevs)',
    'agency-menu-items-bevs-summary': 'Agency Menu Items (Bevs) w/ Summary',
    'five-and-eight': '$5/$8',
    'five-and-fifteen': '$5/$15',
    'trend': 'Trending',
    'extra-value-meal-15': 'Extra Value Meal: 15%',
    'extra-value-meal-5': 'Extra Value Meal: 5%',
    'agency-offers': 'Agency Offers',
    'f-and-p-investment': 'F&P Investment',
    'bu-overview-voice': 'Voice',
    'bu-overview-food': 'Food',
    'bu-overview-digital-app': 'Digital App',
    'bu-overview-labor': 'Labor',
    'bu_loyalty_90_day_active_trend': 'BU Loyalty 90-day Active Trend',
    'bu_loyalty_90_day_active_trend_info': 'Number of unique customers who placed at least one digital app GC in the 90-day period. Because a customer can visit more than one restaurant in an organization, this number cannot be added across restaurants.',
    'widget_disabled_info': 'This Widget only works for BU',
    'bu_digital_app': 'BU Digital App',
    'bu_digital_order_pts': 'Digital Order Pts',
    'bu_food': 'Food',
    'bu_labor': 'Labor',
    'bu_sales': 'Sales',
    'bu_sales_financial': 'Financial is used for current mid-month or date ranges that span outside completed months. Financial excludes locations that have not been open for a full 13 months. Default for Financial is Trading Day Comparison.',
    'bu_sales_analytic': 'Analytic is used for completed months and date ranges that include completed months. Analytics uses a 5-day rule. If a location is missing sales for 5 or more days (in either Current Year or Prior Year) it is excluded from the calculation. Default for Analytic is Calendar Day Comparison.',
    'bu_service': 'Service',
    'people_metrics_info': 'People metrics are calculated using full months.',
    'people_metrics_multiple_months': 'If multiple months are selected, the most recent complete month is used.',
    'people_metrics_partial_month': 'If a past month is partially selected, the full month is used.',
    'people_metrics_detailed_breakdown': 'For a detailed breakdown, see the BU Rank report.',
    'peopleDataRequiresFullMonth': 'People data requires full month date range',
    'voiceDataTooRecent': 'Voice data is generally 2 days behind. Please select an earlier date range',
    'widgetOnlyWorksForBU': 'This Widget only works for BU',
    'bu-voice-noData': 'No Data',
    'boao-for-1': 'BOAO for $1',
    'business-units-not-signed-up': 'One or more of your Business Units have not signed up for Advanced Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'transaction-level-data-updates': 'Transaction level data updates into the current day for prior day. Please note that results may be incomplete early in day.',
    'units-sold-days': 'If units were only sold for 5 out of 7 days, then the "Day" in Units/R/D divides units by 5 days',
    'rod': 'ROD',
    'meal-pct-of-gc': '% of GC',
    'units-served-rd': 'Units Served/R/D *',
    'units-served-1k-gc': 'Units Served/1K GC',
    'avg-margin-amt': 'Avg Margin Amt',
    'margin-pct': 'Margin %',
    'bogo-avg-check': 'Avg Check',
    'chart-subtitle': 'Data for the period',
    'units-per-thousand': 'Units Served/1K GC',
    'bu-category-trend-no-data': 'No Data',
    'bu-category-trend-report-title': '5 Week Trend',
    'units-1k-gc': '/1K GC',
    'bu-rail-row-margin-pct': 'MARGIN %',
    'no-items-found': 'No items found',
    'bu-watchlist-section-watchlist': 'Select Watchlist',
    'meal-deal': 'Meal Deal',
    'weekly-units': 'Weekly units, F&P investment and margin are averaged by GCs that include any national Meal Deal in the order',
    'happy-meal-toy': 'The Happy Meal toy cost is what you have set on key 846. If that is blank, then we use $0.60',
    'explain-rod': 'ROD means “Rest of Day” and excludes the breakfast daypart (4am-11am)',
    'meal-pct-of-rod-gc': '% of ROD GC',
    'units-served-1k-rod-gc': 'Units Served/1K ROD GC',
    'five-meal-deal-units-served-rd': 'Units Served/R/D',
    'five-meal-deal-avg-check': 'Avg Check',
    'promo-&-discount': 'Promo & Disc F&P',
    'discount-f&p': 'Discount F&P = The Menu Item(s) discount % applied to the Menu Item(s) Food & Paper cost. Discount Food and Paper amount.',
    'promo-f&p': 'Promo F&P = the Food & Paper cost of the Promo\'d Menu Item(s).',
    'base-food-plus-promo-discount': 'Base Food + Promo & Disc F&P',
    'base-food': 'Base Food',
    'check-the': 'Check the ',
    'pmix-page': 'PMIX',
    'for-further-info': ' page for further info on Hot Honey',
    'mc-crispy-strips': 'McCrispy Strips',
    'hot-honey-business-units-not-signed-up': 'One or more of your Business Units have not signed up for Transaction Analytics. Locations of these Business Units will not be included in the report. Please contact your BU lead for more information.',
    'hot-honey-units-served-rd': 'Units Served/R/D',
    'hot-honey-avg-check': 'Avg Check',
    'hot-honey-report-title': 'Hot Honey',
    'hot-honey-meal-pct-of-gc': '% of GC',
    'mc-crispy-strips-for-further-info': ' page for further info on Wrap Combos',
    'mc-crispy-strips-units-served-rd': 'Units Served/R/D',
    'mc-crispy-strips-avg-check': 'Avg Check',
    'mc-crispy-strips-report-title': 'New Chicken Platform',
    'the-offers-in-this-container': 'The offers in this container are selected by your agency. That allows you to align with your agency on the most important offers happening in your restaurants and market. Please direct questions on the details of these offers to your agency.',
    'if-units-were-only-sold': 'If units were only sold for 5 out of 7 days, then the "Day" in Units/R/D divides units by 5 days',
    'select-a-bigger-date-range': 'select a bigger date range where this offer occurred',
    'ask-your-agency': 'ask your agency to select two offers (promotions or discounts) to display here',
    'offers-no-data': 'No Data - Please',
    'offers-meal-pct-of-gc': '% of Total GC',
    'offers-avg-check': 'Avg Check',
    'buSummaryContainersDescription': 'Advanced Analytics aligns agencies, McD, and operators on major events. Operators in subscribed BUs view these containers with data only from their organizations while agencies view these with BU-level information.',
    'extra-value-meal': 'Extra Value Meal',
    'two-year-growth-title': '2 Year Growth of {type}',
    'weekly-trading-comparison': 'Weekly Trading Comparison. Financial Sales logic used.',
    'excluded-stores': 'Excluded stores that did not have sales either this year or last year.',
    'watchlist-details-businessUnitMenuItemDetails': 'Business Unit Menu Item Details',
    'watchlist-details-subtitle': 'Select up to five watchlists per BU to display to O/Os in the TA section on the QSRSoft Home Dashboard. There will be no summary or total across the watchlists.',
    'custom-request-form-loading': 'Loading',
    'failed-message': 'Failed to load QuickSight dashboard',
    'new-extract': 'New Extract',
    'extracts': 'Extracts',
    'report-finder': 'Report Finder',
    'report': 'Report',
    // Migrated from <i18n> SFC blocks (#6500)
    'Su': 'Su',
    'M': 'M',
    'Tu': 'Tu',
    'W': 'W',
    'Th': 'Th',
    'F': 'F',
    'Sa': 'Sa',
    'day-type-calendar': 'Calendar',
    'day-type-business-date': 'Business Date',
    'pay-period': 'Pay Period',
    'start-and-end-date-correspond': '* Start and end dates correspond with pay cycles',
    'pay-period-selector-multi-end-date': 'End Date',
    'pay-period-selector-multi-v2-end-date': 'End Date',
    'quick-dates-v3-yesterday': 'YEST',
    'quick-dates-v3-last-month': 'LM',
    'quick-dates-v3-last-year': 'LY',
    'quick-dates-v3-month-to-date': 'MTD',
    'quick-dates-v3-year-to-date': 'YTD',
    'quick-dates-v3-trailing-365-days': 'T365',
    'bu-scope-selector-scope': 'Scope',
    'business-unit': 'Business Unit',
    'location-agg-and-location': '& Location',
    'location-agg-v2-by-location': 'By Location',
    'same-store-comp-all-store-comp-2': 'All Store Comp',
    'same-store-comp-same-store-comp-2': 'Same Store Comp',
    'only-active-stores': 'Data is restricted to active stores only.',
    'daypart-dropdown-v2-all': 'All',
    'peak-dropdown-v2-required': 'Dayparts are required',
    'peak-dropdown-v2-all': 'ALL',
    'Time Segment': 'Time Segment',
    'Peaks': 'Peaks',
    'Dayparts': 'Dayparts',
    'Timeslices': 'Timeslices',
    'Custom': 'Custom',
    'selected': 'selected',
    'timeslice-dropdown-required': 'Dayparts are required',
    'timeslice-dropdown-all': 'All',
    'timeslice-dropdown-v2-required': 'Dayparts are required',
    'timeslice-dropdown-v2-all': 'All',
    'select-report': 'Select Report',
    'view-all-scheduled-reports': 'View All Scheduled Reports',
    'report-schedule-schedule': 'Schedule',
    'schedule-reports': 'Schedule Reports',
    'daily-reports-always-retrieve-data-from-yesterday': 'Daily reports always retrieve data from yesterday',
    'weekly-reports-always-retrieve-data-from-the-previous-week': 'Weekly reports always retrieve data from the previous week.',
    'monthly-reports-always-retrieve-data-from-the-previous-month': 'Monthly reports always retrieve data from the previous month.',
    'frequency': 'Frequency',
    'recipients': 'Recipients',
    'invalid-email': 'Invalid email',
    'recipients-selector-required': 'Required',
    'day-of-month': 'Day of Month',
    'time-selector-day-of-week': 'Day of Week',
    // Migrated from <i18n> SFC blocks (#6500)
    'register-type-selector-register': 'Register Type',
    'select-date': 'Select Date',
    'tomorrow': 'Tomorrow',
    'two-days-ago': '2 Days Ago',
    'quick-dates-last-week': 'LW',
    'next-week': 'NW',
    'quick-dates-this-week': 'TW',
    'quick-dates-this-month': 'TM',
    'next-month': 'NM',
    'week-to-date': 'WTD',
    '24-months': '24 MTH',
    '36-months': '36 MTH',
    'quick-dates-yesterday': 'YEST',
    'quick-dates-last-month': 'LM',
    'quick-dates-last-year': 'LY',
    'quick-dates-month-to-date': 'MTD',
    'quick-dates-year-to-date': 'YTD',
    'quick-dates-trailing-365-days': 'T365',
    'quick-dates-v2-yesterday': 'YEST',
    'quick-dates-v2-last-month': 'LM',
    'quick-dates-v2-last-year': 'LY',
    'quick-dates-v2-month-to-date': 'MTD',
    'quick-dates-v2-year-to-date': 'YTD',
    'quick-dates-v2-trailing-365-days': 'T365',
    'sum-all-locations-long': 'Summary',
    'by': 'By',
    'and-location': '& Loc',
    'location-agg-sum-all-locations': 'Summary',
    'location-agg-by-location': 'By Location',
    'same-store-comp-all-store-comp': 'All Store Comp',
    'same-store-comp-same-store-comp': 'Same Store Comp',
    'daypart-dropdown-all': 'All',
    'by-day': 'By Day',
    'by-timeslice': 'By Timeslice(s)',
    'peak-dropdown-required': 'Dayparts are required',
    'peak-dropdown-all': 'ALL',
    'label': 'View Segment By',
    'timeslice-submenu-label': 'Timeslice(s)',
    'segment-by-dropdown-by-time-slice': 'By Timeslice(s)',
    // Generic actions
    reset: 'Reset',
    discard: 'Discard',

    // Empty / error states
    'no-data': 'No data',
    'an-error-occurred-while-loading-the-report':
      'An error occurred while loading the report. Please reduce the stores or date range. If the problem is not resolved, please contact support.',

    // Filter containers
    filters: 'Filters',
    'please-select-at-least-one-location': 'Please select at least one location',
    'please-select-a-scope': 'Please select a scope',
    'please-select-a-date-range': 'Please select a date range',
    'please-select-a-time-segment': 'Please select a time segment',
    'please-select-please-select-at-least-one-daypart':
      'Please select at least one daypart',
    'please-select-please-select-at-least-one-time-slice':
      'Please select at least one time slice',
    'please-select-please-select-at-least-one-peak':
      'Please select at least one peak',
    'the-following-restaurants-are-not-included':
      'The following restaurants are not included in this report because they are not included in your user profile.',
    'if-you-are-logged-in-with-eid':
      'If you are logged in with an eID, your restaurant list must be corrected in GAM. Once the restaurants are added, you will need to log out and back in for QSRSoft to retrieve the updated restaurant list. GAM changes may take as long as 48 hours to propagate to our system.',
    'if-you-are-logged-in-with-email':
      'If you are logged in with an email, escalate this to your System Admin so they can add the restaurants to your profile. You will need to refresh your browser after the Sys Admin completes the change.',

    // Loading / retry
    retrying: 'Retrying...',

    // Grid container
    'expand-rows': 'Expand Rows',

    // Chart container
    overlap: 'Overlap',
    showChart: 'Show chart',
    hideChart: 'Hide chart',
    // Own key: 'timeslices' resolves to other text app-wide (#6335)
    'timeslice-dropdown-timeslices': 'Timeslices',
    // Own key: 'first-name' resolves to other text app-wide (#6335)
    'minor-exceptions-first-name': 'First Name',
    // Own key: 'timeslice' resolves to other text app-wide (#6335)
    'custom-incentive-timeslice': 'Timeslice',
    // McDonald's dashboard tabs (oldDashboard/mcd/main.vue)
    'transaction-analytics': 'Advanced Analytics',
  },
  es: {
    // Migrated from <i18n> SFC blocks (#6500)
    'buOverview': 'Vista del Operador',
    'quick-sight-insights': 'Inteligencia Empresarial',
    'offer': 'Detalles de la Oferta de BU',
    'buWatchlistDetails': 'Detalles del Artículo del Menú de BU',
    'buWatchlistDetailsWithSummary': 'Detalles del Artículo del Menú de BU con Resumen',
    'buWatchlistDetailsHeader': 'Detalles del Artículo del Menú de la Unidad de Negocio',
    'buWatchlistDetailsSubtitle': 'Seleccione hasta cinco listas de seguimiento por unidad de negocio para mostrar a los O/Os en la sección TA del panel de control de QSRSoft Home. No habrá resumen ni total entre las listas de seguimiento.',
    'buWatchlistDetailsWithSummaryHeader': 'Artículos del Menú de la Agencia con Resumen entre Listas de Seguimiento',
    'buWatchlistDetailsWithSummarySubtitle': 'Seleccione hasta cinco listas de seguimiento por unidad de negocio para mostrar a los O/Os en la sección TA del panel de control de QSRSoft Home. El widget mostrará cada lista de seguimiento más un resumen entre las listas de seguimiento. Todos los artículos de cada lista de seguimiento deben formar parte de la misma plataforma, por ejemplo, Bebidas de Verano o Ofertas de Comidas.',
    'nationalAAIncentiveBuilder': 'Constructor de Incentivos AA Nacional',
    'nationalMenuItemDetails': 'Detalles',
    'nationalMenuItemDetailsWithSummary': 'Detalles con Resumen',
    'nationalMenuItemDetailsHeader': 'Detalles del Artículo del Menú Nacional',
    'nationalMenuItemDetailsSubtitle': 'Seleccione hasta cinco listas de seguimiento por categoría para mostrar a los O/Os en la sección AA del panel de control de QSRSoft Home. No habrá resumen ni total entre las listas de seguimiento.',
    'nationalMenuItemDetailsWithSummaryHeader': 'Artículos del Menú Nacional con Resumen entre Listas de Seguimiento',
    'nationalMenuItemDetailsWithSummarySubtitle': 'Seleccione hasta cinco listas de seguimiento por categoría para mostrar a los O/Os en la sección AA del panel de control de QSRSoft Home. El widget mostrará cada lista de seguimiento más un resumen entre las listas de seguimiento. Todos los artículos de cada lista de seguimiento deben formar parte de la misma plataforma, por ejemplo, Bebidas de Verano o Ofertas de Comidas.',
    'nationalMenuItemDetailsBevs': 'Detalles (Bebidas)',
    'nationalMenuItemDetailsBevsHeader': 'Detalles del Artículo del Menú Nacional (Bebidas)',
    'nationalMenuItemDetailsBevsSubtitle': 'Seleccione hasta cinco listas de seguimiento por categoría para mostrar a los O/Os en la sección AA del panel de control de QSRSoft Home. No habrá resumen ni total entre las listas de seguimiento.',
    'nationalMenuItemDetailsBevsWithSummary': 'Detalles (Bebidas) con Resumen',
    'nationalMenuItemDetailsBevsWithSummaryHeader': 'Artículos del Menú Nacional (Bebidas) con Resumen entre Listas de Seguimiento',
    'nationalMenuItemDetailsBevsWithSummarySubtitle': 'Seleccione hasta cinco listas de seguimiento por categoría para mostrar a los O/Os en la sección AA del panel de control de QSRSoft Home. El widget mostrará cada lista de seguimiento más un resumen entre las listas de seguimiento. Todos los artículos de cada lista de seguimiento deben formar parte de la misma plataforma.',
    // Migrated from <i18n> SFC blocks (#6500)
    'Sunday': 'Domingo',
    'Monday': 'Lunes',
    'Tuesday': 'Martes',
    'Wednesday': 'Miércoles',
    'Thursday': 'Jueves',
    'Friday': 'Viernes',
    'Saturday': 'Sábado',
    'day-of-week-days': 'días',
    'day-of-week-all': 'Todos',
    'day-of-week-v2-days': 'días',
    'day-of-week-v2-all': 'Todos',
    'loading-range': 'Cargando {range}',
    'suspicious-activity-insight-data-failed-to-load': 'Los datos no se cargaron. Por favor, inténtelo de nuevo o contacte a soporte si el problema persiste.',
    'suspicious-activity-insight-more-info': 'Más Información',
    'suspicious-activity-insight-please-subscribe': 'Por Favor Suscríbase',
    'suspicious-activity-insight-learn-more': 'Aprende Más',
    'security-drill-down-security-drill-down': 'Análisis de Seguridad',
    'hello-world': 'Cálculo de comida sobre base basado en uso de PMIX y puede ser ligeramente diferente del cálculo de Efectivo e Inventario',
    'drivers-add-up': 'PMIX, Precios y Costo de Materia Prima suman el cambio en el % de Comida Base respecto al período de comparación.',
    'drivers': 'Qué lo movió',
    'fob-change': 'Comida Base vs Mes Ant',
    'vs-pr-mth': 'vs Mes Ant',
    'adds-up-to': 'suma {value}',
    'colour-key': 'Verde baja el costo de comida, rojo lo sube.',
    'raw-cost': 'Costo Materia Prima',
    'pricing': 'Precios',
    'pmix': 'PMIX',
    'rbd-events': 'RBDs',
    'rbd-tooltip': 'Los RBD pueden sesgar la información de Product Mix y los datos de costo de alimentos presentados.',
    'rbd-store-line': 'Tienda {store}: {count} día(s) con RBD',
    'base-food-decomp-widget-base-food-decomposition': 'Análisis de Comida Base',
    'base-food-decomp-widget-fob': 'Comida Base',
    'base-food-decomp-widget-disclaimer': 'Analizar qué impulsa el cambio en la comida base es una parte importante para entender el rendimiento de los eventos, pero no es toda la historia. Los cambios en promociones y descuentos, así como el crecimiento de ventas y GC a 2 años, también son necesarios para un panorama analítico completo.',
    'base-food-decomp-widget-business-units-not-signed-up': 'Una o más de sus Unidades de Negocio no se han inscrito en Analítica Avanzada. Las ubicaciones de estas Unidades de Negocio no se incluirán en el informe. Comuníquese con su líder de BU para más información.',
    'day-of-week-v3-days': 'días',
    'day-of-week-v3-all': 'Todos',
    'role-defaults': 'Valores Predeterminados del Rol',
    'enterprise-settings': 'Configuración de la Empresa*',
    'enterprise-help': 'Desmarca lo que esta empresa no usa. Los elementos desmarcados se ocultan en todos lados y no pueden ser habilitados por los valores predeterminados del rol.',
    'select-role': 'Seleccionar Rol',
    'select-role-prompt': 'Selecciona un rol para configurar sus valores predeterminados.',
    'allow-access': 'Permitir Acceso',
    'edit-thresholds': 'Editar umbrales',
    'edit-card-color': 'Editar color de tarjeta',
    'edit-pmix-products': 'Editar productos de PMIX',
    'pmix-role-products': 'Productos de PMIX (Predet. de rol)',
    'rule': 'Regla',
    'higher-is-better': 'Más alto es mejor',
    'lower-is-better': 'Más bajo es mejor',
    'restore-default': 'Restaurar Predeterminado',
    'switch-role-title': '¿Cambiar de rol?',
    'switch-role-body': 'Cambiar de rol descartará los cambios actuales. ¿Está seguro de que desea continuar?',
    'unsaved-changes': 'Cambios sin guardar',
    'unsaved-changes-body': 'Has aplicado cambios que no se han guardado. ¿Descartarlos?',
    'keep-editing': 'Seguir editando',
    'save-success': 'Configuración de la empresa guardada',
    'save-failed': 'Error al guardar la configuración de la empresa',
    'copy-to-roles': 'Copiar a otros roles',
    'copy-to-roles-body': 'Copia la configuración que se muestra para {role} (incluidos los cambios sin guardar) a los roles seleccionados. Sus valores predeterminados actuales serán reemplazados.',
    'copy': 'Copiar',
    'copy-success': 'Configuración copiada a {count} rol(es)',
    'copy-failed': 'Error al copiar la configuración a: {roles}',
    'dashboard-settings-select-all': 'Seleccionar todo',
    'dashboard-settings-done': 'Listo',
    'dashboard-settings-preview': 'Vista previa',
    // Migrated from <i18n> SFC blocks (#6500)
    'You currently have': 'Actualmente tienes',
    'items out in the last': 'artículos que salieron en el último',
    'for': 'para',
    'product-outage-insight-item': 'articulo',
    'oepe-w/o-park': 'OEPE sin Parqueo',
    'dt-pulled-fwrd-pct': 'DT % Adelantados',
    'snapshot-service-kvs-time-per-gc': 'KVS Tiempo / GC',
    'snapshot-service-kvs-items-per-gc': 'Artículos KVS / GC',
    'snapshot-service-dt-pulled-forward-pct': 'DT % Adelantados',
    // Migrated from <i18n> SFC blocks (#6500)
    'custom-options-end-time': 'Hora de Finalización',
    'custom-options-open': 'Abrir',
    '12to4': '12 a 4',
    '4to12': '4 a 12',
    'timeslice-dropdown-overnight': 'Durante la noche',
    'breakfast-desc': 'Ventas de Desayuno en hoja de caja',
    'total-desc': 'Todas las ventas',
    'drive-thru-desc': 'Ventas de AutoMac en hoja de caja',
    'in-store-desc': 'Incluye Mostrador, Entrega y Kiosco; todo lo que no es AutoMac',
    'kiosk-desc': 'Ventas originadas en Kiosco, sin importar dónde se pagó',
    'mobile-order-and-pay-desc': 'Ventas originadas en la app digital sin importar dónde se pagó',
    'delivery-desc': 'Total de entregas 3PO y entregas GMA',
    'sales-group-selector-drive-thru': 'AutoMac',
    'sales-group-selector-all': 'Todos',
    'sales-group-selector-in-store': 'En Tienda',
    'sales-group-selector-kiosk': 'Kiosco',
    'sales-group-selector-v2-drive-thru': 'AutoMac',
    'sales-group-selector-v2-all': 'Todos',
    'sales-group-selector-v2-in-store': 'En Tienda',
    'sales-group-selector-v2-kiosk': 'Kiosco',
    'custom-options-v2-end-time': 'Hora de Finalización',
    'custom-options-v2-open': 'Abrir',
    // Migrated from <i18n> SFC blocks (#6500)
    'header': 'Cada restaurante tiene un subconjunto diferente de EVM que califican para los dólares de asociación de McDonald\'s.',
    'part-1': 'El EVM debe haber recibido una reducción de precio recomendada por Deloitte a fines de agosto (en el canal respectivo).',
    'part-2': 'Las unidades en tienda para los artículos del menú de $5/$8 de septiembre, noviembre y enero no están incluidas en la cantidad de asociación durante la ventana de promoción.',
    'part-3': 'Los EVM vendidos a un precio superior a la recomendación de Deloitte pueden no ser elegibles para la asociación',
    'part-4': 'Big Mac / SME está entre el 8 de septiembre y el 5 de octubre',
    'part-5': '10 Pc Nug / SEC McGrd está entre el 4 y el 30 de noviembre',
    'part-6': '2 Wrps / Sau Egg McMuff está entre el 6 de enero y el 2 de febrero',
    'part-7': 'SME / QPC está entre el 2 y el 29 de noviembre de 2026',
    'operator-view': 'Comparación de BU',
    'overview-container-buSummaryContainersDescription': 'La sección BU Comp permite a los operadores comparar su organización y cada restaurante con el promedio del BU. Estos contenedores permiten ver datos a nivel de BU en el mismo formato.',
    // Migrated from <i18n> SFC blocks (#6500)
    'sectioned-dashboard-grid-no-data': 'Sin Datos',
    'sectioned-dashboard-grid-labor': 'Labor',
    'rank-of-the-store-compared-to-the-whole-bu': 'Clasificación de la tienda en comparación con todo el BU',
    'units-1k-rod-gc': 'Unidades/1K ROD GC',
    'number-of-menu-items-that-are-sold-related': 'Número de artículos del menú que se venden relacionados con las Tiras McCrispy durante el Rest of Day (ROD) por cada mil unidades.',
    'mc-crispy-strips-widget-rank': 'Clasificación',
    'new-beverage-units': 'New Beverage Units',
    'new-beverage-watch-list': 'Lista de Seguimiento Beverage 2026',
    'new-beverage-widget-new-beverage': 'Specialty Drinks',
    'new-beverage-widget-units-1k-gc': 'Unidades/1K GC',
    'new-beverage-widget-number-of-menu-items-that-are-sold-related': 'Número de artículos del menú vendidos relacionados con AST Beverage por cada mil unidades.',
    'new-beverage-widget-rank': 'Clasificación',
    'rmhc-gc-percentage': 'Porcentaje de RMHC GC',
    'rmhc-gc-total-gc': 'RMHC GC / Total GC',
    'add-reward-message-link-text': 'Recompensa a los empleados',
    'add-reward-message-end': 'que alcanzan los objetivos de Redondeo',
    'rmhc-incentive-widget-premium-upgrade-message-start': 'Actualiza a Premium: ',
    'rmhc-incentive-widget-premium-upgrade-message-end': 'Mejora Redondeo con Recompensas',
    'rmhc-incentive-widget-rank': 'Clasificación',
    'created-date-header': 'Fecha de creación',
    'incident-date-header': 'Fecha del incidente',
    'case-number-header': 'Número de caso',
    'type-header': 'Tipo',
    'customer-name-header': 'Nombre del cliente',
    'recovery-required-header': 'Recuperación requerida',
    'status-header': 'Estado',
    'resolution-header': 'Resolución',
    'customer-recovery-grid-close': 'cerrar',
    'customer-recovery-grid-yes': 'sí',
    'customer-recovery-grid-no': 'no',
    'total-number-header': 'Total/#',
    'issue-header': 'Problema',
    'equipment-header': 'Equipo',
    'reported-header': 'Reportado',
    'priority-header': 'Prioridad',
    'assignee-header': 'Asignado a',
    'status-reported': 'Reportado',
    'status-accepted': 'Aceptado',
    'status-in-progress': 'En Progreso',
    'status-pending': 'Pendiente',
    'status-resolved': 'Resuelto',
    'priority-low': 'Bajo',
    'priority-medium': 'Medio',
    'priority-high': 'Alto',
    'tickets-grid-close': 'cerrar',
    'trailing-365': 'Últimos 365 Días',
    'trailing-7': 'Últimos 7 Días',
    'last-7': 'Últimos 7 Días',
    'last-30': 'Últimos 30 Días',
    'trailing-30': 'Últimos 30 Días',
    't365': 'Últ. 365',
    'snapshot-container-last-week': 'Semana pasada',
    'snapshot-container-point-of-order': 'Punto de Servicio',
    'snapshot-container-last-month': 'Mes pasado',
    'snapshot-container-wtd': 'SDH',
    'snapshot-container-mtd': 'MDH',
    'snapshot-container-ytd': 'ADH',
    'mop-w-offers': 'MOP con Ofertas',
    'mop-wo-offers': 'MOP sin Ofertas',
    'digital-app-digital-app': 'Aplicación Digital',
    'digital-app-average-check': 'Ticket Promedio',
    'digital-app-trading': 'Comercio',
    'digital-app-loyalty-self-id': 'Identificación Propia de Lealtad',
    'pct-sales': '% Ventas',
    'kiosk-w-offer': 'Kiosco con Oferta',
    'kiosk-w-o-offer': 'Kiosco sin Oferta',
    'mcDelivery-w-offer': 'McDelivery con Oferta',
    'mcDelivery-w-o-offer': 'McDelivery sin Oferta',
    'gma-delivery-w-offer': 'GMA Delivery con Oferta',
    'gma-delivery-w-o-offer': 'GMA Delivery sin Oferta',
    'digital-order-points-digital-order-pts': 'Pts Pedido Digital',
    'digital-order-points-average-check': 'Ticket Promedio',
    'digital-order-points-trading': 'Comercio',
    'digital-order-points-kiosk': 'Kiosco',
    'below-threshold': 'por debajo del umbral objetivo inferior',
    'between-threshold': 'entre los umbrales objetivo',
    'above-threshold': 'por encima del umbral objetivo superior',
    'any-order-containing': 'Cualquier pedido que contenga un componente de App Digital y/o Punto de Pedido Digital',
    'snapshot-digital-usage-red': 'rojo',
    'snapshot-digital-usage-yellow': 'amarillo',
    'snapshot-digital-usage-green': 'verde',
    'snapshot-digital-usage-no-data': 'Sin Datos',
    'snapshot-digital-usage-digital-usage': 'Uso Digital',
    'snapshot-digital-usage-error-loading-data': 'Error al Cargar los Datos',
    'agency': 'Agencia',
    'local-offer': 'Oferta Local',
    'national-offer': 'Oferta Nacional',
    'non-offer': 'Sin Oferta',
    'bu-watchlist-no-data': 'Sin datos - Ya sea',
    'bu-watchlist-select-a-bigger-date-range': 'seleccione un rango de fechas más amplio donde ocurrieron estos artículos de menú',
    'bu-watchlist-ask-your-agency': 'pida a su agencia que seleccione hasta dos artículos de menú para mostrar aquí',
    'bu-watchlist-meal-pct-of-gc': '% de GC',
    'bu-watchlist-avg-check': 'Cheque Promedio',
    'prior': '8/10/2025 - 9/6/2025',
    'extra-value-meal-comp-type-selector-trading': 'El año anterior (comercial)',
    'five-and-eight-units-served-rd': 'Unidades Servidas/R/D',
    'five-and-eight-avg-check': 'Cheque Promedio',
    'five-and-eight-september': 'Big Mac/SME',
    'five-and-eight-november': '10 Pc Nug/SEC McGrd',
    'five-and-eight-january': '2 Wrps/Sau Egg McMuff',
    'five-and-eight-november-2026': 'QPC/SME',
    'five-and-eight-meal-pct-of-gc': '% GC',
    'five-and-eight-business-units-not-signed-up': 'Una o más de tus Unidades de Negocio no están inscritas en Analítica Avanzada. Sus ubicaciones no se incluirán en el informe. Contacta al líder de tu BU para más información.',
    'for-more-detail': 'Para más detalle, por favor visite el',
    'pmix-report': 'Reporte Pmix',
    'national-watchlist-no-data': 'Sin datos - Ya sea',
    'national-watchlist-select-a-bigger-date-range': 'seleccione un rango de fechas más amplio donde ocurrieron estos artículos de menú',
    'national-watchlist-ask-your-agency': 'pida a su agencia que seleccione hasta dos artículos de menú para mostrar aquí',
    'national-watchlist-meal-pct-of-gc': '% de GC',
    'national-watchlist-avg-check': 'Cheque Promedio',
    'national-watchlist-national': 'Nacional',
    'national-watchlist-business-units-not-signed-up': 'Una o más de tus Unidades de Negocio no están inscritas en Analítica Avanzada. Sus ubicaciones no se incluirán en el informe. Contacta al líder de tu BU para más información.',
    'scorecard-ranking': 'Clasificación del Cuadro de Mando',
    'show-goal-diff': 'Mostrar Diferencia de Meta',
    'pivot': 'Pivotar',
    'rank-settings': 'Configuración de Clasificación',
    'score-card-rank-loc': 'Ubic',
    'score-card-rank-patch': 'Grupo',
    'card-settings': 'Configuración de Tarjeta',
    'rows': 'Filas',
    'widget-settings-thresholds': 'Umbrales',
    'watchlist-modal-local': 'Oferta local',
    'watchlist-modal-watchlist': 'Lista de seguimiento',
    'watchlist-modal-filter': 'Filtrar',
    // Migrated from <i18n> SFC blocks (#6500)
    'share': 'Compartir',
    'preparing_report': 'Preparando su reporte...',
    'share_as': 'Compartir como',
    'image': 'Imagen',
    'share-table': 'Tabla',
    'All Filters': 'Todos los Filtros',
    'Clear All': 'Limpiar Todo',
    'Case Number': 'Número de Caso',
    'Search Case Number': 'Buscar Número de Caso',
    'Customer Name': 'Nombre del Cliente',
    'Search Customer Name': 'Buscar Nombre del Cliente',
    'Status': 'Estado',
    'Recovery Required': 'Recuperación Requerida',
    'Global Performance Matrix': 'Matriz de Desempeño Global',
    'Type': 'Tipo',
    'ALL FILTERS {count}/{total}': 'TODOS LOS FILTROS {count}/{total}',
    'voice-analysis': 'Análisis de Voz',
    '1-800-analysis': 'Análisis 1-800',
    'tab-1-800': '1-800',
    'tab-voice': 'VOZ',
    'setup-1800-instructions': 'Para configurar 1-800, siga las instrucciones',
    'payload-too-large': 'La solicitud de datos es demasiado grande. Reduzca su rango de fechas o aplique filtros para ver los datos.',
    'deleted': 'Eliminado',
    'delete-selected': 'Eliminar Seleccionados ({count})',
    'close-selected': 'Cerrar Seleccionados ({count})',
    'feedback-type-complaint': 'Queja',
    'feedback-type-praise': 'Elogio',
    'feedback-type-inquiry': 'Consulta',
    'complaints-per-100k-gc': 'Quejas por 100K GC',
    'praise-per-100k-gc': 'Elogios por 100K GC',
    'inquiries-per-100k-gc': 'Consultas por 100K GC',
    'col-quick-close': 'Cierre Rápido',
    'col-deleted-by': 'Eliminado Por',
    'unknown': 'Desconocido',
    'col-processed-date': 'Fecha de Procesamiento',
    'col-created-date': 'Fecha de Creación',
    'col-incident-date': 'Fecha del Incidente',
    'col-case-number': 'Número de Caso',
    'col-type': 'Tipo',
    'col-customer-name': 'Nombre del Cliente',
    'col-recovery-required': 'Recuperación Requerida',
    'col-status': 'Estado',
    'col-resolution': 'Resolución',
    'col-actions': 'Acciones',
    'col-created-from': 'Creado Desde',
    'col-case-origin': 'Origen del Caso',
    'col-issue-category': 'Categoría del Problema',
    'col-issue-reason': 'Motivo del Problema',
    'col-issue-subcode': 'Subcódigo del Problema',
    'col-global-performance-matrix': 'Matriz de Desempeño Global',
    'col-recovery-next-steps': 'Próximos Pasos de Recuperación',
    'col-recovery-form': 'Formulario de Recuperación',
    'col-customer-email': 'Correo Electrónico del Cliente',
    'col-customer-phone': 'Teléfono del Cliente',
    'col-location-address': 'Dirección de Ubicación',
    'col-order-point': 'Punto de Pedido',
    'col-pickup-point': 'Punto de Recogida',
    'col-letter-sent': 'Carta Enviada',
    'col-customer-comments': 'Comentarios del Cliente',
    'add-shift': 'Agregar Turno',
    'job-title': 'Cargo',
    'shift-times': 'Horario del Turno',
    'shift-length': 'Duración del Turno',
    'breaks': 'Descansos',
    'add-break': 'Agregar descanso',
    'break-tooltip': 'Los descansos se programan para evitar horas pico de comida y conflictos con los descansos de otros empleados.',
    'meal-break': 'Descanso para Comer',
    'rest-break': 'Descanso',
    'scheduling-conflicts': 'Notas de Programación',
    'add-to-schedule': 'Agregar al Horario',
    'add-shift-dialog-this-week': 'esta semana',
    'add-shift-dialog-hours-this-week': 'Horas Esta Semana',
    'available-employees': 'Empleados No Programados',
    'tooltip-description': 'Empleados no programados hoy. Los números indican las horas de esta semana. Haga clic en un empleado para ver su información de contacto. Las filas resaltadas indican menores.',
    'available-employees-error-loading': 'Error al cargar datos de empleados',
    'available-employees-no-data-available': 'No hay empleados disponibles',
    'swap-shift': 'Intercambiar Turno',
    'current-employee': 'Empleado Actual',
    'scheduled-shift': 'Turno Programado',
    'not-working-today': 'No Trabajando Hoy',
    'working-description': 'Seleccione un empleado que ya está trabajando hoy. Ambos empleados intercambiarán sus horarios de trabajo.',
    'not-working-description': 'Seleccione un empleado disponible para tomar este turno. El nuevo empleado reemplazará al original en este turno.',
    'select-employee': 'Seleccionar Empleado',
    'their-shift': 'Su Turno',
    'will-work': 'trabajará',
    'will-take-over': 'tomará el turno',
    'additional-notes': 'Notas Adicionales',
    'notes-placeholder': 'Notas opcionales sobre este intercambio...',
    'confirm-swap': 'Confirmar Intercambio',
    'swap-shift-dialog-hours-this-week': 'Horas Esta Semana',
    // Migrated from <i18n> SFC blocks (#6500)
    'dashboard-settings-dashboard-settings': 'Configuración del Panel de Control',
    'customize-dashboard-containers': 'Personalizar Contenedores del Tablero',
    'unable-to-save-dashboard-settings': 'No se pudo guardar la configuración del tablero.',
    'unable-to-reset-dashboard-settings-to-default': 'No se pudo reiniciar la configuración del tablero a los valores predeterminados.',
    'shift-dashboard': 'Tablero de Turno',
    'disclaimer': 'Los resultados de ventas de FO se muestran después del lanzamiento de ventas trimestrales de McDonalds.',
    'roiOnePager': 'Retorno de Inversión',
    'clickToSetup': 'Haga clic para ver el informe de ROI de consultoría',
    'clickToViewMore': 'Haga clic aquí para ver más',
    'qsrsoftConsulting': 'Consultoría QSRsoft',
    'yourOrganization': 'Su Organización',
    'heroTitle': 'Construiste Algo Valioso.',
    'heroSubtitle': 'Ahora protejámoslo — y hagamos crecer lo que construiste.',
    'heroAmountText': '{amount} está saliendo de su cuenta bancaria hoy',
    'heroShowYou': 'Le mostraremos',
    'exactlyWhere': 'exactamente dónde',
    'andHowTo': 'y cómo',
    'takeItBack': 'recuperarlo',
    'theProblem': 'El Problema',
    'theGuide': 'LA GUÍA',
    'guideSubtitle': 'Casi 100 años dentro del sistema.',
    'excessiveCrewLabor': 'Exceso de Mano de Obra',
    'laborText': 'Oportunidad de flujo de caja de 6 meses — {amount} por tienda erosionando silenciosamente su rentabilidad.',
    'foodOpportunity': 'Oportunidad de Alimentos',
    'foodText': 'Comparado contra una norma de operadores de primera clase - {amount} por tienda de oportunidad.',
    'gcOpportunity': 'Oportunidad de Clientes',
    'guestCountText': 'Brecha de GC comparables con la BU',
    'theTransformation': 'La Transformación',
    'transformationTitle': 'La Transformación — Un retorno de {return}. Un {roi}%+ de ROI.',
    'basedOnCapturing': 'Basado en capturar solo el 25% de la oportunidad',
    'thePlan': 'EL PLAN — 3 PASOS, 90 DÍAS',
    'diagnose': 'Diagnosticar',
    'diagnoseText': 'Conectar con sus datos de QSRSoft. Construir una imagen precisa, tienda por tienda, categoría por semana, de exactamente dónde se está fugando el efectivo.',
    'target': 'Enfocar',
    'targetText': 'Clasificar las oportunidades de mayor valor — mano de obra, horas extras, horarios de operación — y construir un plan priorizado CON sus gerentes.',
    'capture': 'Capturar',
    'captureText': 'Guiar a su equipo en la ejecución. Rastrear el valor capturado en tiempo real. Cerrar la brecha entre donde está y donde quiere estar.',
    'whatOperatorsSay': 'LO QUE DICEN LOS OPERADORES',
    'testimonial1': 'Redujimos los objetivos de mano de obra un 4% en los últimos doce meses. Crecimos el flujo de caja en más de $1 millón.',
    'testimonial2': 'Pasamos de recopilar datos a mejoras significativas. Los gerentes más conscientes, la administración más eficiente.',
    'testimonial3': 'Ahorramos cientos de miles en mano de obra — mientras seguimos creciendo en clientes y superando a nuestra unidad de negocio.',
    'franchisee': 'Franquiciatario de McDonald\'s',
    'nextStepSimple': 'Su próximo paso es simple.',
    'letsTalk': 'Hablemos',
    'see-all-peers': 'Ver Todos los Colegas',
    'this-setting-will': 'Esta configuración permitirá a los usuarios ver a los compañeros que no dependen directamente de su supervisor.',
    'only': 'solo',
    'affect the peers widget': 'afecta al widget de pares',
    'show-peers-across-hierarchy': 'Mostrar Colegas en Toda la Jerarquía',
    'hierarchy': 'Jerarquía',
    'hierarchy-container-locations': 'Ubicaciones',
    'hierarchy-grid-no-data': 'Sin Datos',
    'peers': 'Pares',
    'avg-sales': 'Ventas Promedio',
    'avg-net-sales': 'Ventas Netas Promedio',
    'oepe-wo-park': 'OEPE Sin Parqueo',
    'dt-total-time': 'DT Tiempo Total',
    'kvs-items-per-gc': 'KVS Artículos / GC',
    'healthy-use': 'Uso Saludable',
    'healthy-usage': 'Uso Saludable',
    'kvs-time-per-item': 'KVS Tiempo / Artículo',
    'peers-sales-kvs-time-per-gc': 'KVS Tiempo / GC',
    'peers-sales-dt-sales': 'DT Ventas',
    'peers-sales-dt-average-check': 'DT Cheque Promedio',
    'incentive-grid-no-data': 'Sin Datos',
    'incentive-grid-location': 'Ubicación',
    'incentive-grid-trend': 'Tendencia',
    'select-metric': 'Seleccionar Métrica',
    'array-cannot-be-empty': 'La matriz no puede estar vacía',
    'incentive-trend-trend': 'Tendencia',
    '3d-guest-count-club': 'Club de Conteo de Invitados 3D',
    'average-daily-guest-counts': 'Conteos Diarios Promedio de Invitados',
    'gma-kiosk': 'GMA & Kiosco',
    'gmad-3po': 'GMAD & 3PO',
    'widget-drive-thru': 'Auto Servicio',
    'rank-out-of': 'Clasificación sobre',
    'restaurants': 'Restaurantes',
    'practice-week': 'Práctica Sem',
    'overall-rank': 'Clasificación General',
    'created-by': 'Creado Por',
    'custom-incentive-week': 'Semana',
    'incentive-trend': 'Tendencia del Incentivo',
    'bu': 'Unidad de Negocio',
    'the-ranking-is-the-same-as': 'El ranking es el mismo que el reciente Big Bets Incentive. Los datos son la suma de todos los picos. Las tiendas deben estar abiertas durante al menos un año y no deben haber estado cerradas por más de 1 día tanto este año como el año pasado.',
    'the-overall-rank-is-based-on': 'El rango general se basa en el cálculo de puntos de McD. El rango se basa en una puntuación acumulativa desde el 07/07/2024 hasta ayer.',
    'gc-day-plus-minus': 'GC/Día +/-',
    'guest-count-per-store-per-day': 'Recuento de invitados por tienda por día en comparación con el año pasado',
    'digital-gc-day-plus-minus': 'GC Digital/Día +/-',
    'digital-order-points-gc-r-d': 'Puntos de pedido digital GC/R/D en comparación con el año pasado',
    'oepe-w-o-park': 'OEPE S/O Parqueo',
    'order-end-to-present-end': 'Tiempo desde que el cliente termina de ordenar hasta que se presenta el pedido, sin incluir pedidos estacionados.',
    'oepe-w-o-park-plus-minus': 'OEPE S/O Parqueo +/-',
    'oepe-w-o-parked-compared-to-last-year': 'OEPE S/O Parqueo comparado con el año pasado',
    'quality-sat-percent': '% Satisfacción de Calidad',
    'overall-quality-satisfaction-score': 'Puntuación General de Satisfacción de Calidad',
    'default-incentives-widget-rank-out-of': 'Clasificación de',
    'incentives': 'Incentivos',
    'warm-up-week-1': 'Preparación Sem 1',
    'warm-up-week-2': 'Preparación Sem 2',
    'week-1': 'Semana 1',
    'week-2': 'Semana 2',
    'week-3': 'Semana 3',
    'week-4': 'Semana 4',
    'week-5': 'Semana 5',
    'week-6': 'Semana 6',
    'fall-incentives': 'GOAL-den Momentum',
    'fifa-club-world-cup': 'Mundial de la FIFA 26',
    'overall-rank-based-point-calculation': 'El rango general se basa en el cálculo de puntos de McD. El rango se determina a partir de una puntuación acumulada desde el 9/21/2025 hasta ayer',
    'vs-last-week': '‘Vs Semana Pasada’ muestra cuánto ha cambiado tu rango general al cierre del negocio el sábado más reciente',
    'mcd-corporation-calc': 'McDonald’s Corporation calculará el ranking final, que se utilizará para el reconocimiento final',
    'fifa-upt-definition': '\'FIFA UPT +/-\' es la cantidad de artículos del menú FIFA vendidos por cada 1,000 GC, en comparación con el año anterior',
    'upsized-definition': '\'% Agrandado\' es el porcentaje de unidades de Big Mac y 10 Pc que se agrandaron a tamaño Grande del total de unidades de Combos FIFA durante el ROD',
    'rmhc-bonus': 'RMHC es una métrica de bonificación que solo otorga puntos en las semanas 1-3',
    'oepe-r2p': '* La métrica \'OEPE\' utiliza OEPE sin parques. Los restaurantes que no son de DT usan R2P',
    'fall-incentives-widget-rank-out-of': 'Rango de entre',
    'round-up': 'Redondear',
    'pos': 'POS',
    'register-type': 'Área de POS',
    'round-up-qty-pct': 'Porcentaje de Cantidad Redondeada',
    'round-up-by-pos-area-location': 'Ubicación',
    'round-up-by-pos-area-grid-no-data': 'Sin Datos',
    'rank-up-pos': 'Redondear POS/Empleado',
    'warm-up-week-3': 'Preparación Sem 3',
    'warm-up-week-4': 'Preparación Sem 4',
    'summer-splash': 'Chapuzón de Verano',
    'summer-incentives-widget-overall-rank-based-point-calculation': 'El rango general se basa en el cálculo de puntos de McD. El rango se determina a partir de una puntuación acumulada desde el 6/29/2025 hasta ayer',
    'summer-incentives-widget-oepe-r2p': '* La métrica \'OEPE o R2P\' utiliza OEPE sin parqueo a menos que el restaurante no tenga autoservicio; en ese caso, se usa R2P',
    'summer-incentives-widget-rod': 'Las transacciones del Resto del Día (ROD) excluyen el desayuno',
    'summer-incentives-widget-rank-out-of': 'Rango de entre',
    'business-dates': 'Fecha(s) comercial(es)',
    'number-of-BDC-missing': '# de BDC Desaparecido(s)',
    'bdc-grid-more-info': 'MÁS INFORMACIÓN',
    'bdc-grid-location': 'Ubicación',
    'missing-at-least': 'que le faltan al menos',
    'one-business-day-close': 'cierre en un día hábil',
    'bdc-insight-store': 'tienda',
    'item-num': 'Núm Artículo',
    'desc': 'Descripción',
    'used-diff': 'Dif Usado',
    'dollar-diff': '$ Dif',
    'pct-net-sales': '% de Ventas Netas',
    'stat-grid-title': 'Detalles de Pérdida de Inventario',
    'stat-grid-store': 'Ubicación',
    'most-recent-inventory': 'Inventario Más Reciente',
    'top-loss-items': 'Top 5 Artículos con Pérdida',
    'no-loss-items': 'No se encontraron artículos con pérdida',
    'raw-waste-amount': 'Desperdicio Crudo $',
    'raw-waste-qty': 'Cantidad Desperdicio Crudo',
    'comp-waste-amount': 'Desperdicio Completado $',
    'comp-waste-qty': 'Cantidad Desperdicio Completado',
    'missing-type': 'Tipo Faltante',
    'both': 'Ambos',
    'raw-only': 'Solo Crudo',
    'comp-only': 'Solo Completado',
    'waste-entry-grid-title': 'Detalles de Entrada de Desperdicio',
    'waste-entry-grid-store': 'Ubicación',
    'stores-missing-raw-waste': 'Tiendas Sin Desperdicio Crudo',
    'stores-missing-comp-waste': 'Tiendas Sin Desperdicio Completado',
    'stores-missing-raw-waste-entry': 'tiendas no han ingresado desperdicio crudo en los últimos 7 días',
    'stores-missing-comp-waste-entry': 'tiendas no han ingresado desperdicio completado en los últimos 7 días',
    'store-missing-raw-waste-entry': 'tienda no ha ingresado desperdicio crudo en los últimos 7 días',
    'store-missing-comp-waste-entry': 'tienda no ha ingresado desperdicio completado en los últimos 7 días',
    'day-threshold-settings-days': 'Días',
    'filter-message-open-late': '** Mostrando tiendas que han abierto tarde más del Límite de Días Perdidos ({daysThreshold}) días en las últimas 7 aperturas.',
    'filter-message-close-early': '** Mostrando tiendas que han cerrado temprano más del Límite de Días Perdidos ({daysThreshold}) días en los últimos 7 cierres.',
    'olce-grid-more-info': 'MÁS INFORMACIÓN',
    'olce-grid-minutes': 'minutos',
    'olce-grid-days-missed-limit': 'El Límite de Días Perdidos se puede ajustar en la configuración de Insights.',
    'olce-grid-filter-2': 'Filtro',
    'full-employee-name-header': 'Nombre',
    'crew-type-header': 'Tipo de tripulación',
    'termed-header': 'Terminado',
    'hire-date-header': 'Contratar',
    'termination-date-header': 'Terminación',
    'merged-terms-more-info-yes': 'sí',
    'merged-terms-more-info-no': 'no',
    'crew-termed-prefix': 'miembros del equipo en los primeros',
    'crew-termed-suffix': 'días desde la contratación.',
    'turnover-prefix': 'La rotación supera',
    'turnover-with': 'con',
    'turnover-suffix': 'miembros del equipo despedidos en 90 días.',
    'with-90-day-turnover-above-prefix': 'con una rotación de 90 días superior al',
    'people-terms-insight-no-30-day-terms': 'Sin bajas en los primeros 30 días.',
    'people-terms-insight-location-2': 'ubicación | ubicaciones',
    'ot-grid-title': 'OT Potencial',
    'this-metric-does-not-have-any-goals-set': 'Esta Métrica No Tiene Ningún Objetivo Establecido',
    'all-goals': 'Todas las Metas',
    'employees-over-threshold': 'empleados están en camino de trabajar más de {threshold} horas esta semana.',
    'ot-threshold-settings-hours': 'Horas',
    'prod-out-grid-all-outages': 'TODAS LAS INTERRUPCIONES',
    'prod-out-grid-menu-item-number': 'Número de Elemento del Menú',
    'prod-out-grid-active-product-outages': 'Interrupciones de Productos Activos',
    'prod-out-grid-more-info': 'Más Información',
    'watch-lists': 'Listas de Seguimiento',
    'equipment-tickets': 'Tickets de equipo',
    'open-seven-days': 'abierto > 7 días',
    'customer-recovery': 'Recuperación de clientes',
    'open-forty-eight-hours': 'abierto > 48 horas',
    'training-certificates': 'Certificados de entrenamiento',
    'to-expire': 'a expirar < 30 días',
    'simplethink-info-title': 'Actualmente no está suscrito a SimpleThink.',
    'simplethink-info-I': 'SimpleThink™ de QSRSoft es un asistente digital todo en uno para ayudarle a administrar su restaurante con más de los',
    'simplethink-info-II': 'correctos',
    'simplethink-info-III': 'controles. Experiencias personalizadas y lúdicas que fomentan, recompensan y promueven grandes comportamientos.',
    'simplethink-info-IV': '¡Optimice sus operaciones y potencie a su equipo!',
    'simplethink-info-modal-button': 'Más información',
    'tickets-open-seven-days': 'Tickets abiertos > 7 días',
    'certificates-to-expire': 'Certificados a expirar < 30 días',
    'tickets-open-report': 'Tickets abiertos > 7 días',
    'training-certificates-report': 'Certificados de capacitación a expirar < 30 días',
    'customer-recovery-report': 'Recuperación de clientes abiertos > 48 horas',
    'simple-think-insight-training': 'Capacitación',
    'location-header': 'Ubicación',
    'last-name-header': 'Apellido',
    'course-name-header': 'Nombre del Curso',
    'confirmation-id-header': 'ID de Confirmación',
    'issue-date-header': 'Fecha de Emisión',
    'expiration-date-header': 'Fecha de Expiración',
    'bdc-prefix': 'BDC -',
    'instore-events-insight-old-stores-with-alerts': '{count} tienda(s) con alertas activas',
    'instore-events-insight-old-stores-with-alerts-suffix': 'tienda(s) con alertas activas',
    'instore-events-insight-old-store': 'tienda',
    'instore-events-insight-old-one': 'un',
    'instore-events-insight-old-more-info': 'Más Información',
    'merged-alerts-more-info-business-dates': 'Fecha(s) de Negocio',
    'merged-alerts-more-info-number-of-BDC-missing': '# de BDC faltantes',
    'employees-with-more-than': 'Empleados con más de ',
    'employee-view-modified-punches': ' Marcadas Modificadas ',
    '%-of-modified-punches-in-last-7-days:': 'Porcentaje de marcas modificadas en los últimos 7 días:',
    'store-trend': 'tienda por encima de la tendencia de la tienda | tiendas por encima de la tendencia de la tienda',
    'org-trend': 'tienda por encima de la tendencia de la organización | tiendas por encima de la tendencia de la organización',
    'employee-threshold-part1': 'empleado tuvo más de | empleados tuvieron más de',
    'employee-threshold-part2': ' marcadas modificadas en el último período de pago',
    'mod-punches': 'Marcadas Modificadas',
    'modified-punches-tabs-store-trend': 'Tendencia de la Tienda',
    'modified-punches-tabs-org-trend': 'Tendencia de la Organización',
    'stores-above-org-trend-in-last-7-days': 'Tiendas Por Encima de la Tendencia de la Organización en los Ultimos 7 Días',
    'punches-threshold-settings-punches': 'Marcadas',
    'stores-above-store-trend-in-last-7-days': 'Tiendas Por Encima de la Tendencia de la Tienda en los Ultimos 7 Días',
    'rbd-completions': 'RBD Completados',
    'rbd-activations': 'Activaciones RBD',
    'merged-rbd-more-info-store': 'Ubicación',
    'merged-rbd-more-info-more-info': 'Más Información',
    'rbd-completed-grid-store': 'Ubicación',
    'rbd-completed-grid-more-info': 'Más Información',
    'store-has': 'tienda tiene',
    'stores-have': 'tiendas tienen',
    'or-more': 'o más',
    'error-try-again': 'ERROR - INTÉNTALO DE NUEVO',
    'rbd-completed-insight-rbd-completions': 'RBD completados en los últimos 30 días',
    'rbd-completions-label': 'RBD Completados',
    'rbd-completions-suffix': 'en los últimos 30 días.',
    'no-rbd-completions': 'Ninguna tienda supera el umbral de RBD completados.',
    'store-has-rbd': 'tienda tiene al menos un RBD activado en los últimos 30 días pero no completado.',
    'stores-have-rbd': 'tiendas tienen al menos un RBD activado en los últimos 30 días pero no completado.',
    'store-has-prefix': 'tienda tiene al menos un',
    'stores-have-prefix': 'tiendas tienen al menos un',
    'rbd-activated': 'RBD Activado',
    'activated-middle': 'en los últimos 30 días pero',
    'not-completed': 'no completado.',
    'no-rbd-activations': 'Ninguna tienda con activaciones RBD pendientes.',
    'rbd-insight-rbd-completed': 'Completado',
    'rbd-insight-rbd-activation': 'Activación',
    'rbd-insight-you-have': 'tiene',
    'today-beta': 'Hoy (Beta)',
    'snapshot-digital': 'Resumen Digital',
    'consulting': 'Consultoría',
    'bu-comp': 'BU Comp',
    'main-security-drill-down': 'Análisis de Seguridad',
    'main-file-manager': 'Biblioteca de Recursos',
    'main-transaction-analytics': 'Analítica Avanzada ',
    'over-ring': 'Sobre Registro',
    'red-before': 'Rojo Antes',
    'red-after': 'Rojo Después',
    'total-refund': 'Reembolso Total',
    'snapshot-cash-over-short': 'Faltante/Sobrante',
    'actual-labor-pct': '% Mano de Obra Real',
    'crew-labor-pct': '% Mano de Obra del Equipo',
    'punched-labor-pct': '% Mano de Obra Marcada',
    'avg-rate': 'Tarifa Promedio',
    'total-labor-pct': '% Mano de Obra Total',
    'ot-$': 'HE',
    'ot-hrs': 'Horas HE',
    'snapshot-link-container-point-of-order': 'Punto de Servicio',
    'avg-chk': 'Ticket promedio',
    'breakfast-gc': 'Clientes desayuno',
    'dt-avg-chk': 'Ticket DT promedio',
    'dt-gcs': 'Clientes DT',
    'dt-gcs-qty': 'Cantidad clientes DT',
    'gcs': 'Clientes',
    'prod-sales': 'Ventas producto',
    'gcs-qty': 'Cantidad clientes',
    'breakfast-gcs-qty': 'Cantidad clientes desayuno',
    'comparison': 'Comparación',
    'showing-comparison-to': 'Mostrando comparación con',
    'year-before-last': 'Año anterior al pasado',
    'snapshot-sales-avg-sales': 'Ventas promedio',
    'snapshot-sales-breakfast-sales': 'Ventas desayuno',
    'snapshot-sales-last-year': 'Año pasado',
    'day-parts': 'Partes del Día',
    'pmix-grid-item': 'Artículo',
    'setup-or-manage-watchlists': 'Configurar o Gestionar Watchlists',
    'Thru': 'Hasta',
    'date-options-for-pmix-widget-exclude': 'Las opciones de fecha para el widget PMIX excluyen ADH, UDM, Últ. 365, y cualquier rango personalizado mayor a 31 días.',
    'please-select-at-least-one': 'Por favor seleccione al menos un artículo del menú o lista de seguimiento para ver los datos de PMIX.',
    'digital-snapshot': 'Resumen Digital',
    'digital-hero-kpis': 'KPIs Héroe Digital',
    'digital-diagnostics': 'Diagnóstico Digital',
    'mop-fulfillment': 'Cumplimiento MOP',
    'digital-hero-kpis-2024': 'KPIs Héroe Digital 2024',
    'snapshot-digital-container-digital-usage': 'Uso Digital',
    'snapshot-digital-container-digital-order-pts': 'Pts Pedido Digital',
    'snapshot-digital-container-digital-app': 'App Digital',
    'linked-payment-cnt': 'Cnt Pagos Vinculados',
    'linked-payment-pct-digital': 'Pago Vinculado % App Digital GC',
    'linked-payment-pct-total-gc': 'Pago Vinculado % Total GC',
    'gma-delivery-pct': 'Entrega GMA % Total Entrega GC',
    'snapshot-digital-diagnostics-no-data': 'Sin Datos',
    'snapshot-digital-diagnostics-error-loading-data': 'Error al Cargar los Datos',
    'digital-hero-kpi-definition': 'Resumen de Definiciones de KPIs Héroe Digital',
    'kpi-name': 'Nombre del KPI',
    'kpi-definition': 'Definición del KPI',
    'total-digital-percent-of-sales': 'Porcentaje Total de Ventas Digitales',
    'proportion-of-total-sales-digital-channel': 'Proporción del total de ventas que provienen de un canal Digital (app, kiosco y/o entrega)',
    'digital-app-percent-of-sales': 'Porcentaje de Ventas de la App Digital',
    'proportion-of-total-sales-digital-app': 'Proporción del total de ventas que provienen de la app digital',
    'loyalty-90-day-active-customers': 'Clientes Activos de Lealtad en 90 Días',
    'number-of-customers-90-days': 'Número de clientes* que realizaron al menos una transacción en la app digital durante los 90 días que terminan el último día del período seleccionado',
    'excludes-customers-without-ccpa': '*Esto excluye a cualquier cliente que haya optado por no participar en CCPA',
    'average-number-digital-app': 'Promedio de transacciones de la app digital por restaurante por día',
    'new-digital-app-customers-per-30-days': 'Nuevos Clientes de la App Digital por cada 30 Días',
    'number-of-customers-first-digital-app-transaction': 'Número de clientes que realizaron su primera transacción en la app digital en el período seleccionado, dividido por el número de días en el período seleccionado, multiplicado por 30',
    'summarizes-performace-highest-digital-kpis': 'Resume el desempeño de los KPIs digitales de mayor prioridad en 2024',
    'click-for-detail': 'Haz Clic para ver Detalles',
    'data-for-these-metrics': 'Los Datos para estas métricas son hasta',
    'number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Número de clientes* que realizaron al menos una transacción en la app digital durante el período seleccionado',
    'new-digital-app-customers': 'Nuevos Clientes de la App Digital',
    'mcDelivery-percent-of-sales': 'Porcentaje de Ventas de McDelivery',
    'proportion-of-system-sales': 'Proporción de las ventas del sistema que provienen de McDelivery (tanto 3PO como GMA)',
    'pct-of-mcDelivery-sales': '% de Ventas de McDelivery desde la App',
    'proportion-of-all-mcDelivery-sales': 'Proporción de todas las ventas de McDelivery que provienen de transacciones de Entrega GMA',
    'digital-hero2025-number-of-customers-first-digital-app-transaction': 'Número de clientes que realizaron su primera transacción en la app digital en el período seleccionado',
    'this-excludes-customers-opted-out': '*Esto excluye a cualquier cliente que haya optado por no participar en la Ley de Privacidad del Consumidor de California (y por lo tanto no es elegible para participar en el Programa de Recompensas MyMcDonald\'s)',
    'note-this-metric-only-gets-displayed': 'Nota: Esta métrica solo se muestra a nivel de operador o de tienda individual. La funcionalidad para ver esta métrica cuando se seleccionan varias tiendas no está disponible actualmente',
    'number-of-customers-who-had-first-digital-app-transaction': 'Número de clientes que realizaron su primera transacción en la app digital en el período seleccionado, dividido por el número de días en el período seleccionado, multiplicado por 30*',
    'this-metric-normalizes': '*Esta métrica se normaliza según el número de días en un período, lo que permite comparaciones de tendencias más efectivas. Por ejemplo, marzo siempre tendrá más nuevos clientes de la app digital que febrero debido a que tiene más días en el mes. Sin embargo, al normalizar por número de días, esta métrica permite una comparación justa de la adquisición de nuevos clientes entre los meses',
    'digital-hero-rank-trend': 'Tendencia',
    'digital-hero-rank-number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Número de clientes* que realizaron al menos una transacción en la app digital durante los 90 días que terminan el último día del período seleccionado',
    'digital-hero-rank-rankings': 'Clasificaciones',
    'digital-hero-rank2025-trend': 'Tendencia',
    'digital-hero-rank2025-number-of-customers-who-had-first-digital-app-transaction': 'Número de clientes que realizaron su primera transacción en la app digital en el período seleccionado',
    'digital-hero-rank2025-rankings': 'Clasificaciones',
    '90-day-active-customer-trend': 'Tendencia de Clientes Activos en 90 Días',
    'table': 'Mesa',
    'difference': 'Diferencia',
    'box-target': 'Objetivo',
    'box-container-no-data': 'Sin Datos',
    'snapshot-month-container-food': 'Comida',
    'raw-waste-pct': 'Desperdicio Crudo %',
    'condiments-pct': 'Condimentos %',
    'base-food-pct': 'Comida Base %',
    'comp-waste-pct': 'Desperdicio Comp %',
    'discount-pct': 'Descuento %',
    'emp-meals-pct': 'Comida Emp %',
    'stat-variance-pct': 'Var Est %',
    'unexplained-pct': 'No Explicado %',
    'snapshot-month-food-food': 'Comida',
    'sales-and-service': 'Ventas y Servicio',
    'today.retiring-soon-prefix': '¡Se retirará pronto! Haz clic',
    'today.here': 'aquí',
    'today.retiring-soon-suffix': 'para actualizar a la nueva y mejorada vista de Hoy.',
    'today.why': '¿Por qué?',
    'today.tooltip.better-labor': 'Mejor reporte de mano de obra',
    'today.tooltip.aligns-dar': 'Se alinea con el informe DAR',
    'today.tooltip.additional-metrics': 'Métricas adicionales disponibles para personalización',
    'today-container-labor': 'Labor',
    'actual-vs-needed': 'Real vs Necesario',
    'actual-hrs-vs-needed': 'Horas Reales vs Necesarias',
    'actual-vs-scheduled': 'Real vs Programado',
    'actual-hrs-vs-scheduled': 'Horas Reales vs Programadas',
    'sales-per-punched-hr': 'Ventas / Hora Registrada',
    'gcs-per-punched-hr': 'GCs / Hora Registrada',
    'today-labor-labor': 'Labor',
    'today-labor-avg-rate': 'Tasa Promedio',
    'today-labor-punch-labor-pct': 'Mano de Obra Marcada %',
    'product-sales-comp-pct': 'Ventas de Productos +/- %',
    'average-check-comp-pct': 'Ticket Promedio +/- %',
    'gcs-comp-pct': 'GC\'s +/- %',
    'oepe-wo-park-comp': 'OEPE Sin Parqueo +/-',
    'oepe': 'OEPE',
    'oepe-comp': 'OEPE +/-',
    'r2p': 'R2P',
    'r2p-comp': 'R2P +/-',
    'kvs-healthy-usage-comp': 'Uso Saludable de KVS +/-',
    'kvs-time-per-gc-comp': 'Tiempo KVS / GC +/-',
    'today-sales-and-service-kvs-time-per-gc': 'Tiempo KVS / GC',
    'today-sales-and-service-all-net-sales': 'Todas las Ventas Netas',
    'today-sales-and-service-all-net-sales-comp-pct': 'Todas las Ventas Netas +/- %',
    'today-sales-and-service-prod-sales': 'Ventas Prod',
    'today-sales-and-service-average-check': 'Ticket Promedio',
    'today-sales-and-service-gcs': 'GC\'s',
    'today-sales-and-service-avg-ctp': 'CTP Promedio',
    'today-sales-and-service-avg-ctp-comp': 'CTP Promedio +/-',
    'sales-per-punched': 'Ventas / Hora Marcada',
    'gc-per-punched': 'TPPH',
    'today-widget-kvs-time-per-gc': 'Tiempo KVS / GC',
    'today-widget-labor': 'Labor',
    'today-widget-all-net-sales': 'Todas las Ventas Netas',
    'today-widget-all-net-sales-comp-pct': 'Todas las Ventas Netas +/- %',
    'today-widget-prod-sales': 'Ventas Prod',
    'today-widget-average-check': 'Ticket Promedio',
    'today-widget-gcs': 'GC\'s',
    'today-widget-dt-sales': 'Ventas de Autoservicio',
    'today-widget-avg-dt-ttl': 'TTL Auto Prom',
    'today-widget-kvs-items-per-gc': 'Artículos KVS / GC',
    'today-widget-avg-ctp': 'CTP Promedio',
    'today-widget-kvs-time-per-item': 'Tiempo KVS / Artículo',
    'today-widget-avg-rate': 'Tasa Promedio',
    'today-widget-punch-labor-pct': 'Mano de Obra Marcada %',
    'the-menu-items-in-this-container': 'Los artículos del menú en este contenedor son seleccionados por su agencia. Eso le permite alinearse con su agencia sobre los artículos de menú más importantes que ocurren en sus restaurantes y mercado. Por favor, dirija preguntas sobre los detalles de estos artículos de menú a su agencia.',
    'bu-menuitems-no-data': 'Sin datos - Ya sea',
    'bu-menuitems-select-a-bigger-date-range': 'seleccione un rango de fechas más amplio donde ocurrieron estos artículos de menú',
    'bu-menuitems-ask-your-agency': 'pida a su agencia que seleccione hasta dos artículos de menú para mostrar aquí',
    'bu-menuitems-meal-pct-of-gc': '% de GC',
    'bu-menuitems-avg-check': 'Cheque Promedio',
    'date-selector-ok': 'Aceptar',
    'launch-freebie-verbiage': 'La Analítica Avanzada  (TA) se ha activado temporalmente para apoyar el lanzamiento de la iniciativa del sistema EVM. Para continuar y tener visibilidad completa de TA, comuníquese con el líder de su BU para discutir una suscripción.',
    'extra-value-meal-container-extra-value-meal': 'Comida de valor extra',
    'extra-value-meal-container-start-date': 'Fecha de inicio',
    'extra-value-meal-container-end-date': 'Fecha de finalización',
    'report-rankings-title': '{report} Clasificaciones - {date}',
    'ta-rank-trend': 'Tendencia',
    'ta-trend': 'Tendencia TA',
    'spicy-mcmuffins': 'McMuffins Picantes',
    '5-meal-deal': 'Combo de $5',
    'base-food-decomposition': 'Descomposición de Comida Base',
    'transaction-analytics-container-mcCrispy-strips': 'Nueva Plataforma de Pollo',
    'transaction-analytics-container-two-year-growth': 'Crecimiento a Dos Años',
    'transaction-analytics-container-addon-subscribed': 'Complemento · Suscrito',
    'transaction-analytics-container-addon-upgrade': 'Complemento · Actualizar',
    'transaction-analytics-container-addon-upgrade-tooltip': 'Hable con el líder de su BU sobre cómo agregar Advanced Analytics para:\n1. Soporte de Agencia y BU a través de una aplicación de Business Insights\n2. Soporte O/O a través de MyQ en ofertas y eventos Nacionales y de BU',
    'transaction-analytics-container-transaction-analytics': 'Analítica Avanzada ',
    'transaction-analytics-container-boao-for-1': 'BOAO por $1',
    'no-options-selected': 'Su agencia aún no ha seleccionado ninguna opción para esta unidad de negocio. Solicite a su agencia que seleccione las ofertas y los elementos del menú que se mostrarán aquí.',
    'this-and-agency-support': 'Esto forma parte del servicio BU de Analítica Avanzada ',
    'talk-to-bu-lead': 'Habla con el líder de tu BU sobre agregar Analítica Avanzada  para:',
    'agency-and-bu-support': 'Soporte de Agencia y BU a través de la aplicación Business Insights',
    'oo-support-myq': 'Soporte O/O a través de MyQ en ofertas y eventos nacionales y de BU',
    'current-incentives': 'Eventos Nacionales Actuales',
    'transaction-analytics-service-disclaimer-learn-more': 'Aprender Más',
    'net-sales-comp': 'Ventas Netas/R +/-',
    'pr-yr-net-sales-comp': 'Ventas Netas/R +/- Año Ant.',
    'gc-comp': 'GC/R +/-',
    'pr-yr-gc-comp': 'GC/R +/- Año Ant.',
    'agency-control': 'Control de Agencia',
    'bu-offers-no-data': 'Sin datos - Por favor',
    'bu-offers-meal-pct-of-gc': '% de GC',
    'bu-offers-avg-check': 'Cheque Promedio',
    'base-food-decomp-trend': 'Tendencia Descomp Comida Base',
    'trend-chart-placeholder': 'El gráfico de tendencia se implementará aquí',
    'copied': '¡Copiado al portapapeles!',
    'ROD': 'ROD',
    'urd': 'U/R/D',
    'extra-value-meal-report-title': 'Comida de valor extra: 15%',
    'extra-value-meal-business-units-not-signed-up': 'Una o más de tus Unidades de Negocio no están inscritas en Analítica Avanzada. Sus ubicaciones no se incluirán en el informe. Contacta al líder de tu BU para más información.',
    'breakfast-abbr': 'DESAY',
    'five-and-fifteen-five-and-fifteen': '5%/15%',
    'five-and-fifteen-units-served-rd': 'Unidades Servidas/R/D',
    'five-and-fifteen-avg-check': 'Cheque Promedio',
    'five-and-fifteen-meal-pct-of-gc': '% GC',
    'five-and-fifteen-business-units-not-signed-up': 'Una o más de tus Unidades de Negocio no están inscritas en Analítica Avanzada. Sus ubicaciones no se incluirán en el informe. Contacta al líder de tu BU para más información.',
    'menu-item-selector': 'Conjunto de artículos del menú',
    'metric-selector-day': 'Por día',
    'metric-selector-gc': 'Por 1K invitados',
    'per-day-label': 'Unidades vendidas/R/D',
    'per-gc-label': 'Unidades vendidas/1K GC',
    'week-range': 'Dom-Sáb',
    'trend-prior-no-data': 'Sin datos',
    'trend-prior-report-title': 'Tendencia semanal',
    'prior-year': 'Año anterior',
    'trend-trading-no-data': 'Sin datos',
    'trend-trading-report-title': 'Tendencia semanal',
    'trend-trading-business-units-not-signed-up': 'Una o más de tus Unidades de Negocio no están inscritas en Analítica Avanzada. Sus ubicaciones no se incluirán en el informe. Contacta al líder de tu BU para más información.',
    'five-meal-deal-weekly-units': 'Las unidades semanales, inversiones C&P y margen se promedian según los GC\'s que incluyeron cualquier Combo nacional en el pedido',
    'mcCrispySandwiches': 'Sándwiches McCrispy',
    'snackWraps': 'Bocadillos Envueltos',
    'biscuits': 'Bisquets',
    'hot-honey-meal-pct-of-rod-gc': '% GC',
    'hot-honey-units-served-1k-rod-gc': 'Unidades Servidas/1K GC',
    'mcCrispyStrips': 'Tiras McCrispy',
    'this-category': 'Esta Categoría',
    'national-category-trend-no-data': 'Sin Datos',
    'national-category-trend-report-title': 'Tendencia de 5 Semanas',
    'national-rail-row-margin-pct': 'MARGEN %',
    'as-of': 'Al',
    'what-this-shows': 'Para los insumos de comida y condimentos que usan las recetas de los artículos vigilados: $ Dif. (real menos teórico, sumado en los restaurantes seleccionados) y % Ventas Prod. ($ Dif. absoluto sobre las ventas de producto del mes a la fecha), mayor % primero. Abra el ranking para ver cada restaurante, su último inventario y el $ Dif. de mayor pérdida a mayor ganancia.',
    'national-watchlist-stat-loss-no-data': 'Sin filas de pérdida stat para los artículos vigilados en los restaurantes seleccionados en esta fecha final',
    'national-watchlist-stat-loss-report-title': 'Pérdida Stat',
    'national-control': 'Nacional',
    'national-widget-container-if-units-were-only-sold': 'Si las unidades solo se sirvieron durante 5 de los 7 días, entonces el "Día" en Unidades/R/D divide las unidades por 5 días',
    'national-widget-container-mcCrispy-strips': 'Nueva Plataforma de Pollo',
    'national-widget-container-watchlist': 'Seleccionar Watchlist',
    'national-widget-container-boao-for-1': 'BOAO por $1',
    'national-widget-container-agency-menu-items-bevs': 'Artículos del Menú (Bebidas)',
    'national-widget-container-agency-menu-items-bevs-summary': 'Artículos del Menú (Bebidas) con Resumen',
    'meal-pct-of-breakfast-gc': '%/Desayuno GC',
    'units-served-1k-breakfast-gc': 'Unidades Servidas/1K Desayuno GC',
    'spicy-mc-muffins-units-served-rd': 'Unidades Servidas/R/D',
    'spicy-mc-muffins-avg-check': 'Cheque Promedio',
    'spicy-mc-muffins-business-units-not-signed-up': 'Una o más de sus Unidades de Negocio no se han inscrito en Advanced Analytics. Las ubicaciones de estas Unidades de Negocio no se incluirán en el informe. Por favor, contacte al líder de su Unidad de Negocio para más información.',
    'you-can-only-have': 'Solo puedes tener hasta 10 cuadros de mando. Por favor elimina uno antes de crear uno nuevo.',
    'click-on-the-cells-to-edit-like-excel': 'Haz clic en las celdas para editarlas, como en Excel.',
    'global-goals': 'Objetivos Globales',
    'the-goals-that-you': 'Los objetivos que hayas establecido se aplicarán a todos los cuadros de mando. Puedes editar estos objetivos en la pestaña \'OBJETIVOS\' de la configuración principal.',
    'do-you-want-to-continue': '¿Deseas Continuar?',
    'yes-save-close': 'Si, Guardar y Crear',
    'create-score-card-create-scorecard': 'Crear Cuadro de Mando',
    'create-score-card-name-assign': 'Nombrar y Asignar',
    'create-score-card-qsr-defaults': 'Valores Predeterminados de QSR',
    'create-score-card-defaults': 'Valores Predeterminados',
    'no-goals-set': 'No se han establecido metas',
    'scorecard-name': 'Nombre del Cuadro de Mando',
    'assigning-roles-to': 'La asignación de roles a un cuadro de mando permitirá que solo los usuarios con esos roles puedan ver el cuadro de mando.',
    'create-copy': 'Crear Copia',
    'score-card-container-settings-scorecard-settings': 'Configuración del Cuadro de Mando',
    'score-card-container-settings-scorecards-2': 'Tarjetas de Puntuación',
    'goals-per-location': 'Objetivos Por Ubicación',
    'all-locs': 'Todas las Ubicaciones',
    'goal-grid-locations': 'Ubicaciones',
    'row-order': 'Orden de Fila',
    'tab-date-selector-ok': 'Aceptar',
    'customize-metrics': 'Personalizar Métricas',
    'row': 'Fila',
    'thresholds': 'Limites',
    'healthy-limit': 'Límite Saludable',
    'under': 'Debajo',
    'between': 'Entre',
    'upper': 'Superior',
    'lower': 'Inferior',
    'threshold-settings-over': 'Encima',
    'no-shared-links': 'No se han agregado enlaces compartidos para su organización.',
    'that-need-attention': 'que necesitan atención',
    'manage-requests': 'Gestionar Solicitudes',
    'requests': 'solicitudes',
    'employee-requests-insight-request': 'solicitud',
    'employee-requests-insight-you-have': 'Tienes',
    // BUOverview chart series; only the -title key existed
    'two-year-growth': 'Crecimiento de 2 Años',
    // Migrated from <i18n> SFC blocks (#6500)
    'voice-rankings-voice-rankings': 'Clasificación de Voz',
    'financial-is-used-for-current-mid-month': 'Financiero se utiliza para el mes actual o rangos de fechas que abarcan fuera de los meses completados. Financiero excluye ubicaciones que no han estado abiertas durante 13 meses completos. El valor predeterminado para Financiero es la Comparación de Días de Comercio.',
    'analytic-is-used-for-completed-months': 'Analítico se utiliza para meses completados y rangos de fechas que incluyen meses completados. Analítico utiliza una regla de 5 días. Si una ubicación no tiene ventas durante 5 o más días (ya sea en el Año Actual o en el Año Anterior), se excluye del cálculo. El valor predeterminado para Analítico es la Comparación de Días del Calendario.',
    'confirmDeletion': 'Confirmar Eliminación',
    'areYouSure': '¿Estás seguro de que deseas eliminar esta lista de seguimiento?',
    'businessUnitMenuItemDetails': 'Detalles de los Artículos del Menú Nacional',
    'watchlist': 'Lista de Seguimiento',
    'subtitle': 'Seleccione hasta cinco listas de seguimiento por categoría para mostrar a los O/Os en la sección AA del Panel de Control de QSRSoft. No habrá resumen ni total entre las listas de seguimiento.',
    'widgetLabel': 'Título del Widget',
    'national-menu-items': 'Menú Nacional',
    'widgetDialogTitle': 'Editar Título del Widget',
    'widgetTitleDisclaimer': 'Cree una lista de seguimiento antes de establecer el título',
    'watchlistName': 'Nombre de la lista de seguimiento',
    'non_offer': 'Sin oferta',
    'national': 'Oferta nacional',
    'availableMenuItems': 'Artículos del menú disponibles',
    'noMatchingMenuItems': 'No se encontraron artículos de menú coincidentes.',
    'showAll': 'Mostrar todo',
    'showSelected': 'Mostrar seleccionados',
    'saveWatchlist': 'Guardar',
    'clear': 'Limpiar',
    'widgetTitleLabel': 'Título del widget (se aplica a todas las listas de seguimiento)',
    'construction-watchlist-modal-local': 'Oferta local',
    'construction-watchlist-modal-watchlist': 'Lista de seguimiento',
    'construction-watchlist-modal-filter': 'Filtrar',
    'bumid-businessUnitMenuItemDetails': 'Detalles de los Artículos del Menú de la Unidad de Negocio',
    'menu-item-cell-selectMenuItem': 'Seleccionar Elemento del Menú',
    'menu-item-modal-selectMenuItem': 'Seleccionar Elemento del Menú',
    'menu-item-modal-availableMenuItems': 'Elementos del Menú Disponibles',
    'businessUnitOffers': 'Ofertas de Unidad de Negocio',
    'selectOffersInstructions': 'Seleccione hasta dos ofertas de paquetes automáticos/móviles por unidad de negocio para mostrar a los O/Os en la sección TA del panel de inicio de MyQSRSoft.',
    'promotionMembers': 'Miembros de la Promoción',
    'selectOffer': 'Seleccionar Oferta',
    'transactionAnalytics': 'Análisis de Transacciones',
    'hideWidget': 'Ocultar',
    'showWidget': 'Mostrar',
    'offers': 'Ofertas',
    'example-widget-businessUnit': 'UN',
    'availableOffers': 'Ofertas Disponibles',
    'offerDetails': 'Detalles de la Oferta',
    'selectOfferToViewDetails': 'Seleccione una oferta para ver los detalles',
    'promo-selector-back': 'Volver',
    'bu-grid-noData': 'Sin Datos',
    'operatorDashboard': 'Panel de Vista del Operador',
    'operatorDashboardDescription': 'Vea lo que los operadores ven a diario. Para un análisis en profundidad y tendencias, seleccione el',
    'businessIntelligence': 'Inteligencia Empresarial',
    'tool': 'herramienta.',
    'mcCrispy-strips': 'McCrispy Strips',
    'loyalty-90-day-active-trend': 'Tendencia Activa de Lealtad de 90 Días',
    'agency-menu-items': 'Menú de Agencia',
    'agency-menu-items-with-summary': 'Menú de Agencia con Resumen',
    'agency-menu-items-bevs': 'Menú de Agencia (Bebidas)',
    'agency-menu-items-bevs-summary': 'Menú de Agencia (Bebidas) con Resumen',
    'five-and-eight': '$5/$8',
    'five-and-fifteen': '$5/$15',
    'trend': 'Tendencia',
    'extra-value-meal-15': 'Comida de valor extra: 15%',
    'extra-value-meal-5': 'Comida de valor extra: 5%',
    'agency-offers': 'Ofertas de Agencia',
    'f-and-p-investment': 'C&P Inversión',
    'bu-overview-voice': 'Voz',
    'bu-overview-food': 'Comida',
    'bu-overview-digital-app': 'Aplicación Digital',
    'bu-overview-labor': 'Labor',
    'bu_loyalty_90_day_active_trend': 'Tendencia activa de lealtad de BU a 90 días',
    'bu_loyalty_90_day_active_trend_info': 'Número de clientes únicos que realizaron al menos una GC de aplicación digital en el período de 90 días. Dado que un cliente puede visitar más de un restaurante en una organización, este número no se puede sumar entre restaurantes.',
    'widget_disabled_info': 'Este widget solo funciona para BU',
    'bu_digital_app': 'Aplicación Digital de BU',
    'bu_digital_order_pts': 'Puntos de Pedido Digital',
    'bu_food': 'Comida',
    'bu_labor': 'Labor',
    'bu_sales': 'Ventas',
    'bu_sales_financial': 'Financiero se utiliza para el mes actual o rangos de fechas que abarcan meses completados. Financiero excluye ubicaciones que no han estado abiertas durante 13 meses completos. El predeterminado para Financiero es Comparación de Días de Negocio.',
    'bu_sales_analytic': 'Analítico se utiliza para meses completados y rangos de fechas que incluyen meses completados. Analítico utiliza una regla de 5 días. Si una ubicación no tiene ventas durante 5 o más días (en el Año Actual o el Año Anterior), se excluye del cálculo. El predeterminado para Analítico es Comparación de Días del Calendario.',
    'bu_service': 'Servicio',
    'people_metrics_info': 'Las métricas de personas se calculan utilizando meses completos.',
    'people_metrics_multiple_months': 'Si se seleccionan varios meses, se utiliza el mes completo más reciente.',
    'people_metrics_partial_month': 'Si un mes pasado se selecciona parcialmente, se utiliza el mes completo.',
    'people_metrics_detailed_breakdown': 'Para un desglose detallado, consulte el informe de clasificación de BU.',
    'peopleDataRequiresFullMonth': 'Los datos de personas requieren un rango de fechas de mes completo.',
    'voiceDataTooRecent': 'Los datos de voz generalmente tienen 2 días de retraso. Por favor, seleccione un rango de fechas anterior',
    'widgetOnlyWorksForBU': 'Este Widget solo funciona para BU',
    'bu-voice-noData': 'Sin Datos',
    'boao-for-1': 'BOAO para $1',
    'business-units-not-signed-up': 'Una o más de tus Unidades de Negocio no están inscritas en Analítica Avanzada . Sus ubicaciones no se incluirán en el informe. Contacta al líder de tu BU para más información.',
    'transaction-level-data-updates': 'Los datos a nivel de transacción se actualizan durante el día para el día anterior. Ten en cuenta que los resultados pueden estar incompletos temprano en el día.',
    'units-sold-days': 'Si las unidades solo se vendieron durante 5 de los 7 días, entonces el "Día" en Unidades/R/D divide las unidades entre 5 días',
    'rod': 'RDD',
    'meal-pct-of-gc': '% de GC',
    'units-served-rd': 'Unidades Servidas/R/D *',
    'units-served-1k-gc': 'Unidades Servidas/1K GC',
    'avg-margin-amt': 'Margen Promedio',
    'margin-pct': 'Margen %',
    'bogo-avg-check': 'Cheque Promedio',
    'chart-subtitle': 'Datos para el período',
    'units-per-thousand': 'Unidades Servidas/1K GC',
    'bu-category-trend-no-data': 'Sin Datos',
    'bu-category-trend-report-title': 'Tendencia de 5 Semanas',
    'units-1k-gc': '/1K GC',
    'bu-rail-row-margin-pct': 'MARGEN %',
    'no-items-found': 'No se encontraron artículos',
    'bu-watchlist-section-watchlist': 'Seleccionar Watchlist',
    'meal-deal': 'Combo',
    'weekly-units': 'Las unidades semanales, inversiones C&P y margen se promedian según los GC\'s que incluyeron cualquier Combo nacional en el pedido',
    'happy-meal-toy': 'El costo del juguete Happy Meal es lo que tienes configurado en la clave 846. Si está en blanco, entonces usamos $0.60',
    'explain-rod': 'RDD significa “Resto del Día” y excluye el desayuno (4am-11am)',
    'meal-pct-of-rod-gc': '%/RDD GC',
    'units-served-1k-rod-gc': 'Unidades Servidas/1K RDD GC',
    'five-meal-deal-units-served-rd': 'Unidades Servidas/R/D',
    'five-meal-deal-avg-check': 'Cheque Promedio',
    'promo-&-discount': 'Promo y Desc C&P',
    'discount-f&p': 'Descuento C&P = El % de descuento del Artículo(s) del Menú aplicado al costo de Comida y Papel del Artículo(s). Monto de Comida y Papel descontado.',
    'promo-f&p': 'Promo C&P = El costo de Comida y Papel de los Artículos de Menú en Promoción.',
    'base-food-plus-promo-discount': 'Comida Base + Promo y Desc C&P',
    'base-food': 'Comida Base',
    'check-the': 'Consulta la página',
    'pmix-page': 'PMIX',
    'for-further-info': 'para más información sobre Hot Honey',
    'mc-crispy-strips': 'Tiras McCrispy',
    'hot-honey-business-units-not-signed-up': 'Una o más de tus Unidades de Negocio no están inscritas en Analítica Transacciones. Sus ubicaciones no se incluirán en el informe. Contacta al líder de tu BU para más información.',
    'hot-honey-units-served-rd': 'Unidades Servidas/R/D',
    'hot-honey-avg-check': 'Cheque Promedio',
    'hot-honey-report-title': 'Hot Honey',
    'hot-honey-meal-pct-of-gc': '% GC',
    'mc-crispy-strips-for-further-info': 'para más información sobre Wrap Combos',
    'mc-crispy-strips-units-served-rd': 'Unidades Servidas/R/D',
    'mc-crispy-strips-avg-check': 'Cheque Promedio',
    'mc-crispy-strips-report-title': 'Nueva Plataforma de Pollo',
    'the-offers-in-this-container': 'Las ofertas en este contenedor son seleccionadas por su agencia. Eso le permite alinearse con su agencia sobre las ofertas más importantes que ocurren en sus restaurantes y mercado. Por favor, dirija preguntas sobre los detalles de estas ofertas a su agencia.',
    'if-units-were-only-sold': 'Si las unidades solo se vendieron durante 5 de los 7 días, entonces el "Día" en Unidades/R/D divide las unidades por 5 días',
    'select-a-bigger-date-range': 'seleccione un rango de fechas más amplio donde ocurrió esta oferta',
    'ask-your-agency': 'pida a su agencia que seleccione dos ofertas (promociones o descuentos) para mostrar aquí',
    'offers-no-data': 'Sin datos - Por favor',
    'offers-meal-pct-of-gc': '% de GC',
    'offers-avg-check': 'Cheque Promedio',
    'buSummaryContainersDescription': 'La Analítica Avanzada alinea agencias, McD y operadores en eventos importantes. Los operadores en BUs suscritos ven estos contenedores con datos solo de sus organizaciones, mientras que las agencias los ven con información a nivel de BU.',
    'extra-value-meal': 'Comida de Valor Extra',
    'two-year-growth-title': 'Crecimiento de 2 Años de {type}',
    'weekly-trading-comparison': 'Comparación semanal de ventas. Se utiliza la lógica financiera de ventas.',
    'excluded-stores': 'Se excluyeron las tiendas que no tuvieron ventas este año o el año pasado.',
    'watchlist-details-businessUnitMenuItemDetails': 'Detalles de los Artículos del Menú de la Unidad de Negocio',
    'watchlist-details-subtitle': 'Seleccione hasta cinco listas de seguimiento por unidad de negocio para mostrar a los O/Os en la sección TA del Panel de Control de QSRSoft. No habrá resumen ni total entre las listas de seguimiento.',
    'custom-request-form-loading': 'Cargando',
    'failed-message': 'Error al cargar el panel de control de QuickSight',
    'new-extract': 'Nuevo Extracto',
    'extracts': 'Extractos',
    'report-finder': 'Buscador de Reportes',
    'report': 'Reporte',
    // Migrated from <i18n> SFC blocks (#6500)
    'Su': 'Do',
    'M': 'Lu',
    'Tu': 'Ma',
    'W': 'Mi',
    'Th': 'Ju',
    'F': 'Vi',
    'Sa': 'Sa',
    'day-type-calendar': 'Calendario',
    'day-type-business-date': 'Fecha de Negocio',
    'pay-period': 'Período de Pago',
    'start-and-end-date-correspond': '* Las fechas de inicio y finalización corresponden a los ciclos de pago',
    'pay-period-selector-multi-end-date': 'Fecha de Finalización',
    'pay-period-selector-multi-v2-end-date': 'Fecha de Finalización',
    'quick-dates-v3-yesterday': 'AYER',
    'quick-dates-v3-last-month': 'MP',
    'quick-dates-v3-last-year': 'AA',
    'quick-dates-v3-month-to-date': 'MDH',
    'quick-dates-v3-year-to-date': 'ADH',
    'quick-dates-v3-trailing-365-days': '365D',
    'bu-scope-selector-scope': 'Alcance',
    'business-unit': 'Unidad de Negocio',
    'location-agg-and-location': 'y Ubicación',
    'location-agg-v2-by-location': 'Por ubicación',
    'same-store-comp-all-store-comp-2': 'Todas las Tiendas Comparación',
    'same-store-comp-same-store-comp-2': 'Comparación de la Misma Tienda',
    'only-active-stores': 'Los datos se limitan solo a las tiendas activas.',
    'daypart-dropdown-v2-all': 'Todos',
    'peak-dropdown-v2-required': 'Se requieren partes del día',
    'peak-dropdown-v2-all': 'TODOS',
    'Time Segment': 'Segmento de tiempo',
    'Peaks': 'Picos',
    'Dayparts': 'Partes del día',
    'Timeslices': 'Segmentos de tiempo',
    'Custom': 'Personalizado',
    'selected': 'seleccionado',
    'timeslice-dropdown-required': 'Se requieren partes del día',
    'timeslice-dropdown-all': 'Todos',
    'timeslice-dropdown-v2-required': 'Se requieren partes del día',
    'timeslice-dropdown-v2-all': 'Todos',
    'select-report': 'Seleccionar Informe',
    'view-all-scheduled-reports': 'Ver Todos los Informes Programados',
    'report-schedule-schedule': 'Programar',
    'schedule-reports': 'Programar Informes',
    'daily-reports-always-retrieve-data-from-yesterday': 'Los informes diarios siempre recuperan datos de ayer',
    'weekly-reports-always-retrieve-data-from-the-previous-week': 'Los informes semanales siempre recuperan datos de la semana anterior.',
    'monthly-reports-always-retrieve-data-from-the-previous-month': 'Los informes mensuales siempre recuperan datos del mes anterior.',
    'frequency': 'Frecuencia',
    'recipients': 'Destinatarios',
    'invalid-email': 'Correo electrónico inválido',
    'recipients-selector-required': 'Requerido',
    'day-of-month': 'Día del Mes',
    'time-selector-day-of-week': 'Día de la Semana',
    // Migrated from <i18n> SFC blocks (#6500)
    'register-type-selector-register': 'Tipo de Registro',
    'select-date': 'Seleccionar Fecha',
    'tomorrow': 'Mañana',
    'two-days-ago': 'Hace 2 días',
    'quick-dates-last-week': 'SP',
    'next-week': 'PS',
    'quick-dates-this-week': 'SA',
    'quick-dates-this-month': 'MA',
    'next-month': 'PM',
    'week-to-date': 'SDH',
    '24-months': '24M',
    '36-months': '36M',
    'quick-dates-yesterday': 'AYER',
    'quick-dates-last-month': 'MP',
    'quick-dates-last-year': 'AA',
    'quick-dates-month-to-date': 'MDH',
    'quick-dates-year-to-date': 'ADH',
    'quick-dates-trailing-365-days': '365D',
    'quick-dates-v2-yesterday': 'AYER',
    'quick-dates-v2-last-month': 'MP',
    'quick-dates-v2-last-year': 'AA',
    'quick-dates-v2-month-to-date': 'MDH',
    'quick-dates-v2-year-to-date': 'ADH',
    'quick-dates-v2-trailing-365-days': '365D',
    'sum-all-locations-long': 'Resumen',
    'by': 'Por',
    'and-location': 'y Ubic.',
    'location-agg-sum-all-locations': 'Resumen',
    'location-agg-by-location': 'Por ubicación',
    'same-store-comp-all-store-comp': 'Todas las Tiendas',
    'same-store-comp-same-store-comp': 'Misma Tienda',
    'daypart-dropdown-all': 'Todos',
    'by-day': 'Por día',
    'by-timeslice': 'Por segmento(s) de tiempo',
    'peak-dropdown-required': 'Se requieren partes del día',
    'peak-dropdown-all': 'TODOS',
    'label': 'Ver segmento por',
    'timeslice-submenu-label': 'Segmento(s) de tiempo',
    'segment-by-dropdown-by-time-slice': 'Por segmento(s) de tiempo',
    // Generic actions
    // Must match myq-core's standardTranslations: core's useI18n spreads those
    // over the domain messages this file contributes, so a different value here
    // would render only in global scope ($t / i18n.global.t) and disagree with
    // every component that uses the useI18n composable.
    reset: 'Restablecer',
    discard: 'Descartar',

    // Empty / error states
    'no-data': 'Sin datos',
    'an-error-occurred-while-loading-the-report':
      'Se produjo un error al cargar el informe. Reduzca las tiendas o el rango de fechas. Si el problema no se resuelve, comuníquese con el soporte.',

    // Filter containers
    filters: 'Filtros',
    'please-select-at-least-one-location':
      'Por favor seleccione al menos una ubicación',
    'please-select-a-scope': 'Por favor seleccione un alcance',
    'please-select-a-date-range': 'Por favor seleccione un rango de fechas',
    'please-select-a-time-segment': 'Por favor seleccione un segmento de tiempo',
    'please-select-please-select-at-least-one-daypart':
      'Por favor seleccione al menos un día',
    'please-select-please-select-at-least-one-time-slice':
      'Por favor seleccione al menos una franja horaria',
    'please-select-please-select-at-least-one-peak':
      'Por favor seleccione al menos un pico',
    'the-following-restaurants-are-not-included':
      'Los siguientes restaurantes no están incluidos en este informe porque no están incluidos en su perfil de usuario.',
    'if-you-are-logged-in-with-eid':
      'Si ha iniciado sesión con un eID, su lista de restaurantes debe corregirse en GAM. Una vez que se agreguen los restaurantes, deberá cerrar sesión y volver a iniciar sesión para que QSRSoft recupere la lista actualizada de restaurantes. Los cambios en GAM pueden tardar hasta 48 horas en propagarse a nuestro sistema.',
    'if-you-are-logged-in-with-email':
      'Si ha iniciado sesión con un correo electrónico, comuníquese con su administrador del sistema para que agregue los restaurantes a su perfil. Deberá actualizar su navegador después de que el administrador complete el cambio.',

    // Loading / retry
    retrying: 'Reintentando...',

    // Grid container
    'expand-rows': 'Expandir Filas',

    // Chart container
    overlap: 'Superponer',
    showChart: 'Mostrar gráfico',
    hideChart: 'Ocultar gráfico',
    // Own key: 'timeslices' resolves to other text app-wide (#6335)
    'timeslice-dropdown-timeslices': 'Segmentos de tiempo',
    // Own key: 'first-name' resolves to other text app-wide (#6335)
    'minor-exceptions-first-name': 'Nombre',
    // Own key: 'timeslice' resolves to other text app-wide (#6335)
    'custom-incentive-timeslice': 'Franja horaria',
    // McDonald's dashboard tabs (oldDashboard/mcd/main.vue)
    'transaction-analytics': 'Análisis Avanzado',
  },
  fr: {
    // Migrated from <i18n> SFC blocks (#6500)
    'buOverview': 'Vue exploitant',
    'quick-sight-insights': 'Analyses métier',
    'offer': 'Détails de l’offre BU',
    'buWatchlistDetails': 'Détails des articles du menu BU',
    'buWatchlistDetailsWithSummary': 'Détails des articles du menu BU avec synthèse',
    'buWatchlistDetailsHeader': 'Détails des articles du menu de la business unit',
    'buWatchlistDetailsSubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par BU à afficher aux O/O dans la section TA du tableau de bord d’accueil QSRSoft. Aucune synthèse ni aucun total ne sera calculé sur l’ensemble des listes de suivi.',
    'buWatchlistDetailsWithSummaryHeader': 'Articles du menu de l’agence avec synthèse sur l’ensemble des listes de suivi',
    'buWatchlistDetailsWithSummarySubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par BU à afficher aux O/O dans la section TA du tableau de bord d’accueil QSRSoft. Le widget affichera chaque liste de suivi ainsi qu’une synthèse sur l’ensemble des listes. Tous les articles d’une liste de suivi doivent appartenir à la même plateforme, par ex. Boissons d’été ou Offres repas.',
    'nationalAAIncentiveBuilder': 'Générateur d’incitatifs AA national',
    'nationalMenuItemDetails': 'Détails',
    'nationalMenuItemDetailsWithSummary': 'Détails avec synthèse',
    'nationalMenuItemDetailsHeader': 'Détails des articles du menu national',
    'nationalMenuItemDetailsSubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Aucune synthèse ni aucun total ne sera calculé sur l’ensemble des listes de suivi.',
    'nationalMenuItemDetailsWithSummaryHeader': 'Articles du menu national avec synthèse sur l’ensemble des listes de suivi',
    'nationalMenuItemDetailsWithSummarySubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Le widget affichera chaque liste de suivi ainsi qu’une synthèse sur l’ensemble des listes. Tous les articles d’une liste de suivi doivent appartenir à la même plateforme, par ex. Boissons d’été ou Offres repas.',
    'nationalMenuItemDetailsBevs': 'Détails (boissons)',
    'nationalMenuItemDetailsBevsHeader': 'Détails des articles du menu national (boissons)',
    'nationalMenuItemDetailsBevsSubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Aucune synthèse ni aucun total ne sera calculé sur l’ensemble des listes de suivi.',
    'nationalMenuItemDetailsBevsWithSummary': 'Détails (boissons) avec synthèse',
    'nationalMenuItemDetailsBevsWithSummaryHeader': 'Articles du menu national (boissons) avec synthèse sur l’ensemble des listes de suivi',
    'nationalMenuItemDetailsBevsWithSummarySubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Le widget affichera chaque liste de suivi ainsi qu’une synthèse sur l’ensemble des listes. Tous les articles d’une liste de suivi doivent appartenir à la même plateforme.',
    // Migrated from <i18n> SFC blocks (#6500)
    'Sunday': 'Dimanche',
    'Monday': 'Lundi',
    'Tuesday': 'Mardi',
    'Wednesday': 'Mercredi',
    'Thursday': 'Jeudi',
    'Friday': 'Vendredi',
    'Saturday': 'Samedi',
    'day-of-week-days': 'jours',
    'day-of-week-all': 'Tous',
    'day-of-week-v2-days': 'jours',
    'day-of-week-v2-all': 'Tous',
    'suspicious-activity-insight-data-failed-to-load': 'Le chargement des données a échoué. Veuillez réessayer ou contacter le support si le problème persiste.',
    'suspicious-activity-insight-more-info': 'Plus D\'Informations',
    'suspicious-activity-insight-please-subscribe': 'Veuillez Vous Abonner',
    'suspicious-activity-insight-learn-more': 'En Savoir Plus',
    'loading-range': 'Chargement de {range}',
    'security-drill-down-security-drill-down': 'Analyse détaillée de la sécurité',
    'hello-world': 'Calcul des matières sur base fondé sur l’utilisation PMIX ; il peut différer légèrement du calcul Caisse et Inventaire',
    'drivers-add-up': 'PMIX, Prix et Coût matières premières additionnés donnent la variation du % matières de base par rapport à la période de comparaison.',
    'drivers': 'Ce qui l’a fait varier',
    'fob-change': 'Matières de base vs M. préc.',
    'vs-pr-mth': 'vs M. préc.',
    'adds-up-to': 'totalise {value}',
    'colour-key': 'Le vert baisse le coût matières, le rouge l’augmente.',
    'raw-cost': 'Coût matières premières',
    'pricing': 'Prix',
    'pmix': 'PMIX',
    'rbd-events': 'RBD',
    'rbd-tooltip': 'Les RBD peuvent fausser les données du mix produits et, par conséquent, les coûts matières présentés.',
    'rbd-store-line': 'Restaurant {store} : {count} jour(s) RBD',
    'base-food-decomp-widget-base-food-decomposition': 'Analyse des matières de base',
    'base-food-decomp-widget-fob': 'Matières de base',
    'base-food-decomp-widget-disclaimer': 'Analyser ce qui fait varier les matières de base est essentiel pour comprendre la performance d’un événement, mais ce n’est pas tout. Les variations des promos et remises ainsi que la croissance sur 2 ans des ventes et des GC sont aussi nécessaires pour une vue analytique complète.',
    'base-food-decomp-widget-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'day-of-week-v3-days': 'jours',
    'day-of-week-v3-all': 'Tous',
    'role-defaults': 'Paramètres par défaut du rôle',
    'enterprise-settings': 'Paramètres de l’entreprise*',
    'enterprise-help': 'Décochez ce que cette entreprise n’utilise pas. Les éléments décochés sont masqués partout et ne peuvent pas être activés par les paramètres par défaut des rôles.',
    'select-role': 'Sélectionner un rôle',
    'select-role-prompt': 'Sélectionnez un rôle ci-dessus pour configurer ses paramètres de tableau de bord par défaut.',
    'allow-access': 'Autoriser l’accès',
    'edit-thresholds': 'Modifier les seuils',
    'edit-card-color': 'Modifier la couleur de la carte',
    'edit-pmix-products': 'Modifier les produits PMIX',
    'pmix-role-products': 'Produits PMIX (par défaut du rôle)',
    'rule': 'Règle',
    'higher-is-better': 'Plus élevé, c’est mieux',
    'lower-is-better': 'Plus bas, c’est mieux',
    'restore-default': 'Rétablir la valeur par défaut',
    'switch-role-title': 'Changer de rôle ?',
    'switch-role-body': 'Changer de rôle annulera les modifications de configuration en cours. Voulez-vous vraiment continuer ?',
    'unsaved-changes': 'Modifications non enregistrées',
    'unsaved-changes-body': 'Vous avez appliqué des modifications qui n’ont pas été enregistrées. Les ignorer ?',
    'keep-editing': 'Continuer la modification',
    'save-success': 'Paramètres de l’entreprise enregistrés',
    'save-failed': 'Échec de l’enregistrement des paramètres de l’entreprise',
    'copy-to-roles': 'Copier vers d’autres rôles',
    'copy-to-roles-body': 'Copier les paramètres actuellement affichés pour {role} (y compris les modifications non enregistrées) vers les rôles ci-dessous. Leurs paramètres de tableau de bord par défaut seront remplacés.',
    'copy': 'Copier',
    'copy-success': 'Paramètres copiés vers {count} rôle(s)',
    'copy-failed': 'Échec de la copie des paramètres vers : {roles}',
    'dashboard-settings-select-all': 'Tout sélectionner',
    'dashboard-settings-done': 'Terminé',
    'dashboard-settings-preview': 'Aperçu',
    // Migrated from <i18n> SFC blocks (#6500)
    'You currently have': 'Vous avez actuellement',
    'items out in the last': 'articles en rupture au cours des',
    'for': 'pour',
    'product-outage-insight-item': 'article',
    'oepe-w/o-park': 'OEPE hors mise en attente',
    'dt-pulled-fwrd-pct': '% avancés au Drive',
    'snapshot-service-kvs-time-per-gc': 'Temps KVS / GC',
    'snapshot-service-kvs-items-per-gc': 'Articles KVS / GC',
    'snapshot-service-dt-pulled-forward-pct': '% avancés au Drive',
    // Migrated from <i18n> SFC blocks (#6500)
    'custom-options-end-time': 'Heure de fin',
    'custom-options-open': 'Ouvert',
    '12to4': '12 à 4',
    '4to12': '4 à 12',
    'timeslice-dropdown-overnight': 'Nuit',
    'breakfast-desc': 'Ventes petit-déjeuner de la feuille de caisse',
    'total-desc': 'Toutes les ventes',
    'drive-thru-desc': 'Ventes Drive de la feuille de caisse',
    'in-store-desc': 'Comprend le comptoir, la livraison et la borne : tout ce qui n’est pas le Drive',
    'kiosk-desc': 'Ventes initiées à la borne, quel que soit le lieu de paiement',
    'mobile-order-and-pay-desc': 'Ventes initiées dans l’application mobile, quel que soit le lieu de paiement',
    'delivery-desc': 'Total des livraisons 3PO et GMA',
    'sales-group-selector-drive-thru': 'Drive',
    'sales-group-selector-all': 'Tous',
    'sales-group-selector-in-store': 'En restaurant',
    'sales-group-selector-kiosk': 'Borne',
    'sales-group-selector-v2-drive-thru': 'Drive',
    'sales-group-selector-v2-all': 'Tous',
    'sales-group-selector-v2-in-store': 'En restaurant',
    'sales-group-selector-v2-kiosk': 'Borne',
    'custom-options-v2-end-time': 'Heure de fin',
    'custom-options-v2-open': 'Ouvert',
    // Migrated from <i18n> SFC blocks (#6500)
    'header': 'Chaque restaurant dispose d’un sous-ensemble différent d’EVM admissibles aux fonds de partenariat de McDonald’s.',
    'part-1': 'L’EVM doit avoir bénéficié d’une baisse de prix recommandée par Deloitte fin août (dans le canal concerné).',
    'part-2': 'Les unités en restaurant des articles du menu à 5 $/8 $ de septembre, novembre et janvier ne sont pas incluses dans le montant de partenariat pendant la période de promotion.',
    'part-3': 'Les EVM vendus à un prix supérieur à la recommandation de Deloitte peuvent ne pas être admissibles au partenariat',
    'part-4': 'Big Mac / SME : du 8 sept. au 5 oct.',
    'part-5': '10 Pc Nug / SEC McGrd : du 4 au 30 nov.',
    'part-6': '2 Wrps / Sau Egg McMuff : du 6 janv. au 2 févr.',
    'part-7': 'SME / QPC : du 2 au 29 nov. 2026',
    'operator-view': 'Comparaison BU',
    'overview-container-buSummaryContainersDescription': 'La section Comparaison BU permet aux exploitants de comparer leur organisation et chaque restaurant à la moyenne de la BU. Ces conteneurs présentent les données au niveau de la BU dans le même format.',
    // Migrated from <i18n> SFC blocks (#6500)
    'sectioned-dashboard-grid-no-data': 'Aucune donnée',
    'sectioned-dashboard-grid-labor': 'Main-d’œuvre',
    'rank-of-the-store-compared-to-the-whole-bu': 'Classement du restaurant par rapport à l’ensemble de la BU',
    'units-1k-rod-gc': 'Unités/1 000 GC ROD',
    'number-of-menu-items-that-are-sold-related': 'Nombre d’articles du menu liés aux McCrispy Strips vendus sur le reste de la journée (ROD) pour mille unités.',
    'mc-crispy-strips-widget-rank': 'Classement',
    'new-beverage-units': 'Unités nouvelles boissons',
    'new-beverage-watch-list': 'Liste de suivi boissons 2026',
    'new-beverage-widget-new-beverage': 'Boissons de spécialité',
    'new-beverage-widget-units-1k-gc': 'Unités/1 000 GC',
    'new-beverage-widget-number-of-menu-items-that-are-sold-related': 'Nombre d’articles du menu liés à AST Beverage vendus pour mille unités.',
    'new-beverage-widget-rank': 'Classement',
    'rmhc-gc-percentage': '% GC RMHC',
    'rmhc-gc-total-gc': 'GC RMHC / GC total',
    'add-reward-message-link-text': 'Récompensez les employés',
    'add-reward-message-end': 'qui atteignent les objectifs d’arrondi solidaire',
    'rmhc-incentive-widget-premium-upgrade-message-start': 'Passez à Premium :',
    'rmhc-incentive-widget-premium-upgrade-message-end': 'Améliorez l’arrondi solidaire grâce aux récompenses',
    'rmhc-incentive-widget-rank': 'Classement',
    'created-date-header': 'Date de création',
    'incident-date-header': 'Date de l’incident',
    'case-number-header': 'Numéro de cas',
    'type-header': 'Type',
    'customer-name-header': 'Nom du client',
    'recovery-required-header': 'Récupération requise',
    'status-header': 'Statut',
    'resolution-header': 'Résolution',
    'customer-recovery-grid-close': 'fermer',
    'customer-recovery-grid-yes': 'Oui',
    'customer-recovery-grid-no': 'Non',
    'total-number-header': 'Total/#',
    'issue-header': 'Problème',
    'equipment-header': 'Équipements',
    'reported-header': 'Signalés',
    'priority-header': 'Priorité',
    'assignee-header': 'Assigné à',
    'status-reported': 'Signalés',
    'status-accepted': 'Acceptés',
    'status-in-progress': 'En cours',
    'status-pending': 'En attente',
    'status-resolved': 'Résolu',
    'priority-low': 'Faible',
    'priority-medium': 'Moyenne',
    'priority-high': 'Élevé',
    'tickets-grid-close': 'fermer',
    'trailing-365': '365 derniers jours',
    'trailing-7': '7 derniers jours',
    'last-7': '7 derniers jours',
    'last-30': '30 derniers jours',
    'trailing-30': '30 derniers jours glissants',
    't365': '365J',
    'snapshot-container-last-week': 'Semaine dernière',
    'snapshot-container-point-of-order': 'Point de commande',
    'snapshot-container-last-month': 'Mois dernier',
    'snapshot-container-wtd': 'SEC',
    'snapshot-container-mtd': 'CMJ',
    'snapshot-container-ytd': 'CAJ',
    'mop-w-offers': 'MOP avec offres',
    'mop-wo-offers': 'MOP sans offres',
    'digital-app-digital-app': 'Application mobile',
    'digital-app-average-check': 'Ticket moyen',
    'digital-app-trading': 'Exercice commercial',
    'digital-app-loyalty-self-id': 'Auto-identification fidélité',
    'pct-sales': '% des ventes',
    'kiosk-w-offer': 'Borne avec offre',
    'kiosk-w-o-offer': 'Borne sans offre',
    'mcDelivery-w-offer': 'McDelivery avec offre',
    'mcDelivery-w-o-offer': 'McDelivery sans offre',
    'gma-delivery-w-offer': 'Livraison GMA avec offre',
    'gma-delivery-w-o-offer': 'Livraison GMA sans offre',
    'digital-order-points-digital-order-pts': 'Points de commande digitaux',
    'digital-order-points-average-check': 'Ticket moyen',
    'digital-order-points-trading': 'Exercice commercial',
    'digital-order-points-kiosk': 'Borne',
    'below-threshold': 'en dessous du seuil cible inférieur',
    'between-threshold': 'entre les seuils cibles',
    'above-threshold': 'au-dessus des seuils cibles supérieurs',
    'any-order-containing': 'Toute commande comportant un élément Application mobile et/ou Point de commande digital',
    'snapshot-digital-usage-red': 'rouge',
    'snapshot-digital-usage-yellow': 'jaune',
    'snapshot-digital-usage-green': 'vert',
    'snapshot-digital-usage-no-data': 'Aucune donnée',
    'snapshot-digital-usage-digital-usage': 'Usage digital',
    'snapshot-digital-usage-error-loading-data': 'Erreur de chargement des données',
    'agency': 'Agence',
    'local-offer': 'Offre locale',
    'national-offer': 'Offre nationale',
    'non-offer': 'Sans offre',
    'bu-watchlist-no-data': 'Aucune donnée - Soit',
    'bu-watchlist-select-a-bigger-date-range': 'sélectionnez une plage de dates plus large au cours de laquelle ces articles du menu ont été vendus',
    'bu-watchlist-ask-your-agency': 'demandez à votre agence de sélectionner jusqu’à deux articles du menu à afficher ici',
    'bu-watchlist-meal-pct-of-gc': '% du GC total',
    'bu-watchlist-avg-check': 'Ticket moyen',
    'prior': '8/10/2025 - 9/6/2025',
    'extra-value-meal-comp-type-selector-trading': 'Année précédente (jour d’exploitation)',
    'five-and-eight-units-served-rd': 'Unités servies/R/J',
    'five-and-eight-avg-check': 'Ticket moyen',
    'five-and-eight-september': 'Big Mac/SME',
    'five-and-eight-november': '10 Pc Nug/SEC McGrd',
    'five-and-eight-january': '2 Wrps/Sau Egg McMuff',
    'five-and-eight-november-2026': 'QPC/SME',
    'five-and-eight-meal-pct-of-gc': '% des GC',
    'five-and-eight-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'for-more-detail': 'Pour plus de détails, veuillez consulter le',
    'pmix-report': 'rapport PMIX',
    'national-watchlist-no-data': 'Aucune donnée - Soit',
    'national-watchlist-select-a-bigger-date-range': 'sélectionnez une plage de dates plus large au cours de laquelle ces articles du menu ont été vendus',
    'national-watchlist-ask-your-agency': 'demandez à votre agence de sélectionner jusqu’à deux articles du menu à afficher ici',
    'national-watchlist-meal-pct-of-gc': '% du GC total',
    'national-watchlist-avg-check': 'Ticket moyen',
    'national-watchlist-national': 'National',
    'national-watchlist-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'scorecard-ranking': 'Classement du tableau de résultats',
    'show-goal-diff': 'Afficher l’écart à l’objectif',
    'pivot': 'Pivoter',
    'rank-settings': 'Paramètres du classement',
    'score-card-rank-loc': 'Site',
    'score-card-rank-patch': 'Correctif',
    'card-settings': 'Paramètres de la carte',
    'rows': 'Lignes',
    'widget-settings-thresholds': 'Seuils',
    'watchlist-modal-local': 'Offre locale',
    'watchlist-modal-watchlist': 'Liste de suivi',
    'watchlist-modal-filter': 'Filtre',
    // Migrated from <i18n> SFC blocks (#6500)
    'share': 'Partager',
    'preparing_report': 'Préparation de votre rapport...',
    'share_as': 'Partager sous forme de',
    'image': 'Image',
    'share-table': 'Tableau',
    'All Filters': 'Tous les filtres',
    'Clear All': 'Tout effacer',
    'Case Number': 'Numéro de cas',
    'Search Case Number': 'Rechercher un numéro de dossier',
    'Customer Name': 'Nom du client',
    'Search Customer Name': 'Rechercher un nom de client',
    'Status': 'Statut',
    'Recovery Required': 'Récupération requise',
    'Global Performance Matrix': 'Matrice de performance globale',
    'Type': 'Type',
    'ALL FILTERS {count}/{total}': 'TOUS LES FILTRES {count}/{total}',
    'voice-analysis': 'Analyse Voice',
    '1-800-analysis': 'Analyse 1-800',
    'tab-1-800': '1-800',
    'tab-voice': 'VOICE',
    'setup-1800-instructions': 'Pour configurer 1-800, veuillez suivre les instructions',
    'payload-too-large': 'La demande de données est trop volumineuse. Veuillez réduire votre plage de dates ou appliquer des filtres pour afficher les données.',
    'deleted': 'Supprimé',
    'delete-selected': 'Supprimer la sélection ({count})',
    'close-selected': 'Fermer la sélection ({count})',
    'feedback-type-complaint': 'Plainte',
    'feedback-type-praise': 'Éloge',
    'feedback-type-inquiry': 'Demande',
    'complaints-per-100k-gc': 'Plaintes pour 100 000 GC',
    'praise-per-100k-gc': 'Éloges pour 100 000 GC',
    'inquiries-per-100k-gc': 'Demandes pour 100 000 GC',
    'col-quick-close': 'Clôture rapide',
    'col-deleted-by': 'Supprimé par',
    'unknown': 'Inconnu',
    'col-processed-date': 'Date de traitement',
    'col-created-date': 'Date de création',
    'col-incident-date': 'Date de l’incident',
    'col-case-number': 'Numéro de cas',
    'col-type': 'Type',
    'col-customer-name': 'Nom du client',
    'col-recovery-required': 'Récupération requise',
    'col-status': 'Statut',
    'col-resolution': 'Résolution',
    'col-actions': 'Actions',
    'col-created-from': 'Créé depuis',
    'col-case-origin': 'Origine du cas',
    'col-issue-category': 'Catégorie du problème',
    'col-issue-reason': 'Motif du problème',
    'col-issue-subcode': 'Sous-code du problème',
    'col-global-performance-matrix': 'Matrice de performance globale',
    'col-recovery-next-steps': 'Prochaines étapes de récupération',
    'col-recovery-form': 'Formulaire de récupération',
    'col-customer-email': 'E-mail du client',
    'col-customer-phone': 'Téléphone du client',
    'col-location-address': 'Adresse de l\'emplacement',
    'col-order-point': 'Point de commande',
    'col-pickup-point': 'Point de retrait',
    'col-letter-sent': 'Lettre envoyée',
    'col-customer-comments': 'Commentaires du client',
    'add-shift': 'Ajouter un poste',
    'job-title': 'Intitulé du poste',
    'shift-times': 'Horaires du poste',
    'shift-length': 'Durée du poste',
    'breaks': 'Pauses',
    'add-break': 'Ajouter une pause',
    'break-tooltip': 'Les pauses sont planifiées de façon à éviter les heures de repas de pointe et les chevauchements avec les pauses des autres employés.',
    'meal-break': 'Pause repas',
    'rest-break': 'Pause',
    'scheduling-conflicts': 'Notes de planning',
    'add-to-schedule': 'Ajouter au planning',
    'add-shift-dialog-this-week': 'cette semaine',
    'add-shift-dialog-hours-this-week': 'Heures cette semaine',
    'available-employees': 'Employés non planifiés',
    'tooltip-description': 'Employés non planifiés aujourd’hui. Les chiffres indiquent les heures de cette semaine. Cliquez sur un employé pour afficher ses coordonnées. Les lignes surlignées correspondent à des mineurs.',
    'available-employees-error-loading': 'Erreur de chargement des données des employés',
    'available-employees-no-data-available': 'Aucun employé disponible',
    'swap-shift': 'Échanger le poste',
    'current-employee': 'Employé actuel',
    'scheduled-shift': 'Poste planifié',
    'not-working-today': 'Ne travaille pas aujourd’hui',
    'working-description': 'Sélectionnez un employé qui travaille déjà aujourd’hui. Les deux employés échangeront leurs horaires de travail.',
    'not-working-description': 'Sélectionnez un employé disponible pour reprendre ce poste. Le nouvel employé remplacera l’employé initial sur ce poste.',
    'select-employee': 'Sélectionner un employé',
    'their-shift': 'Son poste',
    'will-work': 'travaillera',
    'will-take-over': 'reprendra le poste',
    'additional-notes': 'Notes supplémentaires',
    'notes-placeholder': 'Notes facultatives sur cet échange...',
    'confirm-swap': 'Confirmer l’échange',
    'swap-shift-dialog-hours-this-week': 'Heures cette semaine',
    // Migrated from <i18n> SFC blocks (#6500)
    'dashboard-settings-dashboard-settings': 'Paramètres du tableau de bord',
    'customize-dashboard-containers': 'Personnaliser les conteneurs du tableau de bord',
    'unable-to-save-dashboard-settings': 'Impossible d’enregistrer les paramètres du tableau de bord.',
    'unable-to-reset-dashboard-settings-to-default': 'Impossible de réinitialiser les paramètres du tableau de bord.',
    'shift-dashboard': 'Tableau de bord de poste',
    'disclaimer': 'Les résultats de ventes FO sont affichés après la publication trimestrielle des ventes McDonald’s.',
    'roiOnePager': 'Retour sur investissement',
    'clickToSetup': 'Cliquez pour afficher le rapport ROI des services de conseil',
    'clickToViewMore': 'Cliquez ici pour en voir plus',
    'qsrsoftConsulting': 'Conseil QSRsoft',
    'yourOrganization': 'Votre organisation',
    'heroTitle': 'Vous avez construit quelque chose de précieux.',
    'heroSubtitle': 'Maintenant, protégeons-le — et faisons fructifier ce que vous avez construit.',
    'heroAmountText': '{amount} sortent de votre compte bancaire aujourd’hui',
    'heroShowYou': 'Nous vous montrerons',
    'exactlyWhere': 'exactement où',
    'andHowTo': 'et comment',
    'takeItBack': 'le récupérer',
    'theProblem': 'Le problème',
    'theGuide': 'LE GUIDE',
    'guideSubtitle': 'Près de 100 ans au cœur du système.',
    'excessiveCrewLabor': 'Excédent de main-d’œuvre équipiers',
    'laborText': 'Opportunité de trésorerie sur 6 mois — {amount} par restaurant qui érodent silencieusement votre résultat net.',
    'foodOpportunity': 'Opportunité matières',
    'foodText': 'Comparé à la norme des meilleurs opérateurs - opportunité de {amount} par restaurant.',
    'gcOpportunity': 'Opportunité GC',
    'guestCountText': 'Opportunité liée à l’écart de GC comparables avec la BU',
    'theTransformation': 'La transformation',
    'transformationTitle': 'La transformation — Un retour de {return}. Un ROI de {roi} %+.',
    'basedOnCapturing': 'En ne captant que 25 % de l’opportunité',
    'thePlan': 'LE PLAN — 3 ÉTAPES, 90 JOURS',
    'diagnose': 'Diagnostiquer',
    'diagnoseText': 'Se connecter à vos données QSRSoft. Dresser un portrait précis, restaurant par restaurant et catégorie par semaine, des endroits exacts où la trésorerie s’échappe.',
    'target': 'Cibler',
    'targetText': 'Classer les opportunités à plus forte valeur — main-d’œuvre équipiers, heures supplémentaires, horaires d’ouverture — et construire un plan priorisé AVEC vos directeurs de restaurant.',
    'capture': 'Capter',
    'captureText': 'Accompagner votre équipe dans la mise en œuvre. Suivre la valeur captée en temps réel. Combler l’écart entre votre situation actuelle et celle que vous visez.',
    'whatOperatorsSay': 'CE QU’EN DISENT LES OPÉRATEURS',
    'testimonial1': 'Objectifs de main-d’œuvre équipiers réduits de 4 % sur les douze derniers mois. Trésorerie augmentée de plus de 1 million de dollars.',
    'testimonial2': 'Nous sommes passés de la collecte de données à une amélioration concrète. Des directeurs plus sensibilisés, une administration plus efficace.',
    'testimonial3': 'Des centaines de milliers économisés en main-d’œuvre — tout en augmentant le nombre de clients et en surpassant notre business unit.',
    'franchisee': 'Franchisé McDonald’s',
    'nextStepSimple': 'Votre prochaine étape est simple.',
    'letsTalk': 'Parlons-en',
    'see-all-peers': 'Voir tous les pairs',
    'this-setting-will': 'Ce paramètre permet aux utilisateurs de voir les pairs qui ne relèvent pas directement de leur superviseur. Cela affectera',
    'only': 'uniquement',
    'affect the peers widget': 'le widget des pairs',
    'show-peers-across-hierarchy': 'Afficher les pairs de toute la hiérarchie',
    'hierarchy': 'Hiérarchie',
    'hierarchy-container-locations': 'Sites',
    'hierarchy-grid-no-data': 'Aucune donnée',
    'peers': 'Pairs',
    'avg-sales': 'Ventes moyennes',
    'avg-net-sales': 'Ventes nettes moyennes',
    'oepe-wo-park': 'OEPE hors mise en attente',
    'dt-total-time': 'Temps total Drive',
    'kvs-items-per-gc': 'Articles KVS / GC',
    'healthy-use': 'Utilisation saine',
    'healthy-usage': 'Utilisation saine',
    'kvs-time-per-item': 'Temps KVS / article',
    'peers-sales-kvs-time-per-gc': 'Temps KVS / GC',
    'peers-sales-dt-sales': 'Ventes Drive',
    'peers-sales-dt-average-check': 'Ticket moyen Drive',
    'incentive-grid-no-data': 'Aucune donnée',
    'incentive-grid-location': 'Site',
    'incentive-grid-trend': 'Tendance',
    'select-metric': 'Sélectionner un indicateur',
    'array-cannot-be-empty': 'Le tableau ne peut pas être vide',
    'incentive-trend-trend': 'Tendance',
    '3d-guest-count-club': 'Club nombre de clients 3D',
    'average-daily-guest-counts': 'Nombre moyen de clients par jour',
    'gma-kiosk': 'GMA et borne',
    'gmad-3po': 'GMAD et 3PO',
    'widget-drive-thru': 'Drive',
    'rank-out-of': 'Classement sur',
    'restaurants': 'Restaurants',
    'practice-week': 'Sem. d’entraînement',
    'overall-rank': 'Classement général',
    'created-by': 'Créé par',
    'custom-incentive-week': 'Sem.',
    'incentive-trend': 'Tendance du challenge',
    'bu': 'BU',
    'the-ranking-is-the-same-as': 'Le classement est le même que celui du récent challenge Big Bets. Les données correspondent à la somme de toutes les pointes. Les restaurants doivent être ouverts depuis au moins un an et ne pas avoir été fermés plus d’un jour, cette année comme l’année dernière.',
    'the-overall-rank-is-based-on': 'Le classement général repose sur le calcul des points McD. Il est établi à partir d’un score cumulé du 7/07/2024 jusqu’à hier',
    'gc-day-plus-minus': 'GC/jour +/-',
    'guest-count-per-store-per-day': 'Nombre de clients par restaurant et par jour comparé à l’an dernier',
    'digital-gc-day-plus-minus': 'GC digital/jour +/-',
    'digital-order-points-gc-r-d': 'GC/R/J points de commande digitaux comparé à l’an dernier',
    'oepe-w-o-park': 'OEPE hors mise en attente',
    'order-end-to-present-end': 'De la fin de la commande à la fin de la présentation, hors commandes mises en attente ; temps écoulé entre la fin de la commande du client et la remise de la commande.',
    'oepe-w-o-park-plus-minus': 'OEPE hors mise en attente +/-',
    'oepe-w-o-parked-compared-to-last-year': 'OEPE hors mise en attente comparé à l’an dernier',
    'quality-sat-percent': '% satisfaction qualité',
    'overall-quality-satisfaction-score': 'Score global de satisfaction qualité',
    'default-incentives-widget-rank-out-of': 'Classement sur',
    'incentives': 'Challenges',
    'warm-up-week-1': 'Sem. d’échauffement 1',
    'warm-up-week-2': 'Sem. d’échauffement 2',
    'week-1': 'Sem. 1',
    'week-2': 'Sem. 2',
    'week-3': 'Sem. 3',
    'week-4': 'Sem. 4',
    'week-5': 'Sem. 5',
    'week-6': 'Sem. 6',
    'fall-incentives': 'GOAL-den Momentum',
    'fifa-club-world-cup': 'Coupe du monde de la FIFA 26',
    'overall-rank-based-point-calculation': 'Le classement général repose sur le calcul des points McD. Il est établi à partir d’un score cumulé du 9/21/2025 jusqu’à hier',
    'vs-last-week': '« Vs semaine dernière » indique l’évolution de votre classement général à la clôture du samedi le plus récent',
    'mcd-corporation-calc': 'McDonald’s Corporation calculera le classement final, qui servira à la reconnaissance finale',
    'fifa-upt-definition': '« FIFA UPT +/- » correspond au nombre d’articles du menu FIFA vendus pour 1 000 GC, comparé à l’année précédente',
    'upsized-definition': '« % format supérieur » correspond au pourcentage de menus Big Mac et 10 pièces passés en grand format sur l’ensemble des menus ROD',
    'rmhc-bonus': 'RMHC est un indicateur bonus qui n’ajoute des points que pour les semaines 1-3',
    'oepe-r2p': '* L’indicateur « OEPE » utilise l’OEPE hors mise en attente. Les restaurants sans Drive utilisent le R2P',
    'fall-incentives-widget-rank-out-of': 'Classement sur',
    'round-up': 'Arrondi solidaire',
    'pos': 'Caisse',
    'register-type': 'Zone caisse',
    'round-up-qty-pct': '% qté arrondi solidaire',
    'round-up-by-pos-area-location': 'Site',
    'round-up-by-pos-area-grid-no-data': 'Aucune donnée',
    'rank-up-pos': 'Arrondi solidaire caisse/employé',
    'warm-up-week-3': 'Sem. d’échauffement 3',
    'warm-up-week-4': 'Sem. d’échauffement 4',
    'summer-splash': 'Vague d’été',
    'summer-incentives-widget-overall-rank-based-point-calculation': 'Le classement général repose sur le calcul des points McD. Il est établi à partir d’un score cumulé du 6/29/2025 jusqu’à hier',
    'summer-incentives-widget-oepe-r2p': '* L’indicateur « OEPE ou R2P » utilise l’OEPE hors mise en attente, sauf si le restaurant n’a pas de Drive ; dans ce cas, il s’agit du R2P',
    'summer-incentives-widget-rod': 'Les transactions du reste de la journée (ROD) excluent le petit-déjeuner',
    'summer-incentives-widget-rank-out-of': 'Classement sur',
    'business-dates': 'Date(s) d’activité',
    'number-of-BDC-missing': 'Nb de BDC manquants',
    'bdc-grid-more-info': 'PLUS D’INFORMATIONS',
    'bdc-grid-location': 'Site',
    'missing-at-least': 'auxquels il manque au moins',
    'one-business-day-close': 'une clôture de journée d’activité',
    'bdc-insight-store': 'restaurant',
    'item-num': 'N° d’article',
    'desc': 'Description',
    'used-diff': 'Écart d’utilisation',
    'dollar-diff': 'Écart $',
    'pct-net-sales': '% des ventes nettes',
    'stat-grid-title': 'Détails des pertes de stock',
    'stat-grid-store': 'Site',
    'most-recent-inventory': 'Dernier inventaire',
    'top-loss-items': '5 principaux articles en perte',
    'no-loss-items': 'Aucun article en perte trouvé',
    'raw-waste-amount': 'Pertes brutes $',
    'raw-waste-qty': 'Qté pertes brutes',
    'comp-waste-amount': 'Pertes produits finis $',
    'comp-waste-qty': 'Qté pertes produits finis',
    'missing-type': 'Type manquant',
    'both': 'Les deux',
    'raw-only': 'Brutes uniquement',
    'comp-only': 'Produits finis uniquement',
    'waste-entry-grid-title': 'Détails de saisie des pertes',
    'waste-entry-grid-store': 'Site',
    'stores-missing-raw-waste': 'Restaurants sans saisie des pertes brutes',
    'stores-missing-comp-waste': 'Restaurants sans saisie des pertes produits finis',
    'stores-missing-raw-waste-entry': 'restaurants n’ont pas saisi de pertes brutes au cours des 7 derniers jours',
    'stores-missing-comp-waste-entry': 'restaurants n’ont pas saisi de pertes produits finis au cours des 7 derniers jours',
    'store-missing-raw-waste-entry': 'restaurant n’a pas saisi de pertes brutes au cours des 7 derniers jours',
    'store-missing-comp-waste-entry': 'restaurant n’a pas saisi de pertes produits finis au cours des 7 derniers jours',
    'day-threshold-settings-days': 'Jours',
    'filter-message-open-late': '** Affichage des restaurants ayant ouvert en retard plus souvent que la limite de jours manqués ({daysThreshold}) jours lors des 7 dernières ouvertures.',
    'filter-message-close-early': '** Affichage des restaurants ayant fermé en avance plus souvent que la limite de jours manqués ({daysThreshold}) jours lors des 7 dernières fermetures.',
    'olce-grid-more-info': 'PLUS D’INFORMATIONS',
    'olce-grid-minutes': 'minutes',
    'olce-grid-days-missed-limit': 'La limite de jours manqués peut être ajustée dans les paramètres des analyses.',
    'olce-grid-filter-2': 'Filtre',
    'full-employee-name-header': 'Nom',
    'crew-type-header': 'Type d’équipier',
    'termed-header': 'Sorti des effectifs',
    'hire-date-header': 'Embauche',
    'termination-date-header': 'Départ',
    'merged-terms-more-info-yes': 'oui',
    'merged-terms-more-info-no': 'non',
    'crew-termed-prefix': 'départs d’équipiers au cours des',
    'crew-termed-suffix': 'premiers jours suivant l’embauche.',
    'turnover-prefix': 'La rotation dépasse',
    'turnover-with': 'avec',
    'turnover-suffix': 'départs d’équipiers dans les 90 jours.',
    'with-90-day-turnover-above-prefix': 'dont la rotation à 90 jours dépasse',
    'people-terms-insight-no-30-day-terms': 'Aucun départ d’équipier au cours des 30 premiers jours.',
    'people-terms-insight-location-2': 'site | sites',
    'ot-grid-title': 'Heures sup. potentielles',
    'this-metric-does-not-have-any-goals-set': 'Aucun objectif n’est défini pour cet indicateur',
    'all-goals': 'Tous les objectifs',
    'employees-over-threshold': 'employés risquent de travailler plus de {threshold} heures cette semaine.',
    'ot-threshold-settings-hours': 'Heures',
    'prod-out-grid-all-outages': 'TOUTES LES RUPTURES',
    'prod-out-grid-menu-item-number': 'Numéro d’article du menu',
    'prod-out-grid-active-product-outages': 'Ruptures de produit actives',
    'prod-out-grid-more-info': 'Plus d’informations',
    'watch-lists': 'Listes de surveillance',
    'equipment-tickets': 'Tickets d’équipement',
    'open-seven-days': 'ouverts > 7 jours',
    'customer-recovery': 'Récupération client',
    'open-forty-eight-hours': 'ouverts > 48 heures',
    'training-certificates': 'Certificats de formation',
    'to-expire': 'expirant dans < 30 jours',
    'simplethink-info-title': 'Vous n’êtes actuellement pas abonné à SimpleThink.',
    'simplethink-info-I': 'SimpleThink™ de QSRSoft est un assistant digital tout-en-un qui vous aide à gérer votre restaurant avec davantage de',
    'simplethink-info-II': 'bons',
    'simplethink-info-III': 'contrôles. Des expériences personnalisées et ludiques qui encouragent, récompensent et favorisent les bons comportements.',
    'simplethink-info-IV': 'Optimisez vos opérations et boostez vos équipiers !',
    'simplethink-info-modal-button': 'En savoir plus',
    'tickets-open-seven-days': 'Tickets ouverts > 7 jours',
    'certificates-to-expire': 'Certificats expirant dans < 30 jours',
    'tickets-open-report': 'Tickets ouverts > 7 jours',
    'training-certificates-report': 'Certificats de formation expirant dans < 30 jours',
    'customer-recovery-report': 'Récupération client ouverte > 48 h',
    'simple-think-insight-training': 'Formation',
    'location-header': 'Site',
    'last-name-header': 'Nom de famille',
    'course-name-header': 'Nom du cours',
    'confirmation-id-header': 'ID de confirmation',
    'issue-date-header': 'Date d’émission',
    'expiration-date-header': 'Date d’expiration',
    'bdc-prefix': 'BDC -',
    'instore-events-insight-old-stores-with-alerts': '{count} restaurant(s) avec alertes actives',
    'instore-events-insight-old-stores-with-alerts-suffix': 'restaurant(s) avec alertes actives',
    'instore-events-insight-old-store': 'restaurant',
    'instore-events-insight-old-one': 'une',
    'instore-events-insight-old-more-info': 'Plus d’informations',
    'merged-alerts-more-info-business-dates': 'Date(s) d’activité',
    'merged-alerts-more-info-number-of-BDC-missing': 'Nb de BDC manquants',
    'employees-with-more-than': 'Employés avec plus de ',
    'employee-view-modified-punches': ' pointages modifiés ',
    '%-of-modified-punches-in-last-7-days:': ' Pointages modifiés supérieurs à la moyenne au cours des 7 derniers jours :',
    'store-trend': 'restaurant au-dessus de sa propre tendance | restaurants au-dessus de leur propre tendance',
    'org-trend': 'restaurant au-dessus de la tendance de l’organisation | restaurants au-dessus de la tendance de l’organisation',
    'employee-threshold-part1': 'employé a eu plus de | employés ont eu plus de',
    'employee-threshold-part2': ' pointages modifiés au cours de la dernière période de paie',
    'mod-punches': 'Pointages modifiés',
    'modified-punches-tabs-store-trend': 'Tendance du restaurant',
    'modified-punches-tabs-org-trend': 'Tendance de l’organisation',
    'stores-above-org-trend-in-last-7-days': 'Restaurants au-dessus de la tendance de l’organisation au cours des 7 derniers jours',
    'punches-threshold-settings-punches': 'Pointages',
    'stores-above-store-trend-in-last-7-days': 'Restaurants au-dessus de leur propre tendance au cours des 7 derniers jours',
    'rbd-completions': 'RBD finalisés',
    'rbd-activations': 'Activations RBD',
    'merged-rbd-more-info-store': 'Site',
    'merged-rbd-more-info-more-info': 'Plus d’informations',
    'rbd-completed-grid-store': 'Site',
    'rbd-completed-grid-more-info': 'Plus d’informations',
    'store-has': 'restaurant a',
    'stores-have': 'restaurants ont',
    'or-more': 'ou plus',
    'error-try-again': 'ERREUR - RÉESSAYEZ',
    'rbd-completed-insight-rbd-completions': 'RBD finalisés au cours des 30 derniers jours',
    'rbd-completions-label': 'RBD finalisés',
    'rbd-completions-suffix': 'au cours des 30 derniers jours.',
    'no-rbd-completions': 'Aucun restaurant au-dessus du seuil de RBD finalisés.',
    'store-has-rbd': 'restaurant a au moins un RBD activé au cours des 30 derniers jours mais non finalisé.',
    'stores-have-rbd': 'restaurants ont au moins un RBD activé au cours des 30 derniers jours mais non finalisé.',
    'store-has-prefix': 'restaurant a au moins un',
    'stores-have-prefix': 'restaurants ont au moins un',
    'rbd-activated': 'RBD activé',
    'activated-middle': 'au cours des 30 derniers jours mais',
    'not-completed': 'non finalisé.',
    'no-rbd-activations': 'Aucun restaurant avec des activations RBD en attente.',
    'rbd-insight-rbd-completed': 'Terminé',
    'rbd-insight-rbd-activation': 'Activation',
    'rbd-insight-you-have': 'vous avez',
    'today-beta': 'Aujourd’hui (bêta)',
    'snapshot-digital': 'Aperçu digital',
    'consulting': 'Conseil',
    'bu-comp': 'Comparaison BU',
    'main-security-drill-down': 'Analyse détaillée de la sécurité',
    'main-file-manager': 'Bibliothèque de ressources',
    'main-transaction-analytics': 'Analyses avancées',
    'over-ring': 'Surenregistrement',
    'red-before': 'Réduction avant',
    'red-after': 'Réduction après',
    'total-refund': 'Remboursement total',
    'snapshot-cash-over-short': 'Excédent / manquant',
    'actual-labor-pct': '% MO réelle',
    'crew-labor-pct': '% MO équipiers',
    'punched-labor-pct': 'MO pointée %',
    'avg-rate': 'Taux moyen',
    'total-labor-pct': '% MO totale',
    'ot-$': 'H. sup.',
    'ot-hrs': 'H. sup.',
    'snapshot-link-container-point-of-order': 'Point de commande',
    'avg-chk': 'Ticket moyen',
    'breakfast-gc': 'GC petit-déjeuner',
    'dt-avg-chk': 'Ticket moyen Drive',
    'dt-gcs': 'GC Drive',
    'dt-gcs-qty': 'Qté GC Drive',
    'gcs': 'GC',
    'prod-sales': 'Ventes produits',
    'gcs-qty': 'Qté GC',
    'breakfast-gcs-qty': 'Qté GC petit-déjeuner',
    'comparison': 'Comparaison',
    'showing-comparison-to': 'Comparaison avec',
    'year-before-last': 'Il y a deux ans',
    'snapshot-sales-avg-sales': 'Ventes moyennes',
    'snapshot-sales-breakfast-sales': 'Ventes petit-déjeuner',
    'snapshot-sales-last-year': 'Année dernière',
    'day-parts': 'Plages horaires',
    'pmix-grid-item': 'Article',
    'setup-or-manage-watchlists': 'Configurer ou gérer les listes de suivi',
    'Thru': 'Au',
    'date-options-for-pmix-widget-exclude': 'Les options de date du widget PMIX excluent CAJ, TTM, T365 et toute plage personnalisée supérieure à 31 jours.',
    'please-select-at-least-one': 'Veuillez sélectionner au moins un article du menu ou une liste de suivi pour afficher les données PMIX.',
    'digital-snapshot': 'Aperçu digital',
    'digital-hero-kpis': 'KPI digitaux phares',
    'digital-diagnostics': 'Diagnostic digital',
    'mop-fulfillment': 'Exécution MOP',
    'digital-hero-kpis-2024': 'KPI digitaux phares 2024',
    'snapshot-digital-container-digital-usage': 'Usage digital',
    'snapshot-digital-container-digital-order-pts': 'Points de commande digitaux',
    'snapshot-digital-container-digital-app': 'Application mobile',
    'linked-payment-cnt': 'Nb paiements liés',
    'linked-payment-pct-digital': 'Paiement lié % GC application mobile',
    'linked-payment-pct-total-gc': 'Paiement lié % GC total',
    'gma-delivery-pct': 'Livraison GMA % GC livraison total',
    'snapshot-digital-diagnostics-no-data': 'Aucune donnée',
    'snapshot-digital-diagnostics-error-loading-data': 'Erreur de chargement des données',
    'digital-hero-kpi-definition': 'Aperçu des définitions des KPI digitaux phares',
    'kpi-name': 'Nom du KPI',
    'kpi-definition': 'Définition du KPI',
    'total-digital-percent-of-sales': 'Pourcentage total des ventes digitales',
    'proportion-of-total-sales-digital-channel': 'Part des ventes totales provenant d’un canal digital (application, borne et/ou livraison)',
    'digital-app-percent-of-sales': 'Pourcentage des ventes application mobile',
    'proportion-of-total-sales-digital-app': 'Part des ventes totales provenant de l’application mobile',
    'loyalty-90-day-active-customers': 'Clients fidélité actifs sur 90 jours',
    'number-of-customers-90-days': 'Nombre de clients* ayant effectué au moins une transaction via l’application mobile au cours des 90 jours se terminant le dernier jour de la période sélectionnée',
    'excludes-customers-without-ccpa': '*Cela exclut tous les clients ayant exercé leur droit de retrait au titre du CCPA',
    'average-number-digital-app': 'Nombre moyen de transactions via l’application mobile par restaurant et par jour',
    'new-digital-app-customers-per-30-days': 'Nouveaux clients application mobile par 30 jours',
    'number-of-customers-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction via l’application mobile au cours de la période sélectionnée, divisé par le nombre de jours de la période sélectionnée, multiplié par 30',
    'summarizes-performace-highest-digital-kpis': 'Synthétise la performance des KPI digitaux les plus prioritaires en 2024',
    'click-for-detail': 'Cliquez pour voir le détail',
    'data-for-these-metrics': 'Les données de ces indicateurs vont jusqu’au',
    'number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Nombre de clients* ayant effectué au moins une transaction via l’application mobile au cours de la période sélectionnée',
    'new-digital-app-customers': 'Nouveaux clients application mobile',
    'mcDelivery-percent-of-sales': 'Pourcentage des ventes McDelivery',
    'proportion-of-system-sales': 'Proportion des ventes du système provenant de McDelivery (3PO et GMA)',
    'pct-of-mcDelivery-sales': '% des ventes McDelivery provenant de l’application',
    'proportion-of-all-mcDelivery-sales': 'Proportion de l’ensemble des ventes McDelivery provenant de transactions GMA Delivery',
    'digital-hero2025-number-of-customers-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction sur l’application mobile au cours de la période sélectionnée',
    'this-excludes-customers-opted-out': '*Sont exclus les clients ayant exercé leur droit d’opposition au titre du California Consumer Privacy Act (et qui ne peuvent donc pas participer au programme MyMcDonald\'s Rewards)',
    'note-this-metric-only-gets-displayed': 'Remarque : cet indicateur ne s’affiche qu’au niveau exploitant ou d’un seul restaurant. L’affichage de cet indicateur lorsque plusieurs restaurants sont sélectionnés n’est pas disponible actuellement',
    'number-of-customers-who-had-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction sur l’application mobile au cours de la période sélectionnée, divisé par le nombre de jours de la période sélectionnée, multiplié par 30*',
    'this-metric-normalizes': '*Cet indicateur est normalisé selon le nombre de jours de la période, ce qui permet de comparer plus efficacement les tendances. Par exemple, mars comptera toujours plus de nouveaux clients de l’application mobile que février, car le mois compte plus de jours. En normalisant selon le nombre de jours, cet indicateur permet toutefois de comparer équitablement l’acquisition de nouveaux clients d’un mois à l’autre',
    'digital-hero-rank-trend': 'Tendance',
    'digital-hero-rank-number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Nombre de clients* ayant effectué au moins une transaction sur l’application mobile au cours des 90 jours se terminant le dernier jour de la période sélectionnée',
    'digital-hero-rank-rankings': 'Classements',
    'digital-hero-rank2025-trend': 'Tendance',
    'digital-hero-rank2025-number-of-customers-who-had-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction sur l’application mobile au cours de la période sélectionnée',
    'digital-hero-rank2025-rankings': 'Classements',
    '90-day-active-customer-trend': 'Tendance des clients actifs sur 90 jours',
    'table': 'Tableau',
    'difference': 'Différence',
    'box-target': 'Objectif',
    'box-container-no-data': 'Aucune donnée',
    'snapshot-month-container-food': 'Matières',
    'raw-waste-pct': '% pertes brutes',
    'condiments-pct': '% condiments',
    'base-food-pct': '% matières de base',
    'comp-waste-pct': '% pertes produits finis',
    'discount-pct': '% remises',
    'emp-meals-pct': '% repas employés',
    'stat-variance-pct': '% écart statistique',
    'unexplained-pct': '% inexpliqué',
    'snapshot-month-food-food': 'Matières',
    'sales-and-service': 'Ventes et service',
    'today.retiring-soon-prefix': 'Retrait imminent ! Cliquez',
    'today.here': 'ici',
    'today.retiring-soon-suffix': 'pour passer à la nouvelle vue Aujourd’hui améliorée.',
    'today.why': 'Pourquoi ?',
    'today.tooltip.better-labor': 'Meilleurs rapports de main-d’œuvre',
    'today.tooltip.aligns-dar': 'Aligné sur le rapport DAR',
    'today.tooltip.additional-metrics': 'Indicateurs supplémentaires disponibles pour la personnalisation',
    'today-container-labor': 'Main-d’œuvre',
    'actual-vs-needed': 'Réel vs requis',
    'actual-hrs-vs-needed': 'H. réelles vs requises',
    'actual-vs-scheduled': 'Réel vs planifié',
    'actual-hrs-vs-scheduled': 'H. réelles vs planifiées',
    'sales-per-punched-hr': 'Ventes / h pointée',
    'gcs-per-punched-hr': 'GC / h pointée',
    'today-labor-labor': 'Main-d’œuvre',
    'today-labor-avg-rate': 'Taux moyen',
    'today-labor-punch-labor-pct': 'MO pointée %',
    'product-sales-comp-pct': 'Ventes produits +/- %',
    'average-check-comp-pct': 'Ticket moyen +/- %',
    'gcs-comp-pct': 'GC +/- %',
    'oepe-wo-park-comp': 'OEPE hors mise en attente +/-',
    'oepe': 'OEPE',
    'oepe-comp': 'OEPE +/-',
    'r2p': 'R2P',
    'r2p-comp': 'R2P +/-',
    'kvs-healthy-usage-comp': 'Utilisation saine du KVS +/-',
    'kvs-time-per-gc-comp': 'Temps KVS / GC +/-',
    'today-sales-and-service-kvs-time-per-gc': 'Temps KVS / GC',
    'today-sales-and-service-all-net-sales': 'Ventes nettes totales',
    'today-sales-and-service-all-net-sales-comp-pct': 'Ventes nettes totales +/- %',
    'today-sales-and-service-prod-sales': 'Ventes prod.',
    'today-sales-and-service-average-check': 'Ticket moyen',
    'today-sales-and-service-gcs': 'GC',
    'today-sales-and-service-avg-ctp': 'CTP moyen',
    'today-sales-and-service-avg-ctp-comp': 'CTP moyen +/-',
    'sales-per-punched': 'Ventes / h pointée',
    'gc-per-punched': 'TPPH',
    'today-widget-kvs-time-per-gc': 'Temps KVS / GC',
    'today-widget-labor': 'Main-d’œuvre',
    'today-widget-all-net-sales': 'Ventes nettes totales',
    'today-widget-all-net-sales-comp-pct': 'Ventes nettes totales +/- %',
    'today-widget-prod-sales': 'Ventes prod.',
    'today-widget-average-check': 'Ticket moyen',
    'today-widget-gcs': 'GC',
    'today-widget-dt-sales': 'Ventes Drive',
    'today-widget-avg-dt-ttl': 'TTL Drive moyen',
    'today-widget-kvs-items-per-gc': 'Articles KVS / GC',
    'today-widget-avg-ctp': 'CTP moyen',
    'today-widget-kvs-time-per-item': 'Temps KVS / article',
    'today-widget-avg-rate': 'Taux moyen',
    'today-widget-punch-labor-pct': 'MO pointée %',
    'the-menu-items-in-this-container': 'Les articles du menu de ce conteneur sont sélectionnés par votre agence. Cela vous permet de vous aligner avec votre agence sur les articles du menu les plus importants de vos restaurants et de votre marché. Veuillez adresser vos questions sur le détail de ces articles du menu à votre agence.',
    'bu-menuitems-no-data': 'Aucune donnée - Soit',
    'bu-menuitems-select-a-bigger-date-range': 'sélectionnez une plage de dates plus large au cours de laquelle ces articles du menu ont été vendus',
    'bu-menuitems-ask-your-agency': 'demandez à votre agence de sélectionner jusqu’à deux articles du menu à afficher ici',
    'bu-menuitems-meal-pct-of-gc': '% du GC total',
    'bu-menuitems-avg-check': 'Ticket moyen',
    'date-selector-ok': 'OK',
    'launch-freebie-verbiage': 'Advanced Analytics (TA) a été temporairement activé pour accompagner le lancement de l’initiative EVM du système. Pour continuer à bénéficier d’une visibilité complète sur TA, contactez votre responsable BU afin d’envisager un abonnement.',
    'extra-value-meal-container-extra-value-meal': 'Extra Value Meal',
    'extra-value-meal-container-start-date': 'Date de début',
    'extra-value-meal-container-end-date': 'Date de fin',
    'report-rankings-title': 'Classements {report} - {date}',
    'ta-rank-trend': 'Tendance',
    'ta-trend': 'Tendance TA',
    'spicy-mcmuffins': 'Spicy McMuffins',
    '5-meal-deal': 'Offre repas à 5 $',
    'base-food-decomposition': 'Décomposition des matières de base',
    'transaction-analytics-container-mcCrispy-strips': 'Nouvelle plateforme poulet',
    'transaction-analytics-container-two-year-growth': 'Croissance sur deux ans',
    'transaction-analytics-container-addon-subscribed': 'Option · Abonné',
    'transaction-analytics-container-addon-upgrade': 'Option · Mise à niveau',
    'transaction-analytics-container-addon-upgrade-tooltip': 'Parlez à votre responsable BU de l’ajout d’Advanced Analytics pour :\n1. Accompagnement de l’agence et de la BU via une application d’analyses métier\n2. Accompagnement des O/O via MyQ sur les offres et événements nationaux et BU',
    'transaction-analytics-container-transaction-analytics': 'Advanced Analytics',
    'transaction-analytics-container-boao-for-1': 'BOAO à 1 $',
    'no-options-selected': 'Votre agence n’a encore sélectionné aucune option pour cette business unit. Demandez à votre agence de sélectionner des offres et des articles du menu à afficher ici.',
    'this-and-agency-support': 'Ceci fait partie du service BU Advanced Analytics',
    'talk-to-bu-lead': 'Parlez à votre responsable BU de l’ajout d’Advanced Analytics pour :',
    'agency-and-bu-support': 'Accompagnement de l’agence et de la BU via une application d’analyses métier',
    'oo-support-myq': 'Accompagnement des O/O via MyQ sur les offres et événements nationaux et BU',
    'current-incentives': 'Événements nationaux en cours',
    'transaction-analytics-service-disclaimer-learn-more': 'En savoir plus',
    'net-sales-comp': 'Ventes nettes/R +/-',
    'pr-yr-net-sales-comp': 'Ventes nettes/R an. préc. +/-',
    'gc-comp': 'GC/R +/-',
    'pr-yr-gc-comp': 'GC/R an. préc. +/-',
    'agency-control': 'Contrôle de l’agence',
    'bu-offers-no-data': 'Aucune donnée - Veuillez',
    'bu-offers-meal-pct-of-gc': '% du GC total',
    'bu-offers-avg-check': 'Ticket moyen',
    'base-food-decomp-trend': 'Tendance décomp. matières de base',
    'trend-chart-placeholder': 'Le graphique de tendance sera intégré ici',
    'copied': 'Copié dans le presse-papiers !',
    'ROD': 'ROD',
    'urd': 'U/R/J',
    'extra-value-meal-report-title': 'Extra Value Meal : 15 %',
    'extra-value-meal-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'breakfast-abbr': 'PETIT-DÉJ',
    'five-and-fifteen-five-and-fifteen': '5 %/15 %',
    'five-and-fifteen-units-served-rd': 'Unités servies/R/J',
    'five-and-fifteen-avg-check': 'Ticket moyen',
    'five-and-fifteen-meal-pct-of-gc': '% des GC',
    'five-and-fifteen-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'menu-item-selector': 'Ensemble d’articles du menu',
    'metric-selector-day': 'Par jour',
    'metric-selector-gc': 'Par 1 000 clients',
    'per-day-label': 'Unités vendues/R/J',
    'per-gc-label': 'Unités vendues/1 000 GC',
    'week-range': 'Dim-Sam',
    'trend-prior-no-data': 'Aucune donnée',
    'trend-prior-report-title': 'Tendance hebdomadaire',
    'prior-year': 'Année précédente',
    'trend-trading-no-data': 'Aucune donnée',
    'trend-trading-report-title': 'Tendance hebdomadaire',
    'trend-trading-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'five-meal-deal-weekly-units': 'Les unités hebdomadaires, l’investissement matières et papier et la marge sont des moyennes calculées sur les GC dont la commande inclut une offre repas nationale',
    'mcCrispySandwiches': 'Sandwichs McCrispy',
    'snackWraps': 'Snack Wraps',
    'biscuits': 'Biscuits',
    'hot-honey-meal-pct-of-rod-gc': '% des GC',
    'hot-honey-units-served-1k-rod-gc': 'Unités servies/1 000 GC',
    'mcCrispyStrips': 'McCrispy Strips',
    'this-category': 'Cette catégorie',
    'national-category-trend-no-data': 'Aucune donnée',
    'national-category-trend-report-title': 'Tendance sur 5 semaines',
    'national-rail-row-margin-pct': '% MARGE',
    'as-of': 'Au',
    'what-this-shows': 'Pour les matières premières alimentaires et les condiments utilisés dans les recettes des articles du menu suivis : Écart $ (réel moins théorique, cumulé sur les restaurants sélectionnés) et % ventes prod. (Écart $ absolu rapporté aux ventes produits du mois en cours), du plus grand % au plus petit. Ouvrez la vue de classement pour voir chaque restaurant, la date de son dernier inventaire et l’Écart $, de la plus forte perte au plus fort gain.',
    'national-watchlist-stat-loss-no-data': 'Aucune ligne de perte statistique pour les articles suivis dans les restaurants sélectionnés à cette date de fin',
    'national-watchlist-stat-loss-report-title': 'Perte statistique',
    'national-control': 'National',
    'national-widget-container-if-units-were-only-sold': 'Si les unités n’ont été servies que 5 jours sur 7, le « Jour » de Unités/R/J divise les unités par 5 jours',
    'national-widget-container-mcCrispy-strips': 'Nouvelle plateforme poulet',
    'national-widget-container-watchlist': 'Sélectionner une liste de suivi',
    'national-widget-container-boao-for-1': 'BOAO à 1 $',
    'national-widget-container-agency-menu-items-bevs': 'Articles du menu de l’agence (boissons)',
    'national-widget-container-agency-menu-items-bevs-summary': 'Articles du menu de l’agence (boissons) avec synthèse',
    'meal-pct-of-breakfast-gc': '% des GC petit-déjeuner',
    'units-served-1k-breakfast-gc': 'Unités servies/1 000 GC petit-déjeuner',
    'spicy-mc-muffins-units-served-rd': 'Unités servies/R/J',
    'spicy-mc-muffins-avg-check': 'Ticket moyen',
    'spicy-mc-muffins-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'you-can-only-have': 'Vous ne pouvez pas avoir plus de 10 tableaux de résultats. Veuillez en supprimer un avant d’en créer un nouveau.',
    'click-on-the-cells-to-edit-like-excel': 'Cliquez sur les cellules pour les modifier - comme dans Excel',
    'global-goals': 'Objectifs globaux',
    'the-goals-that-you': 'Les objectifs que vous avez définis s’appliqueront à tous les tableaux de résultats. Vous pouvez modifier ces objectifs dans l’onglet « OBJECTIFS » des paramètres principaux.',
    'do-you-want-to-continue': 'Voulez-vous continuer ?',
    'yes-save-close': 'Oui, enregistrer et créer',
    'create-score-card-create-scorecard': 'Créer un tableau de résultats',
    'create-score-card-name-assign': 'Nommer et assigner',
    'create-score-card-qsr-defaults': 'Valeurs par défaut QSR',
    'create-score-card-defaults': 'Valeurs par défaut',
    'no-goals-set': 'Aucun objectif défini',
    'scorecard-name': 'Nom du tableau de résultats',
    'assigning-roles-to': 'Assigner des rôles à un tableau de résultats permet de le rendre visible uniquement aux utilisateurs disposant de ces rôles',
    'create-copy': 'Créer une copie',
    'score-card-container-settings-scorecard-settings': 'Paramètres du tableau de résultats',
    'score-card-container-settings-scorecards-2': 'Tableaux de résultats',
    'goals-per-location': 'Objectifs par site',
    'all-locs': 'Tous les sites',
    'goal-grid-locations': 'Sites',
    'row-order': 'Ordre des lignes',
    'tab-date-selector-ok': 'OK',
    'customize-metrics': 'Personnaliser les indicateurs',
    'row': 'Ligne',
    'thresholds': 'Seuils',
    'healthy-limit': 'Limite saine',
    'under': 'Sous',
    'between': 'Entre',
    'upper': 'Haut',
    'lower': 'Bas',
    'threshold-settings-over': 'Au-dessus',
    'no-shared-links': 'Aucun lien partagé n\'a été ajouté pour votre organisation.',
    'that-need-attention': 'qui requièrent votre attention',
    'manage-requests': 'Gérer les demandes',
    'requests': 'demandes',
    'employee-requests-insight-request': 'demande',
    'employee-requests-insight-you-have': 'Vous avez',
    // BUOverview chart series; only the -title key existed
    'two-year-growth': 'Croissance sur 2 ans',
    // Migrated from <i18n> SFC blocks (#6500)
    'voice-rankings-voice-rankings': 'Classements Voice',
    'financial-is-used-for-current-mid-month': 'Le mode Financier s’applique au mois en cours ou aux plages de dates dépassant les mois clôturés. Il exclut les sites ouverts depuis moins de 13 mois complets. Par défaut, le mode Financier utilise la comparaison par jour d’exploitation.',
    'analytic-is-used-for-completed-months': 'Le mode Analytique s’applique aux mois clôturés et aux plages de dates incluant des mois clôturés. Il applique une règle de 5 jours : si un site n’a pas de ventes pendant 5 jours ou plus (sur l’année en cours ou l’année précédente), il est exclu du calcul. Par défaut, le mode Analytique utilise la comparaison par jour calendaire.',
    'confirmDeletion': 'Confirmer la suppression',
    'areYouSure': 'Voulez-vous vraiment supprimer cette liste de suivi ?',
    'businessUnitMenuItemDetails': 'Détails des articles du menu national',
    'watchlist': 'Liste de suivi',
    'subtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Aucune synthèse ni aucun total ne sera calculé sur l’ensemble des listes de suivi.',
    'widgetLabel': 'Titre du widget',
    'national-menu-items': 'Articles du menu national',
    'widgetDialogTitle': 'Modifier le titre du widget',
    'widgetTitleDisclaimer': 'Créez une liste de suivi avant de définir le titre',
    'watchlistName': 'Nom de la liste de suivi',
    'non_offer': 'Sans offre',
    'national': 'Offre nationale',
    'availableMenuItems': 'Articles du menu disponibles',
    'noMatchingMenuItems': 'Aucun article du menu correspondant trouvé.',
    'showAll': 'Tout afficher',
    'showSelected': 'Afficher la sélection',
    'saveWatchlist': 'Enregistrer la liste de suivi',
    'clear': 'Effacer',
    'widgetTitleLabel': 'Titre du widget (s’applique à toutes les listes de suivi)',
    'construction-watchlist-modal-local': 'Offre locale',
    'construction-watchlist-modal-watchlist': 'Liste de suivi',
    'construction-watchlist-modal-filter': 'Filtre',
    'bumid-businessUnitMenuItemDetails': 'Détails des articles du menu de la business unit',
    'menu-item-cell-selectMenuItem': 'Sélectionner un article du menu',
    'menu-item-modal-selectMenuItem': 'Sélectionner un article du menu',
    'menu-item-modal-availableMenuItems': 'Articles du menu disponibles',
    'businessUnitOffers': 'Offres de la business unit',
    'selectOffersInstructions': 'Sélectionnez jusqu’à deux offres groupées automatiques/offres mobiles par BU à afficher aux O/O dans la section TA du tableau de bord d’accueil MyQSRSoft.',
    'promotionMembers': 'Éléments de la promotion',
    'selectOffer': 'Sélectionner une offre',
    'transactionAnalytics': 'Advanced Analytics',
    'hideWidget': 'Masquer',
    'showWidget': 'Afficher',
    'offers': 'Offres',
    'example-widget-businessUnit': 'BU',
    'availableOffers': 'Offres disponibles',
    'offerDetails': 'Détails de l’offre',
    'selectOfferToViewDetails': 'Sélectionnez une offre pour afficher les détails',
    'promo-selector-back': 'Retour',
    'bu-grid-noData': 'Aucune donnée',
    'operatorDashboard': 'Tableau de bord vue exploitant',
    'operatorDashboardDescription': 'Consultez ce que les exploitants voient au quotidien. Pour une analyse approfondie et les tendances, sélectionnez l’outil',
    'businessIntelligence': 'Analyses métier',
    'tool': '.',
    'mcCrispy-strips': 'McCrispy Strips',
    'loyalty-90-day-active-trend': 'Tendance des actifs fidélité sur 90 jours',
    'agency-menu-items': 'Articles du menu de l’agence',
    'agency-menu-items-with-summary': 'Articles du menu de l’agence avec synthèse',
    'agency-menu-items-bevs': 'Articles du menu de l’agence (boissons)',
    'agency-menu-items-bevs-summary': 'Articles du menu de l’agence (boissons) avec synthèse',
    'five-and-eight': '$5/$8',
    'five-and-fifteen': '$5/$15',
    'trend': 'Tendance',
    'extra-value-meal-15': 'Extra Value Meal : 15 %',
    'extra-value-meal-5': 'Extra Value Meal : 5 %',
    'agency-offers': 'Offres de l’agence',
    'f-and-p-investment': 'Investissement matières et papier',
    'bu-overview-voice': 'Voice',
    'bu-overview-food': 'Matières',
    'bu-overview-digital-app': 'Application mobile',
    'bu-overview-labor': 'Main-d’œuvre',
    'bu_loyalty_90_day_active_trend': 'Tendance des actifs fidélité BU sur 90 jours',
    'bu_loyalty_90_day_active_trend_info': 'Nombre de clients uniques ayant enregistré au moins un GC via l’application mobile sur la période de 90 jours. Un client pouvant fréquenter plusieurs restaurants d’une même organisation, ce nombre ne peut pas être additionné entre restaurants.',
    'widget_disabled_info': 'Ce widget ne fonctionne que pour la BU',
    'bu_digital_app': 'Application mobile BU',
    'bu_digital_order_pts': 'Points de commande digitaux',
    'bu_food': 'Matières',
    'bu_labor': 'Main-d’œuvre',
    'bu_sales': 'Ventes',
    'bu_sales_financial': 'Le mode Financier s’applique au mois en cours ou aux plages de dates dépassant les mois clôturés. Il exclut les sites ouverts depuis moins de 13 mois complets. Par défaut, le mode Financier utilise la comparaison par jour d’exploitation.',
    'bu_sales_analytic': 'Le mode Analytique s’applique aux mois clôturés et aux plages de dates incluant des mois clôturés. Il applique une règle de 5 jours : si un site n’a pas de ventes pendant 5 jours ou plus (sur l’année en cours ou l’année précédente), il est exclu du calcul. Par défaut, le mode Analytique utilise la comparaison par jour calendaire.',
    'bu_service': 'Service',
    'people_metrics_info': 'Les indicateurs RH sont calculés sur des mois complets.',
    'people_metrics_multiple_months': 'Si plusieurs mois sont sélectionnés, le mois complet le plus récent est utilisé.',
    'people_metrics_partial_month': 'Si un mois passé est partiellement sélectionné, le mois complet est utilisé.',
    'people_metrics_detailed_breakdown': 'Pour un détail complet, consultez le rapport BU Rank.',
    'peopleDataRequiresFullMonth': 'Les données RH exigent une plage de dates couvrant un mois complet',
    'voiceDataTooRecent': 'Les données Voice accusent généralement 2 jours de retard. Veuillez sélectionner une plage de dates antérieure',
    'widgetOnlyWorksForBU': 'Ce widget ne fonctionne que pour la BU',
    'bu-voice-noData': 'Aucune donnée',
    'boao-for-1': 'BOAO à 1 $',
    'business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Advanced Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'transaction-level-data-updates': 'Les données au niveau des transactions de la veille sont mises à jour au cours de la journée. Veuillez noter que les résultats peuvent être incomplets en début de journée.',
    'units-sold-days': 'Si les unités n’ont été vendues que 5 jours sur 7, le « Jour » de Unités/R/J divise les unités par 5 jours',
    'rod': 'ROD',
    'meal-pct-of-gc': '% des GC',
    'units-served-rd': 'Unités servies/R/J *',
    'units-served-1k-gc': 'Unités servies/1 000 GC',
    'avg-margin-amt': 'Montant moyen de la marge',
    'margin-pct': '% marge',
    'bogo-avg-check': 'Ticket moyen',
    'chart-subtitle': 'Données pour la période',
    'units-per-thousand': 'Unités servies/1 000 GC',
    'bu-category-trend-no-data': 'Aucune donnée',
    'bu-category-trend-report-title': 'Tendance sur 5 semaines',
    'units-1k-gc': '/1 000 GC',
    'bu-rail-row-margin-pct': '% MARGE',
    'no-items-found': 'Aucun article trouvé',
    'bu-watchlist-section-watchlist': 'Sélectionner une liste de suivi',
    'meal-deal': 'Offre repas',
    'weekly-units': 'Les unités hebdomadaires, l’investissement matières et papier et la marge sont des moyennes calculées sur les GC dont la commande inclut une offre repas nationale',
    'happy-meal-toy': 'Le coût du jouet Happy Meal correspond à la valeur définie sur la touche 846. Si elle est vide, nous utilisons 0,60 $',
    'explain-rod': 'ROD signifie « reste de la journée » et exclut la période du petit-déjeuner (4 h-11 h)',
    'meal-pct-of-rod-gc': '% des GC ROD',
    'units-served-1k-rod-gc': 'Unités servies/1 000 GC ROD',
    'five-meal-deal-units-served-rd': 'Unités servies/R/J',
    'five-meal-deal-avg-check': 'Ticket moyen',
    'promo-&-discount': 'Promo et remise matières et papier',
    'discount-f&p': 'Remise matières et papier = le % de remise de l’article ou des articles du menu appliqué au coût matières et papier de ces articles. Montant de la remise matières et papier.',
    'promo-f&p': 'Promo matières et papier = le coût matières et papier de l’article ou des articles du menu en promotion.',
    'base-food-plus-promo-discount': 'Matières de base + matières et papier promos et remises',
    'base-food': 'Matières de base',
    'check-the': 'Consultez la page ',
    'pmix-page': 'PMIX',
    'for-further-info': ' pour plus d’informations sur Hot Honey',
    'mc-crispy-strips': 'McCrispy Strips',
    'hot-honey-business-units-not-signed-up': 'Une ou plusieurs de vos business units ne sont pas inscrites à Transaction Analytics. Les sites de ces business units ne seront pas inclus dans le rapport. Veuillez contacter votre responsable BU pour plus d’informations.',
    'hot-honey-units-served-rd': 'Unités servies/R/J',
    'hot-honey-avg-check': 'Ticket moyen',
    'hot-honey-report-title': 'Hot Honey',
    'hot-honey-meal-pct-of-gc': '% des GC',
    'mc-crispy-strips-for-further-info': ' pour plus d’informations sur les Wrap Combos',
    'mc-crispy-strips-units-served-rd': 'Unités servies/R/J',
    'mc-crispy-strips-avg-check': 'Ticket moyen',
    'mc-crispy-strips-report-title': 'Nouvelle plateforme poulet',
    'the-offers-in-this-container': 'Les offres de ce conteneur sont sélectionnées par votre agence. Cela vous permet de vous aligner avec votre agence sur les offres les plus importantes en cours dans vos restaurants et sur votre marché. Veuillez adresser vos questions sur le détail de ces offres à votre agence.',
    'if-units-were-only-sold': 'Si les unités n’ont été vendues que 5 jours sur 7, le « Jour » de Unités/R/J divise les unités par 5 jours',
    'select-a-bigger-date-range': 'sélectionner une plage de dates plus large au cours de laquelle cette offre a eu lieu',
    'ask-your-agency': 'demander à votre agence de sélectionner deux offres (promotions ou remises) à afficher ici',
    'offers-no-data': 'Aucune donnée - Veuillez',
    'offers-meal-pct-of-gc': '% du GC total',
    'offers-avg-check': 'Ticket moyen',
    'buSummaryContainersDescription': 'Advanced Analytics aligne les agences, McD et les exploitants sur les événements majeurs. Les exploitants des BU abonnées voient ces conteneurs avec les données de leurs seules organisations, tandis que les agences les voient avec des informations au niveau de la BU.',
    'extra-value-meal': 'Extra Value Meal',
    'two-year-growth-title': 'Croissance sur 2 ans de {type}',
    'weekly-trading-comparison': 'Comparaison hebdomadaire de l’activité. Logique des ventes du mode Financier appliquée.',
    'excluded-stores': 'Les restaurants sans ventes cette année ou l’an dernier sont exclus.',
    'watchlist-details-businessUnitMenuItemDetails': 'Détails des articles du menu de la business unit',
    'watchlist-details-subtitle': 'Sélectionnez jusqu’à cinq listes de suivi par BU à afficher aux O/O dans la section TA du tableau de bord d’accueil QSRSoft. Aucune synthèse ni aucun total ne sera calculé sur l’ensemble des listes de suivi.',
    'custom-request-form-loading': 'Chargement',
    'failed-message': 'Échec du chargement du tableau de bord QuickSight',
    'new-extract': 'Nouvelle extraction',
    'extracts': 'Extractions',
    'report-finder': 'Recherche de rapports',
    'report': 'Rapport',
    // Migrated from <i18n> SFC blocks (#6500)
    'Su': 'Di',
    'M': 'Lu',
    'Tu': 'Ma',
    'W': 'Me',
    'Th': 'Je',
    'F': 'Ve',
    'Sa': 'Sa',
    'day-type-calendar': 'Calendrier',
    'day-type-business-date': 'Date d’activité',
    'pay-period': 'Période de paie',
    'start-and-end-date-correspond': '* Les dates de début et de fin correspondent aux cycles de paie',
    'pay-period-selector-multi-end-date': 'Date de fin',
    'pay-period-selector-multi-v2-end-date': 'Date de fin',
    'quick-dates-v3-yesterday': 'HIER',
    'quick-dates-v3-last-month': 'MD',
    'quick-dates-v3-last-year': 'AD',
    'quick-dates-v3-month-to-date': 'CMJ',
    'quick-dates-v3-year-to-date': 'CAJ',
    'quick-dates-v3-trailing-365-days': '365J',
    'bu-scope-selector-scope': 'Portée',
    'business-unit': 'Business unit',
    'location-agg-and-location': '& site',
    'location-agg-v2-by-location': 'Par site',
    'same-store-comp-all-store-comp-2': 'Comparaison tous restaurants',
    'same-store-comp-same-store-comp-2': 'Comparaison à périmètre constant',
    'only-active-stores': 'Les données sont limitées aux restaurants actifs uniquement.',
    'daypart-dropdown-v2-all': 'Tous',
    'peak-dropdown-v2-required': 'Les périodes de la journée sont obligatoires',
    'peak-dropdown-v2-all': 'TOUS',
    'Time Segment': 'Tranche horaire',
    'Peaks': 'Pics',
    'Dayparts': 'Plages horaires',
    'Timeslices': 'Plages horaires',
    'Custom': 'Personnalisé',
    'selected': 'sélectionné(s)',
    'timeslice-dropdown-required': 'Les périodes de la journée sont obligatoires',
    'timeslice-dropdown-all': 'Tous',
    'timeslice-dropdown-v2-required': 'Les périodes de la journée sont obligatoires',
    'timeslice-dropdown-v2-all': 'Tous',
    'select-report': 'Sélectionner un rapport',
    'view-all-scheduled-reports': 'Voir tous les rapports planifiés',
    'report-schedule-schedule': 'Planning',
    'schedule-reports': 'Planifier des rapports',
    'daily-reports-always-retrieve-data-from-yesterday': 'Les rapports quotidiens récupèrent toujours les données de la veille',
    'weekly-reports-always-retrieve-data-from-the-previous-week': 'Les rapports hebdomadaires récupèrent toujours les données de la semaine précédente.',
    'monthly-reports-always-retrieve-data-from-the-previous-month': 'Les rapports mensuels récupèrent toujours les données du mois précédent.',
    'frequency': 'Fréquence',
    'recipients': 'Destinataires',
    'invalid-email': 'Courriel invalide',
    'recipients-selector-required': 'Obligatoire',
    'day-of-month': 'Jour du mois',
    'time-selector-day-of-week': 'Jour de la semaine',
    // Migrated from <i18n> SFC blocks (#6500)
    'register-type-selector-register': 'Type de caisse',
    'select-date': 'Sélectionner une date',
    'tomorrow': 'Demain',
    'two-days-ago': 'Il y a 2 jours',
    'quick-dates-last-week': 'SD',
    'next-week': 'SP',
    'quick-dates-this-week': 'SC',
    'quick-dates-this-month': 'MC',
    'next-month': 'MP',
    'week-to-date': 'SEC',
    '24-months': '24 MOIS',
    '36-months': '36 MOIS',
    'quick-dates-yesterday': 'HIER',
    'quick-dates-last-month': 'MD',
    'quick-dates-last-year': 'AD',
    'quick-dates-month-to-date': 'CMJ',
    'quick-dates-year-to-date': 'CAJ',
    'quick-dates-trailing-365-days': '365J',
    'quick-dates-v2-yesterday': 'HIER',
    'quick-dates-v2-last-month': 'MD',
    'quick-dates-v2-last-year': 'AD',
    'quick-dates-v2-month-to-date': 'CMJ',
    'quick-dates-v2-year-to-date': 'CAJ',
    'quick-dates-v2-trailing-365-days': '365J',
    'sum-all-locations-long': 'Synthèse',
    'by': 'Par',
    'and-location': '& site',
    'location-agg-sum-all-locations': 'Synthèse',
    'location-agg-by-location': 'Par site',
    'same-store-comp-all-store-comp': 'Comparaison tous restaurants',
    'same-store-comp-same-store-comp': 'Comparaison à périmètre constant',
    'daypart-dropdown-all': 'Tous',
    'by-day': 'Par jour',
    'by-timeslice': 'Par plage(s) horaire(s)',
    'peak-dropdown-required': 'Les périodes de la journée sont obligatoires',
    'peak-dropdown-all': 'TOUS',
    'label': 'Afficher le segment par',
    'timeslice-submenu-label': 'Plage(s) horaire(s)',
    'segment-by-dropdown-by-time-slice': 'Par plage(s) horaire(s)',
    // Generic actions
    reset: 'Réinitialiser',
    'discard': 'Ignorer',

    // Empty / error states
    'no-data': 'Aucune donnée',
    'an-error-occurred-while-loading-the-report':
      'Une erreur s’est produite lors du chargement du rapport. Veuillez réduire le nombre de restaurants ou la plage de dates. Si le problème persiste, veuillez communiquer avec le soutien.',

    // Filter containers
    filters: 'Filtres',
    'please-select-at-least-one-location':
      'Veuillez sélectionner au moins un emplacement',
    'please-select-a-scope': 'Veuillez sélectionner une portée',
    'please-select-a-date-range': 'Veuillez sélectionner une plage de dates',
    'please-select-a-time-segment': 'Veuillez sélectionner un segment horaire',
    'please-select-please-select-at-least-one-daypart':
      'Veuillez sélectionner au moins une période de la journée',
    'please-select-please-select-at-least-one-time-slice':
      'Veuillez sélectionner au moins une plage horaire',
    'please-select-please-select-at-least-one-peak':
      'Veuillez sélectionner au moins une pointe',
    'the-following-restaurants-are-not-included':
      'Les restaurants suivants ne sont pas inclus dans ce rapport, car ils ne figurent pas dans votre profil d’utilisateur.',
    'if-you-are-logged-in-with-eid':
      'Si vous êtes connecté avec un eID, votre liste de restaurants doit être corrigée dans GAM. Une fois les restaurants ajoutés, vous devrez vous déconnecter puis vous reconnecter pour que QSRSoft récupère la liste à jour. Les modifications apportées dans GAM peuvent prendre jusqu’à 48 heures pour se propager à notre système.',
    'if-you-are-logged-in-with-email':
      'Si vous êtes connecté avec une adresse courriel, transmettez la demande à votre administrateur système afin qu’il ajoute les restaurants à votre profil. Vous devrez actualiser votre navigateur une fois la modification effectuée.',

    // Loading / retry
    retrying: 'Nouvelle tentative...',

    // Grid container
    'expand-rows': 'Développer les lignes',

    // Chart container
    overlap: 'Superposer',
    showChart: 'Afficher le graphique',
    hideChart: 'Masquer le graphique',
    // Own key: 'timeslices' resolves to other text app-wide (#6335)
    'timeslice-dropdown-timeslices': 'Plages horaires',
    // Own key: 'first-name' resolves to other text app-wide (#6335)
    'minor-exceptions-first-name': 'Prénom',
    // Own key: 'timeslice' resolves to other text app-wide (#6335)
    'custom-incentive-timeslice': 'Plage horaire',
    // McDonald's dashboard tabs (oldDashboard/mcd/main.vue)
    'transaction-analytics': 'Advanced Analytics',
  },
  'fr-ca': {
    // Migrated from <i18n> SFC blocks (#6500)
    'buOverview': 'Vue exploitant',
    'quick-sight-insights': 'Analyses d’affaires',
    'offer': 'Détails de l’offre de l’unité d’affaires',
    'buWatchlistDetails': 'Détails des articles du menu de l’unité d’affaires',
    'buWatchlistDetailsWithSummary': 'Détails des articles du menu de l’unité d’affaires avec sommaire',
    'buWatchlistDetailsHeader': 'Détails des articles du menu de l’unité d’affaires',
    'buWatchlistDetailsSubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par unité d’affaires à afficher aux O/O dans la section TA du tableau de bord d’accueil QSRSoft. Aucun sommaire ni total ne sera calculé pour l’ensemble des listes de suivi.',
    'buWatchlistDetailsWithSummaryHeader': 'Articles du menu de l’agence avec sommaire pour l’ensemble des listes de suivi',
    'buWatchlistDetailsWithSummarySubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par unité d’affaires à afficher aux O/O dans la section TA du tableau de bord d’accueil QSRSoft. Le widget affichera chaque liste de suivi ainsi qu’un sommaire pour l’ensemble des listes. Tous les articles d’une liste de suivi doivent appartenir à la même plateforme, par ex. Boissons d’été ou Offres repas.',
    'nationalAAIncentiveBuilder': 'Générateur d’incitatifs AA national',
    'nationalMenuItemDetails': 'Détails',
    'nationalMenuItemDetailsWithSummary': 'Détails avec sommaire',
    'nationalMenuItemDetailsHeader': 'Détails des articles du menu national',
    'nationalMenuItemDetailsSubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Aucun sommaire ni total ne sera calculé pour l’ensemble des listes de suivi.',
    'nationalMenuItemDetailsWithSummaryHeader': 'Articles du menu national avec sommaire pour l’ensemble des listes de suivi',
    'nationalMenuItemDetailsWithSummarySubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Le widget affichera chaque liste de suivi ainsi qu’un sommaire pour l’ensemble des listes. Tous les articles d’une liste de suivi doivent appartenir à la même plateforme, par ex. Boissons d’été ou Offres repas.',
    'nationalMenuItemDetailsBevs': 'Détails (boissons)',
    'nationalMenuItemDetailsBevsHeader': 'Détails des articles du menu national (boissons)',
    'nationalMenuItemDetailsBevsSubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Aucun sommaire ni total ne sera calculé pour l’ensemble des listes de suivi.',
    'nationalMenuItemDetailsBevsWithSummary': 'Détails (boissons) avec sommaire',
    'nationalMenuItemDetailsBevsWithSummaryHeader': 'Articles du menu national (boissons) avec sommaire pour l’ensemble des listes de suivi',
    'nationalMenuItemDetailsBevsWithSummarySubtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Le widget affichera chaque liste de suivi ainsi qu’un sommaire pour l’ensemble des listes. Tous les articles d’une liste de suivi doivent appartenir à la même plateforme.',
    // Migrated from <i18n> SFC blocks (#6500)
    'Sunday': 'Dimanche',
    'Monday': 'Lundi',
    'Tuesday': 'Mardi',
    'Wednesday': 'Mercredi',
    'Thursday': 'Jeudi',
    'Friday': 'Vendredi',
    'Saturday': 'Samedi',
    'day-of-week-days': 'jours',
    'day-of-week-all': 'Tous',
    'day-of-week-v2-days': 'jours',
    'day-of-week-v2-all': 'Tous',
    'loading-range': 'Chargement de {range}',
    'suspicious-activity-insight-data-failed-to-load': 'Le chargement des données a échoué. Veuillez réessayer ou contacter le support si le problème persiste.',
    'suspicious-activity-insight-more-info': 'Plus D\'Informations',
    'suspicious-activity-insight-please-subscribe': 'Veuillez Vous Abonner',
    'suspicious-activity-insight-learn-more': 'En Savoir Plus',
    'security-drill-down-security-drill-down': 'Analyse détaillée de la sécurité',
    'hello-world': 'Calcul des aliments sur base fondé sur l’utilisation PMIX ; il peut différer légèrement du calcul Caisse et Inventaire',
    'drivers-add-up': 'PMIX, Prix et Coût des matières premières additionnés donnent la variation du % aliments de base par rapport à la période de comparaison.',
    'drivers': 'Ce qui l’a fait varier',
    'fob-change': 'Aliments de base vs M. préc.',
    'vs-pr-mth': 'vs M. préc.',
    'adds-up-to': 'totalise {value}',
    'colour-key': 'Le vert baisse le coût des aliments, le rouge l’augmente.',
    'raw-cost': 'Coût des matières premières',
    'pricing': 'Prix',
    'pmix': 'PMIX',
    'rbd-events': 'RBD',
    'rbd-tooltip': 'Les RBD peuvent fausser les données de répartition des produits et, par conséquent, les coûts des aliments présentés.',
    'rbd-store-line': 'Restaurant {store} : {count} jour(s) RBD',
    'base-food-decomp-widget-base-food-decomposition': 'Analyse des aliments de base',
    'base-food-decomp-widget-fob': 'Aliments de base',
    'base-food-decomp-widget-disclaimer': 'Analyser ce qui fait varier les aliments de base est essentiel pour comprendre la performance d’un événement, mais ce n’est pas tout. Les variations des promos et rabais ainsi que la croissance sur 2 ans des ventes et des GC sont aussi nécessaires pour une vue analytique complète.',
    'base-food-decomp-widget-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'day-of-week-v3-days': 'jours',
    'day-of-week-v3-all': 'Tous',
    'role-defaults': 'Paramètres par défaut du rôle',
    'enterprise-settings': 'Paramètres de l’entreprise*',
    'enterprise-help': 'Décochez ce que cette entreprise n’utilise pas. Les éléments décochés sont masqués partout et ne peuvent pas être activés par les paramètres par défaut des rôles.',
    'select-role': 'Sélectionner un rôle',
    'select-role-prompt': 'Sélectionnez un rôle ci-dessus pour configurer ses paramètres de tableau de bord par défaut.',
    'allow-access': 'Autoriser l’accès',
    'edit-thresholds': 'Modifier les seuils',
    'edit-card-color': 'Modifier la couleur de la carte',
    'edit-pmix-products': 'Modifier les produits PMIX',
    'pmix-role-products': 'Produits PMIX (par défaut du rôle)',
    'rule': 'Règle',
    'higher-is-better': 'Plus élevé, c’est mieux',
    'lower-is-better': 'Plus bas, c’est mieux',
    'restore-default': 'Rétablir la valeur par défaut',
    'switch-role-title': 'Changer de rôle?',
    'switch-role-body': 'Changer de rôle annulera les modifications de configuration en cours. Voulez-vous vraiment continuer?',
    'unsaved-changes': 'Modifications non enregistrées',
    'unsaved-changes-body': 'Vous avez appliqué des modifications qui n’ont pas été enregistrées. Les ignorer?',
    'keep-editing': 'Continuer la modification',
    'save-success': 'Paramètres de l’entreprise enregistrés',
    'save-failed': 'Échec de l’enregistrement des paramètres de l’entreprise',
    'copy-to-roles': 'Copier vers d’autres rôles',
    'copy-to-roles-body': 'Copier les paramètres actuellement affichés pour {role} (y compris les modifications non enregistrées) vers les rôles ci-dessous. Leurs paramètres de tableau de bord par défaut seront remplacés.',
    'copy': 'Copier',
    'copy-success': 'Paramètres copiés vers {count} rôle(s)',
    'copy-failed': 'Échec de la copie des paramètres vers : {roles}',
    'dashboard-settings-select-all': 'Tout sélectionner',
    'dashboard-settings-done': 'Terminé',
    'dashboard-settings-preview': 'Aperçu',
    // Migrated from <i18n> SFC blocks (#6500)
    'You currently have': 'Vous avez actuellement',
    'items out in the last': 'articles en rupture au cours des',
    'for': 'pour',
    'product-outage-insight-item': 'article',
    'oepe-w/o-park': 'OEPE sans stationnement',
    'dt-pulled-fwrd-pct': '% devancés au service au volant',
    'snapshot-service-kvs-time-per-gc': 'Temps KVS / GC',
    'snapshot-service-kvs-items-per-gc': 'Articles KVS / GC',
    'snapshot-service-dt-pulled-forward-pct': '% devancés au service au volant',
    // Migrated from <i18n> SFC blocks (#6500)
    'custom-options-end-time': 'Heure de fin',
    'custom-options-open': 'Ouvert',
    '12to4': '12 à 4',
    '4to12': '4 à 12',
    'timeslice-dropdown-overnight': 'Nuit',
    'breakfast-desc': 'Ventes du déjeuner de la feuille de caisse',
    'total-desc': 'Toutes les ventes',
    'drive-thru-desc': 'Ventes service au volant de la feuille de caisse',
    'in-store-desc': 'Comprend le comptoir, la livraison et la borne : tout ce qui n’est pas le service au volant',
    'kiosk-desc': 'Ventes initiées à la borne, peu importe où le paiement a été fait',
    'mobile-order-and-pay-desc': 'Ventes initiées dans l’application mobile, peu importe où le paiement a été fait',
    'delivery-desc': 'Total des livraisons 3PO et GMA',
    'sales-group-selector-drive-thru': 'Service au volant',
    'sales-group-selector-all': 'Tous',
    'sales-group-selector-in-store': 'En restaurant',
    'sales-group-selector-kiosk': 'Borne',
    'sales-group-selector-v2-drive-thru': 'Service au volant',
    'sales-group-selector-v2-all': 'Tous',
    'sales-group-selector-v2-in-store': 'En restaurant',
    'sales-group-selector-v2-kiosk': 'Borne',
    'custom-options-v2-end-time': 'Heure de fin',
    'custom-options-v2-open': 'Ouvert',
    // Migrated from <i18n> SFC blocks (#6500)
    'header': 'Chaque restaurant dispose d’un sous-ensemble différent d’EVM admissibles aux fonds de partenariat de McDonald’s.',
    'part-1': 'L’EVM doit avoir bénéficié d’une baisse de prix recommandée par Deloitte à la fin août (dans le canal concerné).',
    'part-2': 'Les unités en restaurant des articles du menu à 5 $/8 $ de septembre, novembre et janvier ne sont pas incluses dans le montant de partenariat pendant la période de promotion.',
    'part-3': 'Les EVM vendus à un prix supérieur à la recommandation de Deloitte peuvent ne pas être admissibles au partenariat',
    'part-4': 'Big Mac / SME : du 8 sept. au 5 oct.',
    'part-5': '10 Pc Nug / SEC McGrd : du 4 au 30 nov.',
    'part-6': '2 Wrps / Sau Egg McMuff : du 6 janv. au 2 févr.',
    'part-7': 'SME / QPC : du 2 au 29 nov. 2026',
    'operator-view': 'Comparaison BU',
    'overview-container-buSummaryContainersDescription': 'La section Comparaison BU permet aux exploitants de comparer leur organisation et chaque restaurant à la moyenne de l’unité d’affaires. Ces conteneurs présentent les données au niveau de l’unité d’affaires dans le même format.',
    // Migrated from <i18n> SFC blocks (#6500)
    'sectioned-dashboard-grid-no-data': 'Aucune donnée',
    'sectioned-dashboard-grid-labor': 'Main-d’œuvre',
    'rank-of-the-store-compared-to-the-whole-bu': 'Classement du restaurant par rapport à l’ensemble de l’unité d’affaires',
    'units-1k-rod-gc': 'Unités/1 000 GC ROD',
    'number-of-menu-items-that-are-sold-related': 'Nombre d’articles du menu liés aux McCrispy Strips vendus pendant le reste de la journée (ROD) par millier d’unités.',
    'mc-crispy-strips-widget-rank': 'Classement',
    'new-beverage-units': 'Unités nouveaux breuvages',
    'new-beverage-watch-list': 'Liste de suivi breuvages 2026',
    'new-beverage-widget-new-beverage': 'Breuvages de spécialité',
    'new-beverage-widget-units-1k-gc': 'Unités/1 000 GC',
    'new-beverage-widget-number-of-menu-items-that-are-sold-related': 'Nombre d’articles du menu liés à AST Beverage vendus par millier d’unités.',
    'new-beverage-widget-rank': 'Classement',
    'rmhc-gc-percentage': '% GC RMHC',
    'rmhc-gc-total-gc': 'GC RMHC / GC total',
    'add-reward-message-link-text': 'Récompensez les employés',
    'add-reward-message-end': 'qui atteignent les objectifs d’arrondi pour la cause',
    'rmhc-incentive-widget-premium-upgrade-message-start': 'Passez à Premium :',
    'rmhc-incentive-widget-premium-upgrade-message-end': 'Améliorez l’arrondi pour la cause grâce aux récompenses',
    'rmhc-incentive-widget-rank': 'Classement',
    'created-date-header': 'Date de création',
    'incident-date-header': 'Date de l’incident',
    'case-number-header': 'Numéro de cas',
    'type-header': 'Type',
    'customer-name-header': 'Nom du client',
    'recovery-required-header': 'Récupération requise',
    'status-header': 'Statut',
    'resolution-header': 'Résolution',
    'customer-recovery-grid-close': 'fermer',
    'customer-recovery-grid-yes': 'Oui',
    'customer-recovery-grid-no': 'Non',
    'total-number-header': 'Total/#',
    'issue-header': 'Problème',
    'equipment-header': 'Équipement',
    'reported-header': 'Signalés',
    'priority-header': 'Priorité',
    'assignee-header': 'Assigné à',
    'status-reported': 'Signalés',
    'status-accepted': 'Acceptés',
    'status-in-progress': 'En cours',
    'status-pending': 'En attente',
    'status-resolved': 'Résolu',
    'priority-low': 'Bas',
    'priority-medium': 'Moyenne',
    'priority-high': 'Haut',
    'tickets-grid-close': 'fermer',
    'trailing-365': '365 derniers jours',
    'trailing-7': '7 derniers jours',
    'last-7': '7 derniers jours',
    'last-30': '30 derniers jours',
    'trailing-30': '30 derniers jours glissants',
    't365': '365J',
    'snapshot-container-last-week': 'Semaine dernière',
    'snapshot-container-point-of-order': 'Point de commande',
    'snapshot-container-last-month': 'Mois dernier',
    'snapshot-container-wtd': 'SEC',
    'snapshot-container-mtd': 'CMJ',
    'snapshot-container-ytd': 'CAJ',
    'mop-w-offers': 'MOP avec offres',
    'mop-wo-offers': 'MOP sans offres',
    'digital-app-digital-app': 'Application mobile',
    'digital-app-average-check': 'Facture moyenne',
    'digital-app-trading': 'Exercice commercial',
    'digital-app-loyalty-self-id': 'Auto-identification fidélité',
    'pct-sales': '% des ventes',
    'kiosk-w-offer': 'Borne avec offre',
    'kiosk-w-o-offer': 'Borne sans offre',
    'mcDelivery-w-offer': 'McDelivery avec offre',
    'mcDelivery-w-o-offer': 'McDelivery sans offre',
    'gma-delivery-w-offer': 'Livraison GMA avec offre',
    'gma-delivery-w-o-offer': 'Livraison GMA sans offre',
    'digital-order-points-digital-order-pts': 'Points de commande numériques',
    'digital-order-points-average-check': 'Facture moyenne',
    'digital-order-points-trading': 'Exercice commercial',
    'digital-order-points-kiosk': 'Borne',
    'below-threshold': 'en dessous du seuil cible inférieur',
    'between-threshold': 'entre les seuils cibles',
    'above-threshold': 'au-dessus des seuils cibles supérieurs',
    'any-order-containing': 'Toute commande comportant une composante Application mobile et/ou Point de commande numérique',
    'snapshot-digital-usage-red': 'rouge',
    'snapshot-digital-usage-yellow': 'jaune',
    'snapshot-digital-usage-green': 'vert',
    'snapshot-digital-usage-no-data': 'Aucune donnée',
    'snapshot-digital-usage-digital-usage': 'Utilisation numérique',
    'snapshot-digital-usage-error-loading-data': 'Erreur de chargement des données',
    'agency': 'Agence',
    'local-offer': 'Offre locale',
    'national-offer': 'Offre nationale',
    'non-offer': 'Sans offre',
    'bu-watchlist-no-data': 'Aucune donnée - Soit',
    'bu-watchlist-select-a-bigger-date-range': 'sélectionnez une plage de dates plus large au cours de laquelle ces articles du menu ont été vendus',
    'bu-watchlist-ask-your-agency': 'demandez à votre agence de sélectionner jusqu’à deux articles du menu à afficher ici',
    'bu-watchlist-meal-pct-of-gc': '% du GC total',
    'bu-watchlist-avg-check': 'Facture moyenne',
    'prior': '8/10/2025 - 9/6/2025',
    'extra-value-meal-comp-type-selector-trading': 'Année précédente (jour d’exploitation)',
    'five-and-eight-units-served-rd': 'Unités servies/R/J',
    'five-and-eight-avg-check': 'Facture moyenne',
    'five-and-eight-september': 'Big Mac/SME',
    'five-and-eight-november': '10 Pc Nug/SEC McGrd',
    'five-and-eight-january': '2 Wrps/Sau Egg McMuff',
    'five-and-eight-november-2026': 'QPC/SME',
    'five-and-eight-meal-pct-of-gc': '% des GC',
    'five-and-eight-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'for-more-detail': 'Pour plus de détails, veuillez consulter le',
    'pmix-report': 'rapport PMIX',
    'national-watchlist-no-data': 'Aucune donnée - Soit',
    'national-watchlist-select-a-bigger-date-range': 'sélectionnez une plage de dates plus large au cours de laquelle ces articles du menu ont été vendus',
    'national-watchlist-ask-your-agency': 'demandez à votre agence de sélectionner jusqu’à deux articles du menu à afficher ici',
    'national-watchlist-meal-pct-of-gc': '% du GC total',
    'national-watchlist-avg-check': 'Facture moyenne',
    'national-watchlist-national': 'National',
    'national-watchlist-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'scorecard-ranking': 'Classement de la fiche de rendement',
    'show-goal-diff': 'Afficher l’écart à l’objectif',
    'pivot': 'Pivoter',
    'rank-settings': 'Paramètres du classement',
    'score-card-rank-loc': 'Empl.',
    'score-card-rank-patch': 'Correctif',
    'card-settings': 'Paramètres de la carte',
    'rows': 'Rangées',
    'widget-settings-thresholds': 'Seuils',
    'watchlist-modal-local': 'Offre locale',
    'watchlist-modal-watchlist': 'Liste de suivi',
    'watchlist-modal-filter': 'Filtre',
    // Migrated from <i18n> SFC blocks (#6500)
    'share': 'Partager',
    'preparing_report': 'Préparation de votre rapport...',
    'share_as': 'Partager sous forme de',
    'image': 'Image',
    'share-table': 'Tableau',
    'All Filters': 'Tous les filtres',
    'Clear All': 'Tout effacer',
    'Case Number': 'Numéro de cas',
    'Search Case Number': 'Rechercher un numéro de dossier',
    'Customer Name': 'Nom du client',
    'Search Customer Name': 'Rechercher un nom de client',
    'Status': 'Statut',
    'Recovery Required': 'Récupération requise',
    'Global Performance Matrix': 'Matrice de performance globale',
    'Type': 'Type',
    'ALL FILTERS {count}/{total}': 'TOUS LES FILTRES {count}/{total}',
    'voice-analysis': 'Analyse Voice',
    '1-800-analysis': 'Analyse 1-800',
    'tab-1-800': '1-800',
    'tab-voice': 'VOICE',
    'setup-1800-instructions': 'Pour configurer 1-800, veuillez suivre les instructions',
    'payload-too-large': 'La demande de données est trop volumineuse. Veuillez réduire votre plage de dates ou appliquer des filtres pour afficher les données.',
    'deleted': 'Supprimé',
    'delete-selected': 'Supprimer la sélection ({count})',
    'close-selected': 'Fermer la sélection ({count})',
    'feedback-type-complaint': 'Plainte',
    'feedback-type-praise': 'Éloge',
    'feedback-type-inquiry': 'Demande',
    'complaints-per-100k-gc': 'Plaintes par 100 000 GC',
    'praise-per-100k-gc': 'Éloges par 100 000 GC',
    'inquiries-per-100k-gc': 'Demandes par 100 000 GC',
    'col-quick-close': 'Fermeture rapide',
    'col-deleted-by': 'Supprimé par',
    'unknown': 'Inconnu',
    'col-processed-date': 'Date de traitement',
    'col-created-date': 'Date de création',
    'col-incident-date': 'Date de l’incident',
    'col-case-number': 'Numéro de cas',
    'col-type': 'Type',
    'col-customer-name': 'Nom du client',
    'col-recovery-required': 'Récupération requise',
    'col-status': 'Statut',
    'col-resolution': 'Résolution',
    'col-actions': 'Actions',
    'col-created-from': 'Créé depuis',
    'col-case-origin': 'Origine du cas',
    'col-issue-category': 'Catégorie du problème',
    'col-issue-reason': 'Motif du problème',
    'col-issue-subcode': 'Sous-code du problème',
    'col-global-performance-matrix': 'Matrice de performance globale',
    'col-recovery-next-steps': 'Prochaines étapes de récupération',
    'col-recovery-form': 'Formulaire de récupération',
    'col-customer-email': 'Courriel du client',
    'col-customer-phone': 'Téléphone du client',
    'col-location-address': 'Adresse de l\'emplacement',
    'col-order-point': 'Point de commande',
    'col-pickup-point': 'Point de cueillette',
    'col-letter-sent': 'Lettre envoyée',
    'col-customer-comments': 'Commentaires du client',
    'add-shift': 'Ajouter un quart',
    'job-title': 'Titre d’emploi',
    'shift-times': 'Heures du quart',
    'shift-length': 'Durée du quart',
    'breaks': 'Pauses',
    'add-break': 'Ajouter une pause',
    'break-tooltip': 'Les pauses sont mises à l’horaire de façon à éviter les heures de repas de pointe et les conflits avec les pauses des autres employés.',
    'meal-break': 'Pause repas',
    'rest-break': 'Pause',
    'scheduling-conflicts': 'Notes d’horaire',
    'add-to-schedule': 'Ajouter à l’horaire',
    'add-shift-dialog-this-week': 'cette semaine',
    'add-shift-dialog-hours-this-week': 'Heures cette semaine',
    'available-employees': 'Employés non inscrits à l’horaire',
    'tooltip-description': 'Employés non inscrits à l’horaire aujourd’hui. Les chiffres indiquent les heures de cette semaine. Cliquez sur un employé pour afficher ses coordonnées. Les rangées surlignées correspondent à des mineurs.',
    'available-employees-error-loading': 'Erreur de chargement des données des employés',
    'available-employees-no-data-available': 'Aucun employé disponible',
    'swap-shift': 'Échanger le quart',
    'current-employee': 'Employé actuel',
    'scheduled-shift': 'Quart à l’horaire',
    'not-working-today': 'Ne travaille pas aujourd’hui',
    'working-description': 'Sélectionnez un employé qui travaille déjà aujourd’hui. Les deux employés échangeront leurs heures de travail.',
    'not-working-description': 'Sélectionnez un employé disponible pour reprendre ce quart. Le nouvel employé remplacera l’employé initial pour ce quart.',
    'select-employee': 'Sélectionner un employé',
    'their-shift': 'Son quart',
    'will-work': 'travaillera',
    'will-take-over': 'reprendra le quart',
    'additional-notes': 'Notes supplémentaires',
    'notes-placeholder': 'Notes facultatives sur cet échange...',
    'confirm-swap': 'Confirmer l’échange',
    'swap-shift-dialog-hours-this-week': 'Heures cette semaine',
    // Migrated from <i18n> SFC blocks (#6500)
    'dashboard-settings-dashboard-settings': 'Paramètres du tableau de bord',
    'customize-dashboard-containers': 'Personnaliser les conteneurs du tableau de bord',
    'unable-to-save-dashboard-settings': 'Impossible d’enregistrer les paramètres du tableau de bord.',
    'unable-to-reset-dashboard-settings-to-default': 'Impossible de rétablir les paramètres par défaut du tableau de bord.',
    'shift-dashboard': 'Tableau de bord de quart',
    'disclaimer': 'Les résultats de ventes FO sont affichés après la publication trimestrielle des ventes de McDonald’s.',
    'roiOnePager': 'Retour sur investissement',
    'clickToSetup': 'Cliquez pour afficher le rapport ROI des services-conseils',
    'clickToViewMore': 'Cliquez ici pour en voir plus',
    'qsrsoftConsulting': 'Services-conseils QSRsoft',
    'yourOrganization': 'Votre organisation',
    'heroTitle': 'Vous avez bâti quelque chose de précieux.',
    'heroSubtitle': 'Maintenant, protégeons-le — et faisons fructifier ce que vous avez bâti.',
    'heroAmountText': '{amount} sortent de votre compte bancaire aujourd’hui',
    'heroShowYou': 'Nous vous montrerons',
    'exactlyWhere': 'exactement où',
    'andHowTo': 'et comment',
    'takeItBack': 'le récupérer',
    'theProblem': 'Le problème',
    'theGuide': 'LE GUIDE',
    'guideSubtitle': 'Près de 100 ans au cœur du système.',
    'excessiveCrewLabor': 'Excédent de main-d’œuvre des équipiers',
    'laborText': 'Occasion liée aux flux de trésorerie sur 6 mois — {amount} par restaurant qui érodent silencieusement votre résultat net.',
    'foodOpportunity': 'Occasion liée aux aliments',
    'foodText': 'Comparé à la norme des meilleurs exploitants - occasion de {amount} par restaurant.',
    'gcOpportunity': 'Occasion GC',
    'guestCountText': 'Occasion liée à l’écart de GC comparables avec la BU',
    'theTransformation': 'La transformation',
    'transformationTitle': 'La transformation — Un retour de {return}. Un ROI de {roi} %+.',
    'basedOnCapturing': 'En ne saisissant que 25 % de l’occasion',
    'thePlan': 'LE PLAN — 3 ÉTAPES, 90 JOURS',
    'diagnose': 'Diagnostiquer',
    'diagnoseText': 'Se connecter à vos données QSRSoft. Dresser un portrait précis, restaurant par restaurant et catégorie par semaine, des endroits exacts où les liquidités s’échappent.',
    'target': 'Cibler',
    'targetText': 'Classer les occasions à plus forte valeur — main-d’œuvre des équipiers, temps supplémentaire, heures d’ouverture — et bâtir un plan priorisé AVEC vos directeurs généraux.',
    'capture': 'Saisir',
    'captureText': 'Accompagner votre équipe dans la mise en œuvre. Suivre la valeur récupérée en temps réel. Combler l’écart entre là où vous êtes et là où vous voulez être.',
    'whatOperatorsSay': 'CE QU’EN DISENT LES EXPLOITANTS',
    'testimonial1': 'Objectifs de main-d’œuvre des équipiers réduits de 4 % sur les douze derniers mois. Flux de trésorerie augmentés de plus de 1 million de dollars.',
    'testimonial2': 'Nous sommes passés de la collecte de données à une amélioration concrète. Des directeurs plus sensibilisés, une administration plus efficace.',
    'testimonial3': 'Des centaines de milliers économisés en main-d’œuvre — tout en augmentant l’achalandage et en surpassant notre unité d’affaires.',
    'franchisee': 'Franchisé McDonald’s',
    'nextStepSimple': 'Votre prochaine étape est simple.',
    'letsTalk': 'Parlons-en',
    'see-all-peers': 'Voir tous les pairs',
    'this-setting-will': 'Ce paramètre permet aux utilisateurs de voir les pairs qui ne relèvent pas directement de leur superviseur. Cela affectera',
    'only': 'uniquement',
    'affect the peers widget': 'le widget des pairs',
    'show-peers-across-hierarchy': 'Afficher les pairs de toute la hiérarchie',
    'hierarchy': 'Hiérarchie',
    'hierarchy-container-locations': 'Emplacements',
    'hierarchy-grid-no-data': 'Aucune donnée',
    'peers': 'Pairs',
    'avg-sales': 'Ventes moyennes',
    'avg-net-sales': 'Ventes nettes moyennes',
    'oepe-wo-park': 'OEPE sans stationnement',
    'dt-total-time': 'Temps total service au volant',
    'kvs-items-per-gc': 'Articles KVS / GC',
    'healthy-use': 'Utilisation saine',
    'healthy-usage': 'Utilisation saine',
    'kvs-time-per-item': 'Temps KVS / article',
    'peers-sales-kvs-time-per-gc': 'Temps KVS / GC',
    'peers-sales-dt-sales': 'Ventes SV',
    'peers-sales-dt-average-check': 'Facture moyenne service au volant',
    'incentive-grid-no-data': 'Aucune donnée',
    'incentive-grid-location': 'Emplacement',
    'incentive-grid-trend': 'Tendance',
    'select-metric': 'Sélectionner un indicateur',
    'array-cannot-be-empty': 'Le tableau ne peut pas être vide',
    'incentive-trend-trend': 'Tendance',
    '3d-guest-count-club': 'Club achalandage 3D',
    'average-daily-guest-counts': 'Achalandage quotidien moyen',
    'gma-kiosk': 'GMA et borne',
    'gmad-3po': 'GMAD et 3PO',
    'widget-drive-thru': 'Service au volant',
    'rank-out-of': 'Classement sur',
    'restaurants': 'Restaurants',
    'practice-week': 'Sem. de pratique',
    'overall-rank': 'Classement général',
    'created-by': 'Créé par',
    'custom-incentive-week': 'Sem.',
    'incentive-trend': 'Tendance du programme incitatif',
    'bu': 'BU',
    'the-ranking-is-the-same-as': 'Le classement est le même que celui du récent programme incitatif Big Bets. Les données correspondent à la somme de toutes les périodes de pointe. Les restaurants doivent être ouverts depuis au moins un an et ne pas avoir été fermés plus d’un jour, tant cette année que l’an dernier.',
    'the-overall-rank-is-based-on': 'Le classement général repose sur le calcul des points McD. Il est établi à partir d’un score cumulé du 7/07/2024 jusqu’à hier',
    'gc-day-plus-minus': 'GC/jour +/-',
    'guest-count-per-store-per-day': 'Achalandage par restaurant et par jour comparé à l’an dernier',
    'digital-gc-day-plus-minus': 'GC numérique/jour +/-',
    'digital-order-points-gc-r-d': 'GC/R/J points de commande numériques comparé à l’an dernier',
    'oepe-w-o-park': 'OEPE sans stationnement',
    'order-end-to-present-end': 'De la fin de la commande à la fin de la présentation, sans les commandes stationnées; temps écoulé entre le moment où le client termine sa commande et celui où la commande lui est présentée.',
    'oepe-w-o-park-plus-minus': 'OEPE sans stationnement +/-',
    'oepe-w-o-parked-compared-to-last-year': 'OEPE sans stationnement comparé à l’an dernier',
    'quality-sat-percent': '% satisfaction qualité',
    'overall-quality-satisfaction-score': 'Score global de satisfaction qualité',
    'default-incentives-widget-rank-out-of': 'Classement sur',
    'incentives': 'Programmes incitatifs',
    'warm-up-week-1': 'Sem. de préparation 1',
    'warm-up-week-2': 'Sem. de préparation 2',
    'week-1': 'Sem. 1',
    'week-2': 'Sem. 2',
    'week-3': 'Sem. 3',
    'week-4': 'Sem. 4',
    'week-5': 'Sem. 5',
    'week-6': 'Sem. 6',
    'fall-incentives': 'GOAL-den Momentum',
    'fifa-club-world-cup': 'Coupe du monde de la FIFA 26',
    'overall-rank-based-point-calculation': 'Le classement général repose sur le calcul des points McD. Il est établi à partir d’un score cumulé du 9/21/2025 jusqu’à hier',
    'vs-last-week': '« C. semaine dernière » indique de combien votre classement général a changé à la fermeture du samedi le plus récent',
    'mcd-corporation-calc': 'McDonald’s Corporation calculera le classement final, qui servira à la reconnaissance finale',
    'fifa-upt-definition': '« FIFA UPT +/- » correspond au nombre d’articles du menu FIFA vendus par 1 000 GC, comparé à l’année précédente',
    'upsized-definition': '« % format supérieur » correspond au pourcentage de trios Big Mac et 10 morceaux passés en grand format sur l’ensemble des trios ROD',
    'rmhc-bonus': 'RMHC est un indicateur boni qui n’ajoute des points que pour les semaines 1-3',
    'oepe-r2p': '* L’indicateur « OEPE » utilise l’OEPE sans stationnement. Les restaurants sans service au volant utilisent le R2P',
    'fall-incentives-widget-rank-out-of': 'Classement sur',
    'round-up': 'Arrondi pour la cause',
    'pos': 'PDV',
    'register-type': 'Secteur PDV',
    'round-up-qty-pct': '% qté arrondi pour la cause',
    'round-up-by-pos-area-location': 'Emplacement',
    'round-up-by-pos-area-grid-no-data': 'Aucune donnée',
    'rank-up-pos': 'Arrondi pour la cause PDV/employé',
    'warm-up-week-3': 'Sem. de préparation 3',
    'warm-up-week-4': 'Sem. de préparation 4',
    'summer-splash': 'Vague d’été',
    'summer-incentives-widget-overall-rank-based-point-calculation': 'Le classement général repose sur le calcul des points McD. Il est établi à partir d’un score cumulé du 6/29/2025 jusqu’à hier',
    'summer-incentives-widget-oepe-r2p': '* L’indicateur « OEPE ou R2P » utilise l’OEPE sans stationnement, sauf si le restaurant n’a pas de service au volant; dans ce cas, il s’agit du R2P',
    'summer-incentives-widget-rod': 'Les transactions du reste de la journée (ROD) excluent le déjeuner',
    'summer-incentives-widget-rank-out-of': 'Classement sur',
    'business-dates': 'Date(s) d’exploitation',
    'number-of-BDC-missing': 'Nb de BDC manquants',
    'bdc-grid-more-info': 'PLUS DE RENSEIGNEMENTS',
    'bdc-grid-location': 'Emplacement',
    'missing-at-least': 'auxquels il manque au moins',
    'one-business-day-close': 'une fermeture de journée d’exploitation',
    'bdc-insight-store': 'restaurant',
    'item-num': 'N° d’article',
    'desc': 'Description',
    'used-diff': 'Écart d’utilisation',
    'dollar-diff': 'Écart $',
    'pct-net-sales': '% des ventes nettes',
    'stat-grid-title': 'Détails des pertes d’inventaire',
    'stat-grid-store': 'Empl.',
    'most-recent-inventory': 'Dernier inventaire',
    'top-loss-items': '5 principaux articles en perte',
    'no-loss-items': 'Aucun article en perte trouvé',
    'raw-waste-amount': 'Pertes brutes $',
    'raw-waste-qty': 'Qté pertes brutes',
    'comp-waste-amount': 'Pertes produits finis $',
    'comp-waste-qty': 'Qté pertes produits finis',
    'missing-type': 'Type manquant',
    'both': 'Les deux',
    'raw-only': 'Brutes seulement',
    'comp-only': 'Produits finis seulement',
    'waste-entry-grid-title': 'Détails de saisie des pertes',
    'waste-entry-grid-store': 'Empl.',
    'stores-missing-raw-waste': 'Restaurants sans saisie des pertes brutes',
    'stores-missing-comp-waste': 'Restaurants sans saisie des pertes produits finis',
    'stores-missing-raw-waste-entry': 'restaurants n’ont pas saisi de pertes brutes au cours des 7 derniers jours',
    'stores-missing-comp-waste-entry': 'restaurants n’ont pas saisi de pertes produits finis au cours des 7 derniers jours',
    'store-missing-raw-waste-entry': 'restaurant n’a pas saisi de pertes brutes au cours des 7 derniers jours',
    'store-missing-comp-waste-entry': 'restaurant n’a pas saisi de pertes produits finis au cours des 7 derniers jours',
    'day-threshold-settings-days': 'Jours',
    'filter-message-open-late': '** Affichage des restaurants ayant ouvert en retard plus souvent que la limite de jours manqués ({daysThreshold}) jours lors des 7 dernières ouvertures.',
    'filter-message-close-early': '** Affichage des restaurants ayant fermé hâtivement plus souvent que la limite de jours manqués ({daysThreshold}) jours lors des 7 dernières fermetures.',
    'olce-grid-more-info': 'PLUS DE RENSEIGNEMENTS',
    'olce-grid-minutes': 'minutes',
    'olce-grid-days-missed-limit': 'La limite de jours manqués peut être ajustée dans les paramètres des analyses.',
    'olce-grid-filter-2': 'Filtre',
    'full-employee-name-header': 'Nom',
    'crew-type-header': 'Type d’équipier',
    'termed-header': 'Cessation d’emploi',
    'hire-date-header': 'Embauche',
    'termination-date-header': 'Cessation d’emploi',
    'merged-terms-more-info-yes': 'oui',
    'merged-terms-more-info-no': 'non',
    'crew-termed-prefix': 'cessations d’emploi d’équipiers au cours des',
    'crew-termed-suffix': 'premiers jours suivant l’embauche.',
    'turnover-prefix': 'Le roulement dépasse',
    'turnover-with': 'avec',
    'turnover-suffix': 'cessations d’emploi d’équipiers dans les 90 jours.',
    'with-90-day-turnover-above-prefix': 'dont le roulement à 90 jours dépasse',
    'people-terms-insight-no-30-day-terms': 'Aucune cessation d’emploi d’équipier au cours des 30 premiers jours.',
    'people-terms-insight-location-2': 'emplacement | emplacements',
    'ot-grid-title': 'Temps supp. potentiel',
    'this-metric-does-not-have-any-goals-set': 'Aucun objectif n’est défini pour cet indicateur',
    'all-goals': 'Tous les objectifs',
    'employees-over-threshold': 'employés sont en voie de travailler plus de {threshold} heures cette semaine.',
    'ot-threshold-settings-hours': 'Heures',
    'prod-out-grid-all-outages': 'TOUTES LES RUPTURES',
    'prod-out-grid-menu-item-number': 'Numéro d’article du menu',
    'prod-out-grid-active-product-outages': 'Ruptures de produit actives',
    'prod-out-grid-more-info': 'Plus de renseignements',
    'watch-lists': 'Listes de surveillance',
    'equipment-tickets': 'Billets d’équipement',
    'open-seven-days': 'ouverts > 7 jours',
    'customer-recovery': 'Récupération client',
    'open-forty-eight-hours': 'ouverts > 48 heures',
    'training-certificates': 'Certificats de formation',
    'to-expire': 'expirant dans < 30 jours',
    'simplethink-info-title': 'Vous n’êtes actuellement pas abonné à SimpleThink.',
    'simplethink-info-I': 'SimpleThink™ de QSRSoft est un assistant numérique tout-en-un qui vous aide à gérer votre restaurant avec davantage de',
    'simplethink-info-II': 'bons',
    'simplethink-info-III': 'contrôles. Des expériences personnalisées et ludiques qui encouragent, récompensent et favorisent les bons comportements.',
    'simplethink-info-IV': 'Optimisez vos opérations et stimulez vos équipiers!',
    'simplethink-info-modal-button': 'En savoir plus',
    'tickets-open-seven-days': 'Billets ouverts > 7 jours',
    'certificates-to-expire': 'Certificats expirant dans < 30 jours',
    'tickets-open-report': 'Billets ouverts > 7 jours',
    'training-certificates-report': 'Certificats de formation expirant dans < 30 jours',
    'customer-recovery-report': 'Récupération client ouverte > 48 h',
    'simple-think-insight-training': 'Formation',
    'location-header': 'Emplacement',
    'last-name-header': 'Nom de famille',
    'course-name-header': 'Nom du cours',
    'confirmation-id-header': 'ID de confirmation',
    'issue-date-header': 'Date d’émission',
    'expiration-date-header': 'Date d’expiration',
    'bdc-prefix': 'BDC -',
    'instore-events-insight-old-stores-with-alerts': '{count} restaurant(s) avec alertes actives',
    'instore-events-insight-old-stores-with-alerts-suffix': 'restaurant(s) avec alertes actives',
    'instore-events-insight-old-store': 'restaurant',
    'instore-events-insight-old-one': 'une',
    'instore-events-insight-old-more-info': 'Plus de renseignements',
    'merged-alerts-more-info-business-dates': 'Date(s) d’exploitation',
    'merged-alerts-more-info-number-of-BDC-missing': 'Nb de BDC manquants',
    'employees-with-more-than': 'Employés avec plus de ',
    'employee-view-modified-punches': ' poinçons modifiés ',
    '%-of-modified-punches-in-last-7-days:': ' Pointages modifiés supérieurs à la moyenne au cours des 7 derniers jours :',
    'store-trend': 'restaurant au-dessus de sa propre tendance | restaurants au-dessus de leur propre tendance',
    'org-trend': 'restaurant au-dessus de la tendance de l’organisation | restaurants au-dessus de la tendance de l’organisation',
    'employee-threshold-part1': 'employé a eu plus de | employés ont eu plus de',
    'employee-threshold-part2': ' poinçons modifiés au cours de la dernière période de paie',
    'mod-punches': 'Pointages modifiés',
    'modified-punches-tabs-store-trend': 'Tendance du restaurant',
    'modified-punches-tabs-org-trend': 'Tendance de l’organisation',
    'stores-above-org-trend-in-last-7-days': 'Restaurants au-dessus de la tendance de l’organisation au cours des 7 derniers jours',
    'punches-threshold-settings-punches': 'Poinçons',
    'stores-above-store-trend-in-last-7-days': 'Restaurants au-dessus de leur propre tendance au cours des 7 derniers jours',
    'rbd-completions': 'RBD complétés',
    'rbd-activations': 'Activations RBD',
    'merged-rbd-more-info-store': 'Empl.',
    'merged-rbd-more-info-more-info': 'Plus de renseignements',
    'rbd-completed-grid-store': 'Empl.',
    'rbd-completed-grid-more-info': 'Plus de renseignements',
    'store-has': 'restaurant a',
    'stores-have': 'restaurants ont',
    'or-more': 'ou plus',
    'error-try-again': 'ERREUR - RÉESSAYEZ',
    'rbd-completed-insight-rbd-completions': 'RBD complétés au cours des 30 derniers jours',
    'rbd-completions-label': 'RBD complétés',
    'rbd-completions-suffix': 'au cours des 30 derniers jours.',
    'no-rbd-completions': 'Aucun restaurant au-dessus du seuil de RBD complétés.',
    'store-has-rbd': 'restaurant a au moins un RBD activé au cours des 30 derniers jours, mais non complété.',
    'stores-have-rbd': 'restaurants ont au moins un RBD activé au cours des 30 derniers jours, mais non complété.',
    'store-has-prefix': 'restaurant a au moins un',
    'stores-have-prefix': 'restaurants ont au moins un',
    'rbd-activated': 'RBD activé',
    'activated-middle': 'au cours des 30 derniers jours, mais',
    'not-completed': 'non complété.',
    'no-rbd-activations': 'Aucun restaurant avec des activations RBD en attente.',
    'rbd-insight-rbd-completed': 'Terminé',
    'rbd-insight-rbd-activation': 'Activation',
    'rbd-insight-you-have': 'vous avez',
    'today-beta': 'Aujourd’hui (bêta)',
    'snapshot-digital': 'Aperçu numérique',
    'consulting': 'Services-conseils',
    'bu-comp': 'Comparaison BU',
    'main-security-drill-down': 'Analyse détaillée de la sécurité',
    'main-file-manager': 'Bibliothèque de ressources',
    'main-transaction-analytics': 'Analyses avancées',
    'over-ring': 'Surenregistrement',
    'red-before': 'Réduction avant',
    'red-after': 'Réduction après',
    'total-refund': 'Remboursement total',
    'snapshot-cash-over-short': 'Excédent / manque',
    'actual-labor-pct': '% main-d’œuvre réelle',
    'crew-labor-pct': '% main-d’œuvre équipiers',
    'punched-labor-pct': 'Main-d’œuvre poinçonnée %',
    'avg-rate': 'Taux moyen',
    'total-labor-pct': '% main-d’œuvre totale',
    'ot-$': 'Temps suppl.',
    'ot-hrs': 'H. temps suppl.',
    'snapshot-link-container-point-of-order': 'Point de commande',
    'avg-chk': 'Facture moyenne',
    'breakfast-gc': 'GC déjeuner',
    'dt-avg-chk': 'Facture moyenne service au volant',
    'dt-gcs': 'GC service au volant',
    'dt-gcs-qty': 'Qté GC service au volant',
    'gcs': 'GC',
    'prod-sales': 'Ventes de produits',
    'gcs-qty': 'Qté GC',
    'breakfast-gcs-qty': 'Qté GC déjeuner',
    'comparison': 'Comparaison',
    'showing-comparison-to': 'Comparaison avec',
    'year-before-last': 'Il y a deux ans',
    'snapshot-sales-avg-sales': 'Ventes moyennes',
    'snapshot-sales-breakfast-sales': 'Ventes du déjeuner',
    'snapshot-sales-last-year': 'An dernier',
    'day-parts': 'Plages horaires',
    'pmix-grid-item': 'Article',
    'setup-or-manage-watchlists': 'Configurer ou gérer les listes de suivi',
    'Thru': 'Au',
    'date-options-for-pmix-widget-exclude': 'Les options de date du widget PMIX excluent CAJ, TTM, T365 et toute plage personnalisée supérieure à 31 jours.',
    'please-select-at-least-one': 'Veuillez sélectionner au moins un article du menu ou une liste de suivi pour afficher les données PMIX.',
    'digital-snapshot': 'Aperçu numérique',
    'digital-hero-kpis': 'IRC numériques phares',
    'digital-diagnostics': 'Diagnostic numérique',
    'mop-fulfillment': 'Exécution MOP',
    'digital-hero-kpis-2024': 'IRC numériques phares 2024',
    'snapshot-digital-container-digital-usage': 'Utilisation numérique',
    'snapshot-digital-container-digital-order-pts': 'Points de commande numériques',
    'snapshot-digital-container-digital-app': 'Application mobile',
    'linked-payment-cnt': 'Nb paiements liés',
    'linked-payment-pct-digital': 'Paiement lié % GC de l’application mobile',
    'linked-payment-pct-total-gc': 'Paiement lié % GC total',
    'gma-delivery-pct': 'Livraison GMA % GC livraison total',
    'snapshot-digital-diagnostics-no-data': 'Aucune donnée',
    'snapshot-digital-diagnostics-error-loading-data': 'Erreur de chargement des données',
    'digital-hero-kpi-definition': 'Aperçu des définitions des IRC numériques phares',
    'kpi-name': 'Nom de l’IRC',
    'kpi-definition': 'Définition de l’IRC',
    'total-digital-percent-of-sales': 'Pourcentage total des ventes numériques',
    'proportion-of-total-sales-digital-channel': 'Part des ventes totales provenant d’un canal numérique (application, borne et/ou livraison)',
    'digital-app-percent-of-sales': 'Pourcentage des ventes de l’application mobile',
    'proportion-of-total-sales-digital-app': 'Part des ventes totales provenant de l’application mobile',
    'loyalty-90-day-active-customers': 'Clients fidélité actifs sur 90 jours',
    'number-of-customers-90-days': 'Nombre de clients* ayant effectué au moins une transaction par l’application mobile au cours des 90 jours se terminant le dernier jour de la période sélectionnée',
    'excludes-customers-without-ccpa': '*Cela exclut tous les clients s’étant retirés en vertu du CCPA',
    'average-number-digital-app': 'Nombre moyen de transactions par l’application mobile par restaurant et par jour',
    'new-digital-app-customers-per-30-days': 'Nouveaux clients de l’application mobile par 30 jours',
    'number-of-customers-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction par l’application mobile au cours de la période sélectionnée, divisé par le nombre de jours de la période sélectionnée, multiplié par 30',
    'summarizes-performace-highest-digital-kpis': 'Résume le rendement des IRC numériques les plus prioritaires en 2024',
    'click-for-detail': 'Cliquez pour afficher le détail',
    'data-for-these-metrics': 'Les données de ces indicateurs vont jusqu’au',
    'number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Nombre de clients* ayant effectué au moins une transaction par l’application mobile au cours de la période sélectionnée',
    'new-digital-app-customers': 'Nouveaux clients de l’application mobile',
    'mcDelivery-percent-of-sales': 'Pourcentage des ventes McDelivery',
    'proportion-of-system-sales': 'Proportion des ventes du système provenant de McDelivery (3PO et GMA)',
    'pct-of-mcDelivery-sales': '% des ventes McDelivery provenant de l’application',
    'proportion-of-all-mcDelivery-sales': 'Proportion de l’ensemble des ventes McDelivery provenant de transactions GMA Delivery',
    'digital-hero2025-number-of-customers-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction sur l’application mobile pendant la période sélectionnée',
    'this-excludes-customers-opted-out': '*Sont exclus les clients qui se sont retirés en vertu du California Consumer Privacy Act (et qui ne sont donc pas admissibles au programme MyMcDonald\'s Rewards)',
    'note-this-metric-only-gets-displayed': 'Remarque : cet indicateur s’affiche seulement au niveau de l’exploitant ou d’un seul restaurant. La fonctionnalité permettant de voir cet indicateur lorsque plusieurs restaurants sont sélectionnés n’est pas offerte actuellement',
    'number-of-customers-who-had-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction sur l’application mobile pendant la période sélectionnée, divisé par le nombre de jours de la période sélectionnée, multiplié par 30*',
    'this-metric-normalizes': '*Cet indicateur est normalisé selon le nombre de jours de la période, ce qui permet de comparer plus efficacement les tendances. Par exemple, mars aura toujours plus de nouveaux clients de l’application mobile que février, puisque le mois compte plus de jours. En normalisant selon le nombre de jours, cet indicateur permet toutefois de comparer équitablement l’acquisition de nouveaux clients d’un mois à l’autre',
    'digital-hero-rank-trend': 'Tendance',
    'digital-hero-rank-number-of-customers-who-placed-at-least-one-digital-app-transaction': 'Nombre de clients* ayant effectué au moins une transaction sur l’application mobile pendant les 90 jours se terminant le dernier jour de la période sélectionnée',
    'digital-hero-rank-rankings': 'Classements',
    'digital-hero-rank2025-trend': 'Tendance',
    'digital-hero-rank2025-number-of-customers-who-had-first-digital-app-transaction': 'Nombre de clients ayant effectué leur toute première transaction sur l’application mobile pendant la période sélectionnée',
    'digital-hero-rank2025-rankings': 'Classements',
    '90-day-active-customer-trend': 'Tendance des clients actifs sur 90 jours',
    'table': 'Tableau',
    'difference': 'Différence',
    'box-target': 'Cible',
    'box-container-no-data': 'Aucune donnée',
    'snapshot-month-container-food': 'Aliments',
    'raw-waste-pct': '% pertes brutes',
    'condiments-pct': '% condiments',
    'base-food-pct': '% aliments de base',
    'comp-waste-pct': '% pertes produits finis',
    'discount-pct': '% rabais',
    'emp-meals-pct': '% repas employés',
    'stat-variance-pct': '% écart statistique',
    'unexplained-pct': '% inexpliqué',
    'snapshot-month-food-food': 'Aliments',
    'sales-and-service': 'Ventes et service',
    'today.retiring-soon-prefix': 'Retrait imminent! Cliquez',
    'today.here': 'ici',
    'today.retiring-soon-suffix': 'pour passer à la nouvelle vue Aujourd’hui améliorée.',
    'today.why': 'Pourquoi?',
    'today.tooltip.better-labor': 'Meilleurs rapports de main-d’œuvre',
    'today.tooltip.aligns-dar': 'Aligné sur le rapport DAR',
    'today.tooltip.additional-metrics': 'Indicateurs supplémentaires disponibles pour la personnalisation',
    'today-container-labor': 'Main-d’œuvre',
    'actual-vs-needed': 'Réel c. requis',
    'actual-hrs-vs-needed': 'H. réelles c. requises',
    'actual-vs-scheduled': 'Réel c. horaire',
    'actual-hrs-vs-scheduled': 'H. réelles c. à l’horaire',
    'sales-per-punched-hr': 'Ventes / h poinçonnée',
    'gcs-per-punched-hr': 'GC / h poinçonnée',
    'today-labor-labor': 'Main-d’œuvre',
    'today-labor-avg-rate': 'Taux moyen',
    'today-labor-punch-labor-pct': 'Main-d’œuvre poinçonnée %',
    'product-sales-comp-pct': 'Ventes de produits +/- %',
    'average-check-comp-pct': 'Facture moyenne +/- %',
    'gcs-comp-pct': 'GC +/- %',
    'oepe-wo-park-comp': 'OEPE sans stationnement +/-',
    'oepe': 'OEPE',
    'oepe-comp': 'OEPE +/-',
    'r2p': 'R2P',
    'r2p-comp': 'R2P +/-',
    'kvs-healthy-usage-comp': 'Utilisation saine du KVS +/-',
    'kvs-time-per-gc-comp': 'Temps KVS / GC +/-',
    'today-sales-and-service-kvs-time-per-gc': 'Temps KVS / GC',
    'today-sales-and-service-all-net-sales': 'Ventes nettes totales',
    'today-sales-and-service-all-net-sales-comp-pct': 'Ventes nettes totales +/- %',
    'today-sales-and-service-prod-sales': 'Ventes prod.',
    'today-sales-and-service-average-check': 'Facture moyenne',
    'today-sales-and-service-gcs': 'GC',
    'today-sales-and-service-avg-ctp': 'CTP moyen',
    'today-sales-and-service-avg-ctp-comp': 'CTP moyen +/-',
    'sales-per-punched': 'Ventes / h poinçonnée',
    'gc-per-punched': 'TPPH',
    'today-widget-kvs-time-per-gc': 'Temps KVS / GC',
    'today-widget-labor': 'Main-d’œuvre',
    'today-widget-all-net-sales': 'Ventes nettes totales',
    'today-widget-all-net-sales-comp-pct': 'Ventes nettes totales +/- %',
    'today-widget-prod-sales': 'Ventes prod.',
    'today-widget-average-check': 'Facture moyenne',
    'today-widget-gcs': 'GC',
    'today-widget-dt-sales': 'Ventes SV',
    'today-widget-avg-dt-ttl': 'TTL moyen service au volant',
    'today-widget-kvs-items-per-gc': 'Articles KVS / GC',
    'today-widget-avg-ctp': 'CTP moyen',
    'today-widget-kvs-time-per-item': 'Temps KVS / article',
    'today-widget-avg-rate': 'Taux moyen',
    'today-widget-punch-labor-pct': 'Main-d’œuvre poinçonnée %',
    'the-menu-items-in-this-container': 'Les articles du menu de ce conteneur sont sélectionnés par votre agence. Cela vous permet de vous harmoniser avec votre agence sur les articles du menu les plus importants de vos restaurants et de votre marché. Veuillez adresser vos questions sur le détail de ces articles du menu à votre agence.',
    'bu-menuitems-no-data': 'Aucune donnée - Soit',
    'bu-menuitems-select-a-bigger-date-range': 'sélectionnez une plage de dates plus large au cours de laquelle ces articles du menu ont été vendus',
    'bu-menuitems-ask-your-agency': 'demandez à votre agence de sélectionner jusqu’à deux articles du menu à afficher ici',
    'bu-menuitems-meal-pct-of-gc': '% du GC total',
    'bu-menuitems-avg-check': 'Facture moyenne',
    'date-selector-ok': 'OK',
    'launch-freebie-verbiage': 'Advanced Analytics (TA) a été temporairement activé pour soutenir le lancement de l’initiative EVM du système. Pour continuer à profiter d’une visibilité complète sur TA, communiquez avec le responsable de votre unité d’affaires pour discuter d’un abonnement.',
    'extra-value-meal-container-extra-value-meal': 'Extra Value Meal',
    'extra-value-meal-container-start-date': 'Date de début',
    'extra-value-meal-container-end-date': 'Date de fin',
    'report-rankings-title': 'Classements {report} - {date}',
    'ta-rank-trend': 'Tendance',
    'ta-trend': 'Tendance TA',
    'spicy-mcmuffins': 'Spicy McMuffins',
    '5-meal-deal': 'Offre repas à 5 $',
    'base-food-decomposition': 'Décomposition des aliments de base',
    'transaction-analytics-container-mcCrispy-strips': 'Nouvelle plateforme poulet',
    'transaction-analytics-container-two-year-growth': 'Croissance sur deux ans',
    'transaction-analytics-container-addon-subscribed': 'Option · Abonné',
    'transaction-analytics-container-addon-upgrade': 'Option · Mise à niveau',
    'transaction-analytics-container-addon-upgrade-tooltip': 'Parlez à votre responsable de l’unité d’affaires de l’ajout d’Advanced Analytics pour :\n1. Soutien à l’agence et à l’unité d’affaires par une application d’analyses d’affaires\n2. Soutien aux O/O par MyQ sur les offres et événements nationaux et de l’unité d’affaires',
    'transaction-analytics-container-transaction-analytics': 'Advanced Analytics',
    'transaction-analytics-container-boao-for-1': 'BOAO à 1 $',
    'no-options-selected': 'Votre agence n’a encore sélectionné aucune option pour cette unité d’affaires. Demandez à votre agence de sélectionner des offres et des articles du menu à afficher ici.',
    'this-and-agency-support': 'Ceci fait partie du service Advanced Analytics de l’unité d’affaires',
    'talk-to-bu-lead': 'Parlez à votre responsable de l’unité d’affaires de l’ajout d’Advanced Analytics pour :',
    'agency-and-bu-support': 'Soutien à l’agence et à l’unité d’affaires par une application d’analyses d’affaires',
    'oo-support-myq': 'Soutien aux O/O par MyQ sur les offres et événements nationaux et de l’unité d’affaires',
    'current-incentives': 'Événements nationaux en cours',
    'transaction-analytics-service-disclaimer-learn-more': 'En savoir plus',
    'net-sales-comp': 'Ventes nettes/R +/-',
    'pr-yr-net-sales-comp': 'Ventes nettes/R an. préc. +/-',
    'gc-comp': 'GC/R +/-',
    'pr-yr-gc-comp': 'GC/R an. préc. +/-',
    'agency-control': 'Contrôle de l’agence',
    'bu-offers-no-data': 'Aucune donnée - Veuillez',
    'bu-offers-meal-pct-of-gc': '% du GC total',
    'bu-offers-avg-check': 'Facture moyenne',
    'base-food-decomp-trend': 'Tendance décomp. aliments de base',
    'trend-chart-placeholder': 'Le graphique de tendance sera intégré ici',
    'copied': 'Copié dans le presse-papiers!',
    'ROD': 'ROD',
    'urd': 'U/R/J',
    'extra-value-meal-report-title': 'Extra Value Meal : 15 %',
    'extra-value-meal-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'breakfast-abbr': 'DÉJ',
    'five-and-fifteen-five-and-fifteen': '5 %/15 %',
    'five-and-fifteen-units-served-rd': 'Unités servies/R/J',
    'five-and-fifteen-avg-check': 'Facture moyenne',
    'five-and-fifteen-meal-pct-of-gc': '% des GC',
    'five-and-fifteen-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'menu-item-selector': 'Ensemble d’articles du menu',
    'metric-selector-day': 'Par jour',
    'metric-selector-gc': 'Par 1 000 clients',
    'per-day-label': 'Unités vendues/R/J',
    'per-gc-label': 'Unités vendues/1 000 GC',
    'week-range': 'Dim-Sam',
    'trend-prior-no-data': 'Aucune donnée',
    'trend-prior-report-title': 'Tendance hebdomadaire',
    'prior-year': 'Année précédente',
    'trend-trading-no-data': 'Aucune donnée',
    'trend-trading-report-title': 'Tendance hebdomadaire',
    'trend-trading-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'five-meal-deal-weekly-units': 'Les unités hebdomadaires, l’investissement aliments et papier et la marge sont des moyennes calculées sur les GC dont la commande inclut une offre repas nationale',
    'mcCrispySandwiches': 'Sandwichs McCrispy',
    'snackWraps': 'Snack Wraps',
    'biscuits': 'Biscuits',
    'hot-honey-meal-pct-of-rod-gc': '% des GC',
    'hot-honey-units-served-1k-rod-gc': 'Unités servies/1 000 GC',
    'mcCrispyStrips': 'McCrispy Strips',
    'this-category': 'Cette catégorie',
    'national-category-trend-no-data': 'Aucune donnée',
    'national-category-trend-report-title': 'Tendance sur 5 semaines',
    'national-rail-row-margin-pct': '% MARGE',
    'as-of': 'Au',
    'what-this-shows': 'Pour les matières premières (aliments et condiments) utilisées dans les recettes des articles du menu suivis : Écart $ (réel moins théorique, additionné sur les restaurants sélectionnés) et % ventes prod. (Écart $ absolu par rapport aux ventes de produits du mois à ce jour), du plus grand % au plus petit. Ouvrez la vue de classement pour voir chaque restaurant, la date de son dernier inventaire et l’Écart $, de la plus grande perte au plus grand gain.',
    'national-watchlist-stat-loss-no-data': 'Aucune ligne de perte statistique pour les articles suivis dans les restaurants sélectionnés à cette date de fin',
    'national-watchlist-stat-loss-report-title': 'Perte statistique',
    'national-control': 'National',
    'national-widget-container-if-units-were-only-sold': 'Si les unités n’ont été servies que 5 jours sur 7, le « Jour » de Unités/R/J divise les unités par 5 jours',
    'national-widget-container-mcCrispy-strips': 'Nouvelle plateforme poulet',
    'national-widget-container-watchlist': 'Sélectionner une liste de suivi',
    'national-widget-container-boao-for-1': 'BOAO à 1 $',
    'national-widget-container-agency-menu-items-bevs': 'Articles du menu de l’agence (breuvages)',
    'national-widget-container-agency-menu-items-bevs-summary': 'Articles du menu de l’agence (breuvages) avec sommaire',
    'meal-pct-of-breakfast-gc': '% des GC déjeuner',
    'units-served-1k-breakfast-gc': 'Unités servies/1 000 GC déjeuner',
    'spicy-mc-muffins-units-served-rd': 'Unités servies/R/J',
    'spicy-mc-muffins-avg-check': 'Facture moyenne',
    'spicy-mc-muffins-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'you-can-only-have': 'Vous ne pouvez pas avoir plus de 10 fiches de rendement. Veuillez en supprimer une avant d’en créer une nouvelle.',
    'click-on-the-cells-to-edit-like-excel': 'Cliquez sur les cellules pour les modifier - comme dans Excel',
    'global-goals': 'Objectifs globaux',
    'the-goals-that-you': 'Les objectifs que vous avez définis s’appliqueront à toutes les fiches de rendement. Vous pouvez modifier ces objectifs dans l’onglet « OBJECTIFS » des paramètres principaux.',
    'do-you-want-to-continue': 'Voulez-vous continuer?',
    'yes-save-close': 'Oui, enregistrer et créer',
    'create-score-card-create-scorecard': 'Créer une fiche de rendement',
    'create-score-card-name-assign': 'Nommer et assigner',
    'create-score-card-qsr-defaults': 'Valeurs par défaut QSR',
    'create-score-card-defaults': 'Valeurs par défaut',
    'no-goals-set': 'Aucun objectif défini',
    'scorecard-name': 'Nom de la fiche de rendement',
    'assigning-roles-to': 'Assigner des rôles à une fiche de rendement permet de la rendre visible uniquement aux utilisateurs ayant ces rôles',
    'create-copy': 'Créer une copie',
    'score-card-container-settings-scorecard-settings': 'Paramètres de la fiche de rendement',
    'score-card-container-settings-scorecards-2': 'Fiches de rendement',
    'goals-per-location': 'Objectifs par emplacement',
    'all-locs': 'Tous les emplacements',
    'goal-grid-locations': 'Emplacements',
    'row-order': 'Ordre des lignes',
    'tab-date-selector-ok': 'OK',
    'customize-metrics': 'Personnaliser les indicateurs',
    'row': 'Ligne',
    'thresholds': 'Seuils',
    'healthy-limit': 'Limite saine',
    'under': 'En dessous',
    'between': 'Entre',
    'upper': 'Supérieur',
    'lower': 'Inférieur',
    'threshold-settings-over': 'Au-dessus',
    'no-shared-links': 'Aucun lien partagé n\'a été ajouté pour votre organisation.',
    'that-need-attention': 'qui requièrent votre attention',
    'manage-requests': 'Gérer les demandes',
    'requests': 'demandes',
    'employee-requests-insight-request': 'demande',
    'employee-requests-insight-you-have': 'Vous avez',
    // BUOverview chart series; only the -title key existed
    'two-year-growth': 'Croissance sur 2 ans',
    // Migrated from <i18n> SFC blocks (#6500)
    'voice-rankings-voice-rankings': 'Classements Voice',
    'financial-is-used-for-current-mid-month': 'Le mode Financier s’applique au mois en cours ou aux plages de dates qui dépassent les mois complétés. Il exclut les emplacements ouverts depuis moins de 13 mois complets. Par défaut, le mode Financier utilise la comparaison par jour d’exploitation.',
    'analytic-is-used-for-completed-months': 'Le mode Analytique s’applique aux mois complétés et aux plages de dates qui comprennent des mois complétés. Il applique une règle de 5 jours : si un emplacement n’a aucune vente pendant 5 jours ou plus (année en cours ou année précédente), il est exclu du calcul. Par défaut, le mode Analytique utilise la comparaison par jour civil.',
    'confirmDeletion': 'Confirmer la suppression',
    'areYouSure': 'Voulez-vous vraiment supprimer cette liste de suivi?',
    'businessUnitMenuItemDetails': 'Détails des articles du menu national',
    'watchlist': 'Liste de suivi',
    'subtitle': 'Sélectionnez jusqu’à cinq listes de suivi par catégorie à afficher aux O/O dans la section AA du tableau de bord d’accueil QSRSoft. Aucun sommaire ni total ne sera calculé pour l’ensemble des listes de suivi.',
    'widgetLabel': 'Titre du widget',
    'national-menu-items': 'Articles du menu national',
    'widgetDialogTitle': 'Modifier le titre du widget',
    'widgetTitleDisclaimer': 'Créez une liste de suivi avant de définir le titre',
    'watchlistName': 'Nom de la liste de suivi',
    'non_offer': 'Sans offre',
    'national': 'Offre nationale',
    'availableMenuItems': 'Articles du menu disponibles',
    'noMatchingMenuItems': 'Aucun article du menu correspondant trouvé.',
    'showAll': 'Tout afficher',
    'showSelected': 'Afficher la sélection',
    'saveWatchlist': 'Enregistrer la liste de suivi',
    'clear': 'Retirer',
    'widgetTitleLabel': 'Titre du widget (s’applique à toutes les listes de suivi)',
    'construction-watchlist-modal-local': 'Offre locale',
    'construction-watchlist-modal-watchlist': 'Liste de suivi',
    'construction-watchlist-modal-filter': 'Filtre',
    'bumid-businessUnitMenuItemDetails': 'Détails des articles du menu de l’unité d’affaires',
    'menu-item-cell-selectMenuItem': 'Sélectionner un article du menu',
    'menu-item-modal-selectMenuItem': 'Sélectionner un article du menu',
    'menu-item-modal-availableMenuItems': 'Articles du menu disponibles',
    'businessUnitOffers': 'Offres de l’unité d’affaires',
    'selectOffersInstructions': 'Sélectionnez jusqu’à deux offres groupées automatiques/offres mobiles par unité d’affaires à afficher aux O/O dans la section TA du tableau de bord d’accueil MyQSRSoft.',
    'promotionMembers': 'Éléments de la promotion',
    'selectOffer': 'Sélectionner une offre',
    'transactionAnalytics': 'Advanced Analytics',
    'hideWidget': 'Masquer',
    'showWidget': 'Afficher',
    'offers': 'Offres',
    'example-widget-businessUnit': 'BU',
    'availableOffers': 'Offres disponibles',
    'offerDetails': 'Détails de l’offre',
    'selectOfferToViewDetails': 'Sélectionnez une offre pour afficher les détails',
    'promo-selector-back': 'Retour',
    'bu-grid-noData': 'Aucune donnée',
    'operatorDashboard': 'Tableau de bord vue exploitant',
    'operatorDashboardDescription': 'Consultez ce que les exploitants voient au quotidien. Pour une analyse approfondie et les tendances, sélectionnez l’outil',
    'businessIntelligence': 'Analyses d’affaires',
    'tool': '.',
    'mcCrispy-strips': 'McCrispy Strips',
    'loyalty-90-day-active-trend': 'Tendance des membres fidélité actifs sur 90 jours',
    'agency-menu-items': 'Articles du menu de l’agence',
    'agency-menu-items-with-summary': 'Articles du menu de l’agence avec sommaire',
    'agency-menu-items-bevs': 'Articles du menu de l’agence (breuvages)',
    'agency-menu-items-bevs-summary': 'Articles du menu de l’agence (breuvages) avec sommaire',
    'five-and-eight': '$5/$8',
    'five-and-fifteen': '$5/$15',
    'trend': 'Tendance',
    'extra-value-meal-15': 'Extra Value Meal : 15 %',
    'extra-value-meal-5': 'Extra Value Meal : 5 %',
    'agency-offers': 'Offres de l’agence',
    'f-and-p-investment': 'Investissement aliments et papier',
    'bu-overview-voice': 'Voice',
    'bu-overview-food': 'Aliments',
    'bu-overview-digital-app': 'Application mobile',
    'bu-overview-labor': 'Main-d’œuvre',
    'bu_loyalty_90_day_active_trend': 'Tendance des membres fidélité actifs sur 90 jours de l’unité d’affaires',
    'bu_loyalty_90_day_active_trend_info': 'Nombre de clients uniques ayant enregistré au moins un GC par l’application mobile durant la période de 90 jours. Comme un client peut fréquenter plus d’un restaurant d’une même organisation, ce nombre ne peut pas être additionné d’un restaurant à l’autre.',
    'widget_disabled_info': 'Ce widget ne fonctionne que pour l’unité d’affaires',
    'bu_digital_app': 'Application mobile de l’unité d’affaires',
    'bu_digital_order_pts': 'Points de commande numériques',
    'bu_food': 'Aliments',
    'bu_labor': 'Main-d’œuvre',
    'bu_sales': 'Ventes',
    'bu_sales_financial': 'Le mode Financier s’applique au mois en cours ou aux plages de dates qui dépassent les mois complétés. Il exclut les emplacements ouverts depuis moins de 13 mois complets. Par défaut, le mode Financier utilise la comparaison par jour d’exploitation.',
    'bu_sales_analytic': 'Le mode Analytique s’applique aux mois complétés et aux plages de dates qui comprennent des mois complétés. Il applique une règle de 5 jours : si un emplacement n’a aucune vente pendant 5 jours ou plus (année en cours ou année précédente), il est exclu du calcul. Par défaut, le mode Analytique utilise la comparaison par jour civil.',
    'bu_service': 'Service',
    'people_metrics_info': 'Les indicateurs RH sont calculés à partir de mois complets.',
    'people_metrics_multiple_months': 'Si plusieurs mois sont sélectionnés, le mois complet le plus récent est utilisé.',
    'people_metrics_partial_month': 'Si un mois passé est partiellement sélectionné, le mois complet est utilisé.',
    'people_metrics_detailed_breakdown': 'Pour une ventilation détaillée, consultez le rapport BU Rank.',
    'peopleDataRequiresFullMonth': 'Les données RH exigent une plage de dates couvrant un mois complet',
    'voiceDataTooRecent': 'Les données Voice accusent habituellement 2 jours de retard. Veuillez sélectionner une plage de dates antérieure',
    'widgetOnlyWorksForBU': 'Ce widget ne fonctionne que pour l’unité d’affaires',
    'bu-voice-noData': 'Aucune donnée',
    'boao-for-1': 'BOAO à 1 $',
    'business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Advanced Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'transaction-level-data-updates': 'Les données au niveau des transactions de la veille sont mises à jour au cours de la journée. Veuillez noter que les résultats peuvent être incomplets en début de journée.',
    'units-sold-days': 'Si les unités n’ont été vendues que 5 jours sur 7, le « Jour » de Unités/R/J divise les unités par 5 jours',
    'rod': 'ROD',
    'meal-pct-of-gc': '% des GC',
    'units-served-rd': 'Unités servies/R/J *',
    'units-served-1k-gc': 'Unités servies/1 000 GC',
    'avg-margin-amt': 'Montant moyen de la marge',
    'margin-pct': '% marge',
    'bogo-avg-check': 'Facture moyenne',
    'chart-subtitle': 'Données pour la période',
    'units-per-thousand': 'Unités servies/1 000 GC',
    'bu-category-trend-no-data': 'Aucune donnée',
    'bu-category-trend-report-title': 'Tendance sur 5 semaines',
    'units-1k-gc': '/1 000 GC',
    'bu-rail-row-margin-pct': '% MARGE',
    'no-items-found': 'Aucun article trouvé',
    'bu-watchlist-section-watchlist': 'Sélectionner une liste de suivi',
    'meal-deal': 'Offre repas',
    'weekly-units': 'Les unités hebdomadaires, l’investissement aliments et papier et la marge sont des moyennes calculées sur les GC dont la commande inclut une offre repas nationale',
    'happy-meal-toy': 'Le coût du jouet Happy Meal correspond à la valeur définie sur la touche 846. Si elle est vide, nous utilisons 0,60 $',
    'explain-rod': 'ROD signifie « reste de la journée » et exclut la période du déjeuner (4 h-11 h)',
    'meal-pct-of-rod-gc': '% des GC ROD',
    'units-served-1k-rod-gc': 'Unités servies/1 000 GC ROD',
    'five-meal-deal-units-served-rd': 'Unités servies/R/J',
    'five-meal-deal-avg-check': 'Facture moyenne',
    'promo-&-discount': 'Promo et rabais aliments et papier',
    'discount-f&p': 'Rabais aliments et papier = le % de rabais de l’article ou des articles du menu appliqué au coût aliments et papier de ces articles. Montant du rabais aliments et papier.',
    'promo-f&p': 'Promo aliments et papier = le coût aliments et papier de l’article ou des articles du menu en promotion.',
    'base-food-plus-promo-discount': 'Aliments de base + aliments et papier promos et rabais',
    'base-food': 'Aliments de base',
    'check-the': 'Consultez la page ',
    'pmix-page': 'PMIX',
    'for-further-info': ' pour plus d’informations sur Hot Honey',
    'mc-crispy-strips': 'McCrispy Strips',
    'hot-honey-business-units-not-signed-up': 'Une ou plusieurs de vos unités d’affaires ne sont pas inscrites à Transaction Analytics. Les emplacements de ces unités d’affaires ne seront pas inclus dans le rapport. Veuillez communiquer avec le responsable de votre unité d’affaires pour plus d’informations.',
    'hot-honey-units-served-rd': 'Unités servies/R/J',
    'hot-honey-avg-check': 'Facture moyenne',
    'hot-honey-report-title': 'Hot Honey',
    'hot-honey-meal-pct-of-gc': '% des GC',
    'mc-crispy-strips-for-further-info': ' pour plus d’informations sur les Wrap Combos',
    'mc-crispy-strips-units-served-rd': 'Unités servies/R/J',
    'mc-crispy-strips-avg-check': 'Facture moyenne',
    'mc-crispy-strips-report-title': 'Nouvelle plateforme poulet',
    'the-offers-in-this-container': 'Les offres de ce conteneur sont sélectionnées par votre agence. Cela vous permet de vous aligner avec votre agence sur les offres les plus importantes en cours dans vos restaurants et dans votre marché. Veuillez adresser vos questions sur le détail de ces offres à votre agence.',
    'if-units-were-only-sold': 'Si les unités n’ont été vendues que 5 jours sur 7, le « Jour » de Unités/R/J divise les unités par 5 jours',
    'select-a-bigger-date-range': 'sélectionner une plage de dates plus large au cours de laquelle cette offre a eu lieu',
    'ask-your-agency': 'demander à votre agence de sélectionner deux offres (promotions ou remises) à afficher ici',
    'offers-no-data': 'Aucune donnée - Veuillez',
    'offers-meal-pct-of-gc': '% du GC total',
    'offers-avg-check': 'Facture moyenne',
    'buSummaryContainersDescription': 'Advanced Analytics aligne les agences, McD et les exploitants sur les événements majeurs. Les exploitants des unités d’affaires abonnées voient ces conteneurs avec les données de leurs seules organisations, tandis que les agences les voient avec des informations au niveau de l’unité d’affaires.',
    'extra-value-meal': 'Extra Value Meal',
    'two-year-growth-title': 'Croissance sur 2 ans de {type}',
    'weekly-trading-comparison': 'Comparaison hebdomadaire de l’activité. Logique des ventes du mode Financier appliquée.',
    'excluded-stores': 'Les restaurants sans ventes cette année ou l’an dernier sont exclus.',
    'watchlist-details-businessUnitMenuItemDetails': 'Détails des articles du menu de l’unité d’affaires',
    'watchlist-details-subtitle': 'Sélectionnez jusqu’à cinq listes de suivi par unité d’affaires à afficher aux O/O dans la section TA du tableau de bord d’accueil QSRSoft. Aucun sommaire ni total ne sera calculé pour l’ensemble des listes de suivi.',
    'custom-request-form-loading': 'Chargement',
    'failed-message': 'Échec du chargement du tableau de bord QuickSight',
    'new-extract': 'Nouvelle extraction',
    'extracts': 'Extractions',
    'report-finder': 'Recherche de rapports',
    'report': 'Rapport',
    // Migrated from <i18n> SFC blocks (#6500)
    'Su': 'Di',
    'M': 'Lu',
    'Tu': 'Ma',
    'W': 'Me',
    'Th': 'Je',
    'F': 'Ve',
    'Sa': 'Sa',
    'day-type-calendar': 'Calendrier',
    'day-type-business-date': 'Date d’exploitation',
    'pay-period': 'Période de paie',
    'start-and-end-date-correspond': '* Les dates de début et de fin correspondent aux cycles de paie',
    'pay-period-selector-multi-end-date': 'Date de fin',
    'pay-period-selector-multi-v2-end-date': 'Date de fin',
    'quick-dates-v3-yesterday': 'HIER',
    'quick-dates-v3-last-month': 'MD',
    'quick-dates-v3-last-year': 'AD',
    'quick-dates-v3-month-to-date': 'CMJ',
    'quick-dates-v3-year-to-date': 'CAJ',
    'quick-dates-v3-trailing-365-days': '365J',
    'bu-scope-selector-scope': 'Portée',
    'business-unit': 'Unité d’affaires',
    'location-agg-and-location': '& emplacement',
    'location-agg-v2-by-location': 'Par emplacement',
    'same-store-comp-all-store-comp-2': 'Comparaison de tous les restaurants',
    'same-store-comp-same-store-comp-2': 'Comparaison des restaurants comparables',
    'only-active-stores': 'Les données sont limitées aux restaurants actifs seulement.',
    'daypart-dropdown-v2-all': 'Tous',
    'peak-dropdown-v2-required': 'Les périodes de la journée sont obligatoires',
    'peak-dropdown-v2-all': 'TOUS',
    'Time Segment': 'Tranche horaire',
    'Peaks': 'Pics',
    'Dayparts': 'Plages horaires',
    'Timeslices': 'Plages horaires',
    'Custom': 'Personnalisé',
    'selected': 'sélectionné(s)',
    'timeslice-dropdown-required': 'Les périodes de la journée sont obligatoires',
    'timeslice-dropdown-all': 'Tous',
    'timeslice-dropdown-v2-required': 'Les périodes de la journée sont obligatoires',
    'timeslice-dropdown-v2-all': 'Tous',
    'select-report': 'Sélectionner un rapport',
    'view-all-scheduled-reports': 'Voir tous les rapports planifiés',
    'report-schedule-schedule': 'Horaire',
    'schedule-reports': 'Planifier des rapports',
    'daily-reports-always-retrieve-data-from-yesterday': 'Les rapports quotidiens récupèrent toujours les données de la veille',
    'weekly-reports-always-retrieve-data-from-the-previous-week': 'Les rapports hebdomadaires récupèrent toujours les données de la semaine précédente.',
    'monthly-reports-always-retrieve-data-from-the-previous-month': 'Les rapports mensuels récupèrent toujours les données du mois précédent.',
    'frequency': 'Fréquence',
    'recipients': 'Destinataires',
    'invalid-email': 'Courriel invalide',
    'recipients-selector-required': 'Obligatoire',
    'day-of-month': 'Jour du mois',
    'time-selector-day-of-week': 'Jour de la semaine',
    // Migrated from <i18n> SFC blocks (#6500)
    'register-type-selector-register': 'Type de caisse',
    'select-date': 'Sélectionner une date',
    'tomorrow': 'Demain',
    'two-days-ago': 'Il y a 2 jours',
    'quick-dates-last-week': 'SD',
    'next-week': 'SP',
    'quick-dates-this-week': 'SC',
    'quick-dates-this-month': 'MC',
    'next-month': 'MP',
    'week-to-date': 'SEC',
    '24-months': '24 MOIS',
    '36-months': '36 MOIS',
    'quick-dates-yesterday': 'HIER',
    'quick-dates-last-month': 'MD',
    'quick-dates-last-year': 'AD',
    'quick-dates-month-to-date': 'CMJ',
    'quick-dates-year-to-date': 'CAJ',
    'quick-dates-trailing-365-days': '365J',
    'quick-dates-v2-yesterday': 'HIER',
    'quick-dates-v2-last-month': 'MD',
    'quick-dates-v2-last-year': 'AD',
    'quick-dates-v2-month-to-date': 'CMJ',
    'quick-dates-v2-year-to-date': 'CAJ',
    'quick-dates-v2-trailing-365-days': '365J',
    'sum-all-locations-long': 'Sommaire',
    'by': 'Par',
    'and-location': '& empl.',
    'location-agg-sum-all-locations': 'Sommaire',
    'location-agg-by-location': 'Par emplacement',
    'same-store-comp-all-store-comp': 'Comparaison de tous les restaurants',
    'same-store-comp-same-store-comp': 'Comparaison des restaurants comparables',
    'daypart-dropdown-all': 'Tous',
    'by-day': 'Par jour',
    'by-timeslice': 'Par plage(s) horaire(s)',
    'peak-dropdown-required': 'Les périodes de la journée sont obligatoires',
    'peak-dropdown-all': 'TOUS',
    'label': 'Afficher le segment par',
    'timeslice-submenu-label': 'Plage(s) horaire(s)',
    'segment-by-dropdown-by-time-slice': 'Par plage(s) horaire(s)',
    // Generic actions
    reset: 'Réinitialiser',
    'discard': 'Ignorer',

    // Empty / error states
    'no-data': 'Aucune donnée',
    'an-error-occurred-while-loading-the-report':
      'Une erreur s’est produite lors du chargement du rapport. Veuillez réduire le nombre de restaurants ou la plage de dates. Si le problème persiste, veuillez communiquer avec le soutien.',

    // Filter containers
    filters: 'Filtres',
    'please-select-at-least-one-location':
      'Veuillez sélectionner au moins un emplacement',
    'please-select-a-scope': 'Veuillez sélectionner une portée',
    'please-select-a-date-range': 'Veuillez sélectionner une plage de dates',
    'please-select-a-time-segment': 'Veuillez sélectionner un segment horaire',
    'please-select-please-select-at-least-one-daypart':
      'Veuillez sélectionner au moins une période de la journée',
    'please-select-please-select-at-least-one-time-slice':
      'Veuillez sélectionner au moins une plage horaire',
    'please-select-please-select-at-least-one-peak':
      'Veuillez sélectionner au moins une pointe',
    'the-following-restaurants-are-not-included':
      'Les restaurants suivants ne sont pas inclus dans ce rapport, car ils ne figurent pas dans votre profil d’utilisateur.',
    'if-you-are-logged-in-with-eid':
      'Si vous êtes connecté avec un eID, votre liste de restaurants doit être corrigée dans GAM. Une fois les restaurants ajoutés, vous devrez vous déconnecter puis vous reconnecter pour que QSRSoft récupère la liste à jour. Les modifications apportées dans GAM peuvent prendre jusqu’à 48 heures pour se propager à notre système.',
    'if-you-are-logged-in-with-email':
      'Si vous êtes connecté avec une adresse courriel, transmettez la demande à votre administrateur système afin qu’il ajoute les restaurants à votre profil. Vous devrez actualiser votre navigateur une fois la modification effectuée.',

    // Loading / retry
    retrying: 'Nouvelle tentative...',

    // Grid container
    'expand-rows': 'Développer les lignes',

    // Chart container
    overlap: 'Superposer',
    showChart: 'Afficher le graphique',
    hideChart: 'Masquer le graphique',
    // Own key: 'timeslices' resolves to other text app-wide (#6335)
    'timeslice-dropdown-timeslices': 'Plages horaires',
    // Own key: 'first-name' resolves to other text app-wide (#6335)
    'minor-exceptions-first-name': 'Prénom',
    // Own key: 'timeslice' resolves to other text app-wide (#6335)
    'custom-incentive-timeslice': 'Plage horaire',
    // McDonald's dashboard tabs (oldDashboard/mcd/main.vue)
    'transaction-analytics': 'Advanced Analytics',
  },
};
